// client/src/App.js

import React, { useState, useEffect, useRef, useCallback, Component } from "react";
import io from "socket.io-client";
import InfiniteScroll from "react-infinite-scroll-component";
import "./App.css";

// Grok API Configuration for client-side name moderation
// This enables parallel moderation - 100 players can moderate simultaneously without blocking
const GROK_API_KEY = "xai-Dk6tSNE511myOJPastjHQE6Fo9AmieRv29oH6WjBKZJjAwOsqW2QOxxogMI85xr49KbZxrBjJaKdf4oA";
const GROK_MODEL = "grok-4-1-fast-reasoning";
const GROK_ENDPOINT = "https://api.x.ai/v1/chat/completions";

/**
 * Client-side nation name moderation using Grok AI
 * Checks if a nation name is appropriate for AI video generation services
 * Also checks for semantic duplicates with existing nations
 * Also validates thematic appropriateness based on fantasy level
 * 
 * Note: With 100+ simultaneous joins, some requests may hit rate limits (8 req/sec).
 * Fail-open behavior ensures players can still join if moderation fails.
 * 
 * @param {string} name - The nation name to moderate
 * @param {string[]} existingNations - List of existing nation names to check for semantic duplicates
 * @param {number} fantasyLevel - Game fantasy level (1=Realistic, 2=Somewhat Real, 3=Fantastical)
 * @returns {Promise<{approved: boolean, reason?: string}>}
 */
async function moderateNationName(name, existingNations = [], fantasyLevel = 3) {
  console.log(`[Moderation] Checking nation name: "${name}" against ${existingNations.length} existing nations (fantasy level ${fantasyLevel})`);
  
  try {
    // Build the existing nations context for duplicate checking
    const existingNationsContext = existingNations.length > 0 
      ? `\n\n**EXISTING NATIONS IN GAME (check for duplicates):**
${existingNations.join(', ')}

5. **Duplicate/Equivalent Names**: Names that are effectively the same as an existing nation
   - This includes: alternate names, abbreviations, translations, historical names, common nicknames
   - Examples: "USA" = "America" = "United States" = "U.S.A." = "The States"
   - Examples: "USSR" = "Russia" = "Soviet Union" = "Russian Federation"
   - Examples: "UK" = "Britain" = "United Kingdom" = "Great Britain" = "England"
   - Examples: "PRC" = "China" = "People's Republic of China"
   - If ANY existing nation is semantically equivalent, REJECT with reason "Duplicate: this is the same nation as [existing name]"`
      : '';

    // Build fantasy level thematic restriction
    const fantasyLevelContext = {
      1: `
4. **Fantasy Level 1 - REALISTIC ONLY**: This game is set in a realistic 1980s Cold War scenario.
   - ONLY ACCEPT: Real nations, countries, city-states, or territories that actually existed on Earth in or around the 1980s
   - REJECT all fictional, fantasy, sci-fi, or made-up nation names
   - Good examples: USA, USSR, France, West Germany, East Germany, Yugoslavia, Rhodesia, Taiwan, Hong Kong
   - Bad examples to REJECT: "Peacelandia", "Moonbase Alpha", "New Atlantis", "Freedomland", "Moonpeople", "Dwarven Kingdoms", any made-up names`,
      2: `
4. **Fantasy Level 2 - SLIGHTLY FANTASTICAL**: This game allows creative and whimsical nation names, but not overt fantasy/sci-fi.
   - ACCEPT: Real nations, plausible fictional names, AND slightly whimsical/creative names
   - Creative compound names like "Spacelandia" or "Robotopia" are fine - they follow real naming conventions
   - Good examples: USA, France, "Peacelandia", "Spacelandia", "Nova Republic", "Robotopia", "East Pacifica", "Freedonia"
   - REJECT: Names with overt fantasy creatures, magic, medieval fantasy, or explicit sci-fi factions
   - Bad examples to REJECT: "Dragon Land", "Dwarven Kingdoms", "The Elf Realm", "Wizard Republic", "Moonpeople", "Galactic Federation", "Cyborg Collective"`,
      3: `
4. **Fantasy Level 3 - ANYTHING GOES**: This game allows ALL types of nation names - realistic, creative, or fantastical.
   - ACCEPT: Real nations, fictional nations, fantastical names, sci-fi factions - EVERYTHING is valid
   - Realistic names like "USA" and "France" are just as welcome as "Dwarven Kingdoms" or "Galactic Federation"
   - Good examples: "USA", "France", "Peacelandia", "Moonpeople", "The Galactic Federation", "Dwarven Kingdoms", "Cyborg Collective"
   - Only reject names that are completely absurd/meaningless or impossible to use as a faction name
   - Examples to REJECT: "The Sun", "The Concept of Fear", "AAAAAAA", "123456", incoherent strings`
    };

    const messages = [
      {
        role: "system",
        content: `You are a content moderation system for a Cold War simulation game. Players create nation-state names that will be used in AI-generated video content.

Your job is to MODERATE the nation name (REJECT if inappropriate OR doesn't fit the game's fantasy level).

REJECT names that are:
1. **Intellectual Property Infringement**: Names from copyrighted franchises, games, movies, etc.
   - Examples to REJECT: "Mushroom Kingdom", "Hyrule", "Wakanda", "Mordor", "Gotham", "Hogwarts", "Narnia", "Westeros", "Panem", "The Empire" (Star Wars), "Cybertron"
   
2. **Sexual/Lewd Content**: Any name containing sexual, lewd, or obscene language or innuendo
   - Examples to REJECT: Names containing profanity, sexual terms, slurs, crude body part references
   
3. **Violent/Disturbing Content**: Names promoting violence, terrorism, or disturbing imagery
   - Examples to REJECT: Names referencing terrorism, genocide, mass murder, hate groups, or graphic violence
${fantasyLevelContext[fantasyLevel] || fantasyLevelContext[3]}${existingNationsContext}

APPROVE names that are:
- Appropriate for the current fantasy level (see rule 4 above)
- NOT duplicates of existing nations (if any listed)
- NOT intellectual property, sexual, violent, or disturbing

Respond with ONLY valid JSON in this exact format:
{"approved": true} 
OR
{"approved": false, "reason": "Brief explanation", "type": "content|fantasy_level|duplicate"}

The "type" field indicates WHY the name was rejected:
- "content" = IP infringement, sexual, violent, or disturbing content
- "fantasy_level" = name doesn't fit the current fantasy level setting
- "duplicate" = name is equivalent to an existing nation`
      },
      {
        role: "user",
        content: `Moderate this nation-state name: "${name}"`
      }
    ];

    const requestBody = {
      model: GROK_MODEL,
      messages: messages,
      max_tokens: 150,
      temperature: 0.1 // Low temperature for consistent moderation
    };

    const response = await fetch(GROK_ENDPOINT, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${GROK_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Grok API Error (${response.status}): ${errorText}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content.trim();
    console.log(`[Moderation] AI response: ${content}`);

    // Parse the JSON response
    try {
      // Handle potential markdown code blocks
      let jsonStr = content;
      if (content.includes('```')) {
        const match = content.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (match) jsonStr = match[1].trim();
      }
      
      const result = JSON.parse(jsonStr);
      
      if (result.approved === true) {
        console.log(`[Moderation] APPROVED: "${name}"`);
        return { approved: true };
      } else {
        const rejectionType = result.type || 'content'; // Default to content if not specified
        console.log(`[Moderation] REJECTED: "${name}" - ${result.reason} (type: ${rejectionType})`);
        return { approved: false, reason: result.reason || "Name not allowed", type: rejectionType };
      }
    } catch (parseError) {
      console.error(`[Moderation] Failed to parse AI response, allowing name (fail-open): ${content}`);
      // Default to allowing if we can't parse (fail open for UX)
      return { approved: true };
    }
  } catch (error) {
    console.error(`[Moderation] AI moderation failed, allowing name (fail-open):`, error.message);
    // If moderation fails, allow the name (fail open for UX)
    return { approved: true };
  }
}

/**
 * Reusable Influence Rankings Panel Component
 * Shows top 3 and bottom 3 with clickable "..." in between when > 6 nations
 * 
 * @param {Object} props
 * @param {Object|Array} props.influences - Object of {nation: score} or sorted array of [nation, score]
 * @param {string} props.currentPlayer - Optional name to highlight as current player
 * @param {boolean} props.showWinner - Show gold star on #1 (for final screen)
 * @param {Function} props.onShowAll - Callback when clicking to expand full list
 * @param {boolean} props.compact - Smaller font/padding for processing screen
 * @param {string} props.title - Optional title to display
 * @param {boolean} props.showTitle - Whether to show title (default true)
 */
function InfluenceRankingsPanel({ 
  influences, 
  currentPlayer, 
  showWinner = false, 
  onShowAll, 
  compact = false,
  title = "INFLUENCE RANKINGS",
  showTitle = true 
}) {
  // Normalize influences to sorted array
  const sorted = Array.isArray(influences) 
    ? influences 
    : Object.entries(influences || {}).sort(([, a], [, b]) => b - a);
  
  const showCondensed = sorted.length > 6;
  const top3 = sorted.slice(0, 3);
  const bottom3 = sorted.slice(-3);
  
  const fontSize = compact ? "0.75em" : "0.9em";
  const padding = compact ? "4px 6px" : "4px 10px";
  const maxNameWidth = compact ? "120px" : undefined;
  
  const RankingRow = ({ nation, score, index }) => {
    const isCurrentPlayer = nation === currentPlayer;
    const isWinner = showWinner && index === 0;
    
    return (
      <div style={{ 
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: padding,
        marginBottom: compact ? "3px" : "4px",
        backgroundColor: isWinner 
          ? "rgba(255, 215, 0, 0.15)" 
          : isCurrentPlayer 
            ? "rgba(57, 255, 20, 0.15)" 
            : "transparent",
        border: isWinner 
          ? "1px solid rgba(255, 215, 0, 0.5)" 
          : isCurrentPlayer 
            ? "1px solid rgba(57, 255, 20, 0.4)" 
            : "1px solid transparent",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isWinner && <span style={{ color: "#FFD700" }}>★</span>}
          <span style={{ 
            color: isWinner ? "#FFD700" : "#39FF14",
            fontWeight: isWinner || isCurrentPlayer ? "bold" : "normal",
            textShadow: isWinner 
              ? "0 0 8px #FFD700" 
              : isCurrentPlayer 
                ? "0 0 8px #39FF14" 
                : "none",
            letterSpacing: "0.02em",
            overflow: maxNameWidth ? "hidden" : undefined,
            textOverflow: maxNameWidth ? "ellipsis" : undefined,
            whiteSpace: maxNameWidth ? "nowrap" : undefined,
            maxWidth: maxNameWidth,
          }}>
            {index + 1}. {nation.toUpperCase()}{isCurrentPlayer && !showWinner ? "*" : ""}
          </span>
        </div>
        <span style={{ 
          color: isWinner ? "#FFD700" : "#39FF14",
          fontWeight: "bold",
          textShadow: isWinner ? "0 0 8px #FFD700" : "0 0 5px #39FF14",
          marginLeft: compact ? "8px" : "15px",
          flexShrink: 0,
        }}>
          {score}
        </span>
      </div>
    );
  };
  
  const DotsClickable = () => (
    <div 
      onClick={(e) => {
        e.stopPropagation();
        onShowAll && onShowAll();
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: compact ? "6px 0" : "8px 0",
        color: "#39FF14",
        opacity: 0.6,
        fontSize: "0.9em",
        letterSpacing: "0.3em",
        cursor: onShowAll ? "pointer" : "default",
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) => {
        if (onShowAll) {
          e.currentTarget.style.opacity = "1";
          e.currentTarget.style.textShadow = "0 0 10px #39FF14";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = "0.6";
        e.currentTarget.style.textShadow = "none";
      }}
      title={onShowAll ? "Click to view all nations" : ""}
    >
      ·<br/>·<br/>·
    </div>
  );

  if (sorted.length === 0) {
    return (
      <div style={{ 
        color: "#39FF14", 
        opacity: 0.5, 
        textAlign: "center",
        fontSize: compact ? "0.8em" : "0.9em",
        padding: compact ? "20px 0" : "10px",
      }}>
        {compact ? "NO DATA" : "> NO NATIONS TRACKED"}
      </div>
    );
  }

  return (
    <div style={{ fontSize }}>
      {showCondensed ? (
        <>
          {top3.map(([nation, score], idx) => (
            <RankingRow key={nation} nation={nation} score={score} index={idx} />
          ))}
          <DotsClickable />
          {bottom3.map(([nation, score]) => {
            const actualIndex = sorted.findIndex(([n]) => n === nation);
            return <RankingRow key={nation} nation={nation} score={score} index={actualIndex} />;
          })}
          <div style={{
            textAlign: "center",
            fontSize: "0.8em",
            opacity: 0.6,
            marginTop: compact ? "8px" : "10px",
            borderTop: "1px solid rgba(57, 255, 20, 0.2)",
            paddingTop: compact ? "8px" : "10px",
          }}>
            ▼ {sorted.length} NATIONS{showWinner ? " TOTAL" : ""}
          </div>
        </>
      ) : (
        sorted.map(([nation, score], idx) => (
          <RankingRow key={nation} nation={nation} score={score} index={idx} />
        ))
      )}
    </div>
  );
}

// Error Boundary to catch React errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("React Error Boundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          backgroundColor: "#001100",
          color: "#ff3333",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'PrintChar21', 'Courier New', monospace",
          padding: "40px",
          textAlign: "center",
        }}>
          <div style={{
            fontSize: "2em",
            marginBottom: "30px",
            textShadow: "0 0 15px #ff3333",
            letterSpacing: "0.2em",
          }}>
            ═══════════════════════════<br/>
            SYSTEM ERROR<br/>
            ═══════════════════════════
          </div>
          <div style={{
            fontSize: "1.2em",
            marginBottom: "30px",
            maxWidth: "600px",
            lineHeight: "1.6em",
          }}>
            A critical error has occurred in the simulation.<br/>
            Please refresh the page to reconnect.
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: "#330000",
              color: "#ff3333",
              border: "1px solid #ff3333",
              padding: "15px 40px",
              fontFamily: "'PrintChar21', 'Courier New', monospace",
              fontSize: "1.2em",
              cursor: "pointer",
              letterSpacing: "0.1em",
            }}
          >
            [ RESTART SYSTEM ]
          </button>
          <div style={{
            marginTop: "30px",
            fontSize: "0.8em",
            opacity: 0.6,
            maxWidth: "500px",
          }}>
            Error: {this.state.error?.message || "Unknown error"}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Connect to Socket.IO - automatically connects to same origin
const socket = io();

// Check if we're in admin mode via URL parameter
const isAdminMode = new URLSearchParams(window.location.search).has('admin');

// Fantasy Level Descriptions
const FANTASY_LEVEL_DESCRIPTIONS = {
  1: "A realistic simulation. Nothing fantastical.",
  2: "A somewhat real simulation with some allowance for fantasy.",
  3: "A fantastical simulation still moderated by a good Game Master."
};

// Admin Panel Component
function AdminPanel() {
  const [players, setPlayers] = useState([]);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [videoBackend, setVideoBackend] = useState("luma-flash");
  const [lumaDuration, setLumaDuration] = useState(5);
  const [runwayDuration, setRunwayDuration] = useState(4);
  const [turnNumber, setTurnNumber] = useState(0);
  const [turnLimit, setTurnLimit] = useState(10);
  const [turnLimitInput, setTurnLimitInput] = useState("10");
  const [turnDuration, setTurnDuration] = useState(180);
  const [turnDurationInput, setTurnDurationInput] = useState("180");
  const [gameCompleted, setGameCompleted] = useState(false);
  const [connected, setConnected] = useState(false);
  const [isProcessingTurn, setIsProcessingTurn] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [fantasyLevel, setFantasyLevel] = useState(1);
  const [tokenEstimate, setTokenEstimate] = useState(null); // Live token tracking
  const [tokenHistory, setTokenHistory] = useState([]); // Historical token usage per turn
  const [botCount, setBotCount] = useState(0); // Number of bot players

  useEffect(() => {
    // Request initial data
    socket.emit("admin:getSettings");
    socket.emit("admin:getPlayers");

    // Handle admin events - initial data requests
    socket.on("admin:settings", (settings) => {
      setVideoEnabled(settings.videoGenerationEnabled);
      setVideoBackend(settings.videoBackend || "luma-flash");
      setLumaDuration(settings.lumaDuration || 5);
      setRunwayDuration(settings.runwayDuration || 4);
      setTurnNumber(settings.turnNumber);
      setTurnLimit(settings.turnLimit || 10);
      setTurnLimitInput(String(settings.turnLimit || 10));
      setTurnDuration(settings.turnDuration || 180);
      setTurnDurationInput(String(settings.turnDuration || 180));
      setGameCompleted(settings.gameCompleted || false);
      setIsProcessingTurn(settings.isProcessingTurn || false);
      setGameStarted(settings.gameStarted || false);
      setFantasyLevel(settings.fantasyLevel || 1);
      setTokenEstimate(settings.tokenEstimate || null);
      setTokenHistory(settings.tokenHistory || []);
    });

    // Handle real-time settings updates (broadcast from server)
    socket.on("admin:settingsUpdated", (settings) => {
      setVideoEnabled(settings.videoGenerationEnabled);
      setVideoBackend(settings.videoBackend || "luma-flash");
      setLumaDuration(settings.lumaDuration || 5);
      setRunwayDuration(settings.runwayDuration || 4);
      setTurnNumber(settings.turnNumber);
      setTurnLimit(settings.turnLimit || 10);
      setTurnLimitInput(String(settings.turnLimit || 10));
      setTurnDuration(settings.turnDuration || 180);
      setTurnDurationInput(String(settings.turnDuration || 180));
      setGameCompleted(settings.gameCompleted || false);
      setIsProcessingTurn(settings.isProcessingTurn || false);
      setGameStarted(settings.gameStarted || false);
      setFantasyLevel(settings.fantasyLevel || 1);
      setTokenEstimate(settings.tokenEstimate || null);
      setTokenHistory(settings.tokenHistory || []);
    });

    socket.on("admin:playerList", (playerList) => {
      setPlayers(playerList);
      // Count bots (socketIds starting with "bot_")
      const bots = playerList.filter(p => p.socketId.startsWith('bot_'));
      setBotCount(bots.length);
    });

    // Handle real-time player list updates (broadcast from server)
    socket.on("admin:playersUpdated", (playerList) => {
      setPlayers(playerList);
      // Count bots (socketIds starting with "bot_")
      const bots = playerList.filter(p => p.socketId.startsWith('bot_'));
      setBotCount(bots.length);
    });
    
    // Bot management responses
    socket.on("admin:botsAdded", ({ count }) => {
      console.log(`[Admin] ${count} bots added`);
    });
    
    socket.on("admin:botsRemoved", ({ count }) => {
      console.log(`[Admin] ${count} bots removed`);
    });

    socket.on("admin:error", (error) => {
      alert(`Admin Error: ${error.message}`);
    });

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("admin:getSettings");
      socket.emit("admin:getPlayers");
    });

    socket.on("disconnect", () => {
      setConnected(false);
    });

    // Check initial connection state
    setConnected(socket.connected);

    return () => {
      socket.off("admin:settings");
      socket.off("admin:settingsUpdated");
      socket.off("admin:playerList");
      socket.off("admin:playersUpdated");
      socket.off("admin:error");
      socket.off("admin:botsAdded");
      socket.off("admin:botsRemoved");
      socket.off("connect");
      socket.off("disconnect");
    };
  }, []);

  const handleKickPlayer = (socketId, playerName) => {
    if (window.confirm(`Are you sure you want to kick ${playerName}?`)) {
      socket.emit("admin:kickPlayer", socketId);
    }
  };

  const handleToggleVideo = () => {
    const newValue = !videoEnabled;
    socket.emit("admin:setVideoGeneration", newValue);
    // Don't set state here - server will broadcast the update via admin:settingsUpdated
  };

  const handleSetVideoBackend = (backend) => {
    socket.emit("admin:setVideoBackend", backend);
    // Don't set state here - server will broadcast the update via admin:settingsUpdated
  };

  const handleSetLumaDuration = (duration) => {
    socket.emit("admin:setLumaDuration", duration);
  };

  const handleSetRunwayDuration = (duration) => {
    socket.emit("admin:setRunwayDuration", duration);
  };
  
  const handleAddBots = (count) => {
    socket.emit("admin:addBots", count);
  };
  
  const handleRemoveBots = () => {
    if (window.confirm(`Remove all ${botCount} bot players?`)) {
      socket.emit("admin:removeBots");
    }
  };
  
  const handleSetTurnLimit = () => {
    const newLimit = parseInt(turnLimitInput, 10);
    if (!isNaN(newLimit) && newLimit >= 1) {
      socket.emit("admin:setTurnLimit", newLimit);
    }
  };

  const handleSetTurnDuration = () => {
    const newDuration = parseInt(turnDurationInput, 10);
    if (!isNaN(newDuration) && newDuration >= 30 && newDuration <= 600) {
      socket.emit("admin:setTurnDuration", newDuration);
    }
  };
  
  const handleResetGame = () => {
    if (window.confirm("Are you sure you want to reset the game? This will clear all history and start fresh.")) {
      socket.emit("admin:resetGame");
    }
  };
  
  const handleSetFantasyLevel = (level) => {
    socket.emit("admin:setFantasyLevel", level);
  };
  
  const handleStartGame = () => {
    if (players.length === 0) {
      alert("Cannot start game with no players!");
      return;
    }
    if (window.confirm(`Start game with ${players.length} player(s) at Fantasy Level ${fantasyLevel}?`)) {
      socket.emit("admin:startGame");
    }
  };

  // Admin panel styles
  const containerStyle = {
    backgroundColor: "#0a0a0a",
    color: "#ff3333",
    minHeight: "100vh",
    fontFamily: "'PrintChar21', 'Courier New', monospace",
    padding: "30px",
  };

  const headerStyle = {
    fontSize: "2em",
    textAlign: "center",
    marginBottom: "30px",
    color: "#ff3333",
    textShadow: "0 0 15px #ff3333",
    letterSpacing: "0.2em",
  };

  const boxStyle = {
    backgroundColor: "rgba(255, 51, 51, 0.1)",
    border: "1px solid rgba(255, 51, 51, 0.3)",
    padding: "20px",
    marginBottom: "20px",
  };

  const buttonStyle = {
    backgroundColor: "#330000",
    color: "#ff3333",
    border: "1px solid #ff3333",
    padding: "10px 20px",
    fontFamily: "'PrintChar21', 'Courier New', monospace",
    cursor: "pointer",
    fontSize: "1em",
    transition: "all 0.2s",
  };

  const checkboxContainerStyle = {
    display: "flex",
    alignItems: "center",
    gap: "15px",
    fontSize: "1.2em",
  };

  const checkboxStyle = {
    width: "24px",
    height: "24px",
    cursor: "pointer",
    accentColor: "#ff3333",
  };

  return (
    <div style={containerStyle}>
      <div className="scanlines" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 9999 }} />
      
      <div style={headerStyle}>
        ═══════════════════════════<br/>
        ADMIN CONTROL PANEL<br/>
        ═══════════════════════════
      </div>

      {/* Connection Status */}
      <div style={{
        ...boxStyle,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "15px"
      }}>
        <span>
          STATUS: {connected ? (
            <span style={{ color: "#ff2222", textShadow: "0 0 10px #ff2222, 0 0 20px #ff0000" }}>● CONNECTED</span>
          ) : (
            <span style={{ color: "#4a2a2a", textShadow: "0 0 5px #3d1a1a" }}>● DISCONNECTED</span>
          )}
        </span>
        <span>
          TURN: {turnNumber}/{turnLimit} {isProcessingTurn && (
            <span style={{ color: "#cc4444", textShadow: "0 0 10px #aa3333", marginLeft: "10px" }}>
              ⚙ PROCESSING...
            </span>
          )}
          {gameCompleted && (
            <span style={{ color: "#ff3333", textShadow: "0 0 15px #ff3333", marginLeft: "10px" }}>
              ★ GAME COMPLETED
            </span>
          )}
        </span>
        <span>PLAYERS ONLINE: {players.length}</span>
      </div>

      {/* AI Token Usage Monitor */}
      {tokenEstimate && (
        <div style={{
          ...boxStyle,
          backgroundColor: tokenEstimate.percentUsed >= 90 
            ? "rgba(255, 0, 0, 0.15)" 
            : tokenEstimate.percentUsed >= 70 
              ? "rgba(255, 150, 0, 0.1)" 
              : "rgba(57, 255, 20, 0.05)",
          border: tokenEstimate.percentUsed >= 90 
            ? "1px solid rgba(255, 0, 0, 0.5)" 
            : tokenEstimate.percentUsed >= 70 
              ? "1px solid rgba(255, 150, 0, 0.4)" 
              : "1px solid rgba(255, 51, 51, 0.3)",
        }}>
          <h3 style={{ 
            marginBottom: "15px", 
            letterSpacing: "0.1em",
            borderBottom: "1px solid rgba(255, 51, 51, 0.3)",
            paddingBottom: "10px",
            color: "#ff3333",
          }}>
            [ AI CONTEXT MONITOR ]
          </h3>
          
          {/* Progress Bar */}
          <div style={{ marginBottom: "15px" }}>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              marginBottom: "8px",
              fontSize: "0.95em",
            }}>
              <span style={{ 
                color: "#ff3333",
                textShadow: "0 0 10px #ff0000",
              }}>
                {tokenEstimate.total.toLocaleString()} / {tokenEstimate.limit.toLocaleString()} tokens
              </span>
              <span style={{ 
                fontWeight: "bold",
                color: "#ff3333",
              }}>
                {tokenEstimate.percentUsed}%
              </span>
            </div>
            <div style={{
              height: "12px",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "2px",
              overflow: "hidden",
            }}>
              <div style={{
                height: "100%",
                width: `${Math.min(100, tokenEstimate.percentUsed)}%`,
                backgroundColor: "#ff3333",
                boxShadow: "0 0 10px #ff0000",
                transition: "width 0.5s ease, background-color 0.3s ease",
              }} />
            </div>
          </div>

          {/* Key Metrics */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(3, 1fr)", 
            gap: "15px",
            marginBottom: "15px",
          }}>
            <div style={{ 
              padding: "12px",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              textAlign: "center",
            }}>
              <div style={{ fontSize: "0.75em", opacity: 0.7, marginBottom: "5px" }}>EST. SAFE TURNS</div>
              <div style={{ 
                fontSize: "1.4em", 
                fontWeight: "bold",
                color: "#ff3333",
                textShadow: "0 0 10px #ff0000",
              }}>
                {tokenEstimate.estimatedSafeTurns}
              </div>
            </div>
            <div style={{ 
              padding: "12px",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              textAlign: "center",
            }}>
              <div style={{ fontSize: "0.75em", opacity: 0.7, marginBottom: "5px" }}>TOKENS/TURN</div>
              <div style={{ fontSize: "1.4em", fontWeight: "bold", color: "#ff3333" }}>
                +{tokenEstimate.tokensPerTurn.toLocaleString()}
              </div>
            </div>
            <div style={{ 
              padding: "12px",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              textAlign: "center",
            }}>
              <div style={{ fontSize: "0.75em", opacity: 0.7, marginBottom: "5px" }}>REMAINING</div>
              <div style={{ 
                fontSize: "1.4em", 
                fontWeight: "bold",
                color: "#ff3333",
              }}>
                {(tokenEstimate.remainingTokens / 1000).toFixed(0)}K
              </div>
            </div>
          </div>

          {/* Token Breakdown (collapsible) */}
          <details style={{ marginTop: "10px" }}>
            <summary style={{ 
              cursor: "pointer", 
              opacity: 0.8, 
              fontSize: "0.85em",
              padding: "5px 0",
            }}>
              TOKEN BREAKDOWN ▼
            </summary>
            <div style={{ 
              marginTop: "10px", 
              padding: "12px",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              fontSize: "0.85em",
              lineHeight: "1.8em",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ opacity: 0.7 }}>Base Prompt:</span>
                <span>{tokenEstimate.breakdown.basePrompt.toLocaleString()}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ opacity: 0.7 }}>Turn History:</span>
                <span style={{ color: "#ff3333" }}>
                  {tokenEstimate.breakdown.history.toLocaleString()}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ opacity: 0.7 }}>Player Snapshots:</span>
                <span>{tokenEstimate.breakdown.snapshots.toLocaleString()}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ opacity: 0.7 }}>Secret Events:</span>
                <span style={{ color: "#ff3333" }}>
                  {tokenEstimate.breakdown.secretEvents.toLocaleString()}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ opacity: 0.7 }}>Regional Briefings:</span>
                <span style={{ color: "#33aaff" }}>
                  {(tokenEstimate.breakdown.regionalBriefings || 0).toLocaleString()}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ opacity: 0.7 }}>Video Captions:</span>
                <span>{tokenEstimate.breakdown.videoCaptions.toLocaleString()}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ opacity: 0.7 }}>Est. Actions:</span>
                <span>{tokenEstimate.breakdown.actions.toLocaleString()}</span>
              </div>
            </div>
          </details>

          {/* Warning Messages */}
          {tokenEstimate.percentUsed >= 90 && (
            <div style={{
              marginTop: "15px",
              padding: "12px",
              backgroundColor: "rgba(255, 0, 0, 0.2)",
              border: "1px solid rgba(255, 0, 0, 0.5)",
              color: "#ff3333",
              fontSize: "0.9em",
              textAlign: "center",
            }}>
              ⚠️ CRITICAL: Approaching token limit! Consider ending game soon or reducing turn limit.
            </div>
          )}
          {tokenEstimate.percentUsed >= 70 && tokenEstimate.percentUsed < 90 && (
            <div style={{
              marginTop: "15px",
              padding: "10px",
              backgroundColor: "rgba(255, 51, 51, 0.15)",
              border: "1px solid rgba(255, 51, 51, 0.4)",
              color: "#ff3333",
              fontSize: "0.85em",
              textAlign: "center",
            }}>
              ⚡ Token usage elevated. Monitor closely for remaining turns.
            </div>
          )}
          {tokenEstimate.estimatedSafeTurns < turnLimit && tokenEstimate.percentUsed < 70 && (
            <div style={{
              marginTop: "15px",
              padding: "10px",
              backgroundColor: "rgba(255, 51, 51, 0.1)",
              border: "1px solid rgba(255, 51, 51, 0.3)",
              color: "#ff3333",
              fontSize: "0.85em",
              textAlign: "center",
            }}>
              💡 Turn limit ({turnLimit}) exceeds estimated safe turns ({tokenEstimate.estimatedSafeTurns}). Consider reducing limit.
            </div>
          )}
          
          {/* Historical Token Tracking */}
          {tokenHistory && tokenHistory.length > 0 && (
            <div style={{ marginTop: "20px" }}>
              <h4 style={{ 
                marginBottom: "12px", 
                fontSize: "0.9em",
                opacity: 0.9,
                letterSpacing: "0.05em",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                paddingTop: "15px",
              }}>
                TOKEN USAGE HISTORY
              </h4>
              
              {/* ASCII-style mini graph */}
              <div style={{
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                padding: "15px",
                fontFamily: "monospace",
                fontSize: "0.75em",
                lineHeight: "1.2em",
                overflow: "hidden",
              }}>
                {/* Graph area */}
                <div style={{ position: "relative", height: "80px", marginBottom: "5px" }}>
                  {/* Limit line */}
                  <div style={{
                    position: "absolute",
                    top: "0",
                    left: "0",
                    right: "0",
                    borderTop: "1px dashed rgba(255, 51, 51, 0.5)",
                    fontSize: "0.7em",
                    color: "#ff3333",
                    paddingLeft: "2px",
                  }}>
                    128K
                  </div>
                  
                  {/* Warning line at 70% */}
                  <div style={{
                    position: "absolute",
                    top: "30%",
                    left: "0",
                    right: "0",
                    borderTop: "1px dashed rgba(255, 51, 51, 0.3)",
                    fontSize: "0.7em",
                    color: "#ff3333",
                    paddingLeft: "2px",
                  }}>
                    90K
                  </div>
                  
                  {/* Bars for each turn */}
                  <div style={{
                    display: "flex",
                    alignItems: "flex-end",
                    height: "100%",
                    gap: "3px",
                    paddingTop: "15px",
                  }}>
                    {tokenHistory.map((entry, idx) => {
                      const heightPercent = Math.min(100, (entry.total / 128000) * 100);
                      return (
                        <div
                          key={idx}
                          style={{
                            flex: 1,
                            maxWidth: "30px",
                            minWidth: "8px",
                            height: `${heightPercent}%`,
                            backgroundColor: "#ff3333",
                            boxShadow: "0 0 5px #ff3333",
                            position: "relative",
                            transition: "height 0.3s ease",
                          }}
                          title={`Turn ${entry.turn}: ${entry.total.toLocaleString()} tokens (+${entry.growth?.toLocaleString() || '?'})`}
                        />
                      );
                    })}
                    
                    {/* Projection bars (dotted) for remaining turns */}
                    {tokenEstimate && turnLimit > turnNumber && tokenHistory.length > 0 && (
                      Array.from({ length: Math.min(turnLimit - turnNumber, 5) }, (_, i) => {
                        const projectedTokens = tokenEstimate.total + (tokenEstimate.tokensPerTurn * (i + 1));
                        const heightPercent = Math.min(100, (projectedTokens / 128000) * 100);
                        const willExceed = projectedTokens >= 128000;
                        return (
                          <div
                            key={`proj-${i}`}
                            style={{
                              flex: 1,
                              maxWidth: "30px",
                              minWidth: "8px",
                              height: `${heightPercent}%`,
                              backgroundColor: "transparent",
                              border: `1px dashed ${willExceed ? "#ff3333" : "rgba(255, 255, 255, 0.3)"}`,
                              opacity: 0.5,
                            }}
                            title={`Projected Turn ${turnNumber + i + 1}: ~${projectedTokens.toLocaleString()} tokens`}
                          />
                        );
                      })
                    )}
                  </div>
                </div>
                
                {/* Turn labels */}
                <div style={{
                  display: "flex",
                  gap: "3px",
                  borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                  paddingTop: "3px",
                }}>
                  {tokenHistory.map((entry, idx) => (
                    <div
                      key={idx}
                      style={{
                        flex: 1,
                        maxWidth: "30px",
                        minWidth: "8px",
                        textAlign: "center",
                        fontSize: "0.7em",
                        opacity: 0.7,
                      }}
                    >
                      {entry.turn}
                    </div>
                  ))}
                  {/* Projection labels */}
                  {tokenEstimate && turnLimit > turnNumber && tokenHistory.length > 0 && (
                    Array.from({ length: Math.min(turnLimit - turnNumber, 5) }, (_, i) => (
                      <div
                        key={`proj-label-${i}`}
                        style={{
                          flex: 1,
                          maxWidth: "30px",
                          minWidth: "8px",
                          textAlign: "center",
                          fontSize: "0.7em",
                          opacity: 0.4,
                        }}
                      >
                        {turnNumber + i + 1}?
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Turn-by-turn data table */}
              <div style={{
                marginTop: "12px",
                maxHeight: "150px",
                overflowY: "auto",
                fontSize: "0.75em",
              }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ 
                      borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                    }}>
                      <th style={{ padding: "5px 3px", textAlign: "left", opacity: 0.7 }}>T#</th>
                      <th style={{ padding: "5px 3px", textAlign: "right", opacity: 0.7 }}>Total</th>
                      <th style={{ padding: "5px 3px", textAlign: "right", opacity: 0.7 }}>+Δ</th>
                      <th style={{ padding: "5px 3px", textAlign: "right", opacity: 0.7 }}>Secrets</th>
                      <th style={{ padding: "5px 3px", textAlign: "right", opacity: 0.7 }}>Regional</th>
                      <th style={{ padding: "5px 3px", textAlign: "center", opacity: 0.7 }}>P#</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...tokenHistory].reverse().map((entry, idx) => (
                      <tr 
                        key={idx}
                        style={{ 
                          borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                          backgroundColor: idx === 0 ? "rgba(255, 51, 51, 0.1)" : "transparent",
                        }}
                      >
                        <td style={{ padding: "4px 3px" }}>{entry.turn}</td>
                        <td style={{ 
                          padding: "4px 3px", 
                          textAlign: "right",
                          color: entry.total >= 115200 ? "#ff3333" : 
                                 entry.total >= 89600 ? "#ff9900" : "#ff3333",
                        }}>
                          {(entry.total / 1000).toFixed(1)}K
                        </td>
                        <td style={{ 
                          padding: "4px 3px", 
                          textAlign: "right",
                          color: entry.growth > 8000 ? "#ff9900" : "#ff3333",
                        }}>
                          {entry.growth ? `+${(entry.growth / 1000).toFixed(1)}K` : "-"}
                        </td>
                        <td style={{ 
                          padding: "4px 3px", 
                          textAlign: "right",
                          color: entry.breakdown?.secretEvents > 50000 ? "#ff9900" : "#ff3333",
                        }}>
                          {entry.breakdown?.secretEvents 
                            ? `${(entry.breakdown.secretEvents / 1000).toFixed(0)}K`
                            : "-"}
                        </td>
                        <td style={{ 
                          padding: "4px 3px", 
                          textAlign: "right",
                          color: entry.breakdown?.regionalBriefings > 30000 ? "#ff9900" : "#33aaff",
                        }}>
                          {entry.breakdown?.regionalBriefings 
                            ? `${(entry.breakdown.regionalBriefings / 1000).toFixed(0)}K`
                            : "-"}
                        </td>
                        <td style={{ padding: "4px 3px", textAlign: "center" }}>
                          {entry.playerCount || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Average growth indicator */}
              {tokenHistory.length >= 2 && (
                <div style={{
                  marginTop: "10px",
                  padding: "8px",
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  fontSize: "0.8em",
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px",
                }}>
                  <span style={{ opacity: 0.7 }}>
                    Avg Growth: <span style={{ color: "#ff3333" }}>
                      +{(tokenHistory.reduce((sum, e) => sum + (e.growth || 0), 0) / tokenHistory.length / 1000).toFixed(1)}K/turn
                    </span>
                  </span>
                  <span style={{ opacity: 0.7 }}>
                    At this rate: <span style={{ 
                      color: "#ff3333" 
                    }}>
                      ~{tokenEstimate?.estimatedSafeTurns || "?"} safe turns
                    </span>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Video Generation Settings */}
      <div style={boxStyle}>
        <h3 style={{ 
          marginBottom: "20px", 
          letterSpacing: "0.1em",
          borderBottom: "1px solid rgba(255, 51, 51, 0.3)",
          paddingBottom: "10px"
        }}>
          [ VIDEO GENERATION ]
        </h3>
        
        {/* Enable/Disable Toggle */}
        <div style={checkboxContainerStyle}>
          <input
            type="checkbox"
            id="videoToggle"
            className="admin-checkbox"
            checked={videoEnabled}
            onChange={handleToggleVideo}
            style={checkboxStyle}
          />
          <label htmlFor="videoToggle" style={{ cursor: "pointer" }}>
            GENERATE VIDEOS: {videoEnabled ? (
              <span style={{ color: "#ff2222", textShadow: "0 0 10px #ff2222" }}>ENABLED</span>
            ) : (
              <span style={{ color: "#4a2a2a" }}>DISABLED</span>
            )}
          </label>
        </div>
        
        {/* Backend Selection */}
        <div style={{ marginTop: "20px" }}>
          <label style={{ display: "block", marginBottom: "10px", letterSpacing: "0.1em" }}>
            VIDEO BACKEND:
          </label>
          <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
            <button
              className="admin-btn"
              onClick={() => handleSetVideoBackend("luma-flash")}
              style={{
                ...buttonStyle,
                backgroundColor: videoBackend === "luma-flash" ? "#660000" : "#330000",
                boxShadow: videoBackend === "luma-flash" ? "0 0 15px #ff3333" : "none",
                textShadow: videoBackend === "luma-flash" ? "0 0 10px #ff3333" : "none",
              }}
            >
              LUMA RAY FLASH 2 {videoBackend === "luma-flash" && "✓"}
            </button>
            <button
              className="admin-btn"
              onClick={() => handleSetVideoBackend("luma")}
              style={{
                ...buttonStyle,
                backgroundColor: videoBackend === "luma" ? "#660000" : "#330000",
                boxShadow: videoBackend === "luma" ? "0 0 15px #ff3333" : "none",
                textShadow: videoBackend === "luma" ? "0 0 10px #ff3333" : "none",
              }}
            >
              LUMA RAY 2 {videoBackend === "luma" && "✓"}
            </button>
            <button
              className="admin-btn"
              onClick={() => handleSetVideoBackend("runway")}
              style={{
                ...buttonStyle,
                backgroundColor: videoBackend === "runway" ? "#660000" : "#330000",
                boxShadow: videoBackend === "runway" ? "0 0 15px #ff3333" : "none",
                textShadow: videoBackend === "runway" ? "0 0 10px #ff3333" : "none",
              }}
            >
              RUNWAY ML {videoBackend === "runway" && "✓"}
            </button>
            <button
              className="admin-btn"
              onClick={() => handleSetVideoBackend("unified")}
              style={{
                ...buttonStyle,
                backgroundColor: videoBackend === "unified" ? "#660000" : "#330000",
                boxShadow: videoBackend === "unified" ? "0 0 15px #ff3333" : "none",
                textShadow: videoBackend === "unified" ? "0 0 10px #ff3333" : "none",
              }}
            >
              UNIFIED {videoBackend === "unified" && "✓"}
            </button>
          </div>
        </div>
        
        {/* Luma Duration Selection */}
        <div style={{ marginTop: "20px" }}>
          <label style={{ display: "block", marginBottom: "10px", letterSpacing: "0.1em" }}>
            LUMA DURATION (Ray Flash 2 / Ray 2):
          </label>
          <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
            <button
              className="admin-btn"
              onClick={() => handleSetLumaDuration(5)}
              style={{
                ...buttonStyle,
                backgroundColor: lumaDuration === 5 ? "#660000" : "#330000",
                boxShadow: lumaDuration === 5 ? "0 0 15px #ff3333" : "none",
                textShadow: lumaDuration === 5 ? "0 0 10px #ff3333" : "none",
              }}
            >
              5 SEC {lumaDuration === 5 && "✓"}
            </button>
            <button
              className="admin-btn"
              onClick={() => handleSetLumaDuration(9)}
              style={{
                ...buttonStyle,
                backgroundColor: lumaDuration === 9 ? "#660000" : "#330000",
                boxShadow: lumaDuration === 9 ? "0 0 15px #ff3333" : "none",
                textShadow: lumaDuration === 9 ? "0 0 10px #ff3333" : "none",
              }}
            >
              9 SEC {lumaDuration === 9 && "✓"}
            </button>
          </div>
        </div>
        
        {/* Runway Duration Selection */}
        <div style={{ marginTop: "20px" }}>
          <label style={{ display: "block", marginBottom: "10px", letterSpacing: "0.1em" }}>
            RUNWAY DURATION:
          </label>
          <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
            <button
              className="admin-btn"
              onClick={() => handleSetRunwayDuration(4)}
              style={{
                ...buttonStyle,
                backgroundColor: runwayDuration === 4 ? "#660000" : "#330000",
                boxShadow: runwayDuration === 4 ? "0 0 15px #ff3333" : "none",
                textShadow: runwayDuration === 4 ? "0 0 10px #ff3333" : "none",
              }}
            >
              4 SEC {runwayDuration === 4 && "✓"}
            </button>
            <button
              className="admin-btn"
              onClick={() => handleSetRunwayDuration(6)}
              style={{
                ...buttonStyle,
                backgroundColor: runwayDuration === 6 ? "#660000" : "#330000",
                boxShadow: runwayDuration === 6 ? "0 0 15px #ff3333" : "none",
                textShadow: runwayDuration === 6 ? "0 0 10px #ff3333" : "none",
              }}
            >
              6 SEC {runwayDuration === 6 && "✓"}
            </button>
            <button
              className="admin-btn"
              onClick={() => handleSetRunwayDuration(8)}
              style={{
                ...buttonStyle,
                backgroundColor: runwayDuration === 8 ? "#660000" : "#330000",
                boxShadow: runwayDuration === 8 ? "0 0 15px #ff3333" : "none",
                textShadow: runwayDuration === 8 ? "0 0 10px #ff3333" : "none",
              }}
            >
              8 SEC {runwayDuration === 8 && "✓"}
            </button>
          </div>
        </div>
        
        <p style={{ marginTop: "15px", opacity: 0.6, fontSize: "0.9em" }}>
          {videoBackend === "luma-flash" 
            ? `Active: Luma Ray Flash 2 (540p, ${lumaDuration}s)`
            : videoBackend === "luma"
            ? `Active: Luma Ray 2 (540p, ${lumaDuration}s)`
            : videoBackend === "unified"
            ? `Active: Unified (Runway ${runwayDuration}s → Luma ${lumaDuration}s fallback)`
            : `Active: Runway ML (720p, ${runwayDuration}s)`
          }
        </p>
      </div>

      {/* Game Settings */}
      <div style={boxStyle}>
        <h3 style={{ 
          marginBottom: "20px", 
          letterSpacing: "0.1em",
          borderBottom: "1px solid rgba(255, 51, 51, 0.3)",
          paddingBottom: "10px"
        }}>
          [ GAME SETTINGS ]
        </h3>
        
        {/* Fantasy Level Control */}
        <div style={{ marginBottom: "25px" }}>
          <label style={{ display: "block", marginBottom: "10px", letterSpacing: "0.1em" }}>
            FANTASY LEVEL:
          </label>
          <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
            {[1, 2, 3].map((level) => (
              <button
                key={level}
                className="admin-btn"
                onClick={() => handleSetFantasyLevel(level)}
                disabled={gameStarted && !gameCompleted}
                style={{
                  ...buttonStyle,
                  backgroundColor: fantasyLevel === level ? "#660000" : "#330000",
                  boxShadow: fantasyLevel === level ? "0 0 15px #ff3333" : "none",
                  textShadow: fantasyLevel === level ? "0 0 10px #ff3333" : "none",
                  opacity: (gameStarted && !gameCompleted) ? 0.5 : 1,
                  cursor: (gameStarted && !gameCompleted) ? "not-allowed" : "pointer",
                  padding: "12px 20px",
                }}
              >
                LEVEL {level} {fantasyLevel === level && "✓"}
              </button>
            ))}
          </div>
          <div style={{ 
            marginTop: "12px", 
            padding: "12px",
            backgroundColor: "rgba(255, 51, 51, 0.1)",
            border: "1px solid rgba(255, 51, 51, 0.2)",
            fontSize: "0.95em",
            lineHeight: "1.5em",
          }}>
            <strong style={{ color: "#ff6666" }}>Level {fantasyLevel}:</strong> {FANTASY_LEVEL_DESCRIPTIONS[fantasyLevel]}
          </div>
          {gameStarted && !gameCompleted && (
            <p style={{ marginTop: "8px", opacity: 0.6, fontSize: "0.8em", color: "#ff6666" }}>
              ⚠ Cannot change fantasy level while game is in progress.
            </p>
          )}
        </div>
        
        {/* Bot Players Control (only in lobby) */}
        {!gameStarted && !gameCompleted && (
          <div style={{ 
            marginBottom: "25px", 
            padding: "20px",
            backgroundColor: "rgba(51, 153, 255, 0.1)",
            border: "1px solid rgba(51, 153, 255, 0.3)",
          }}>
            <label style={{ display: "block", marginBottom: "15px", letterSpacing: "0.1em", color: "#3399ff" }}>
              🤖 BOT PLAYERS:
            </label>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "15px" }}>
              {[5, 10, 20, 30, 50].map((count) => (
                <button
                  key={count}
                  className="admin-btn"
                  onClick={() => handleAddBots(count)}
                  style={{
                    backgroundColor: "#0a1a2a",
                    color: "#3399ff",
                    border: "1px solid #3399ff",
                    padding: "10px 18px",
                    fontFamily: "'PrintChar21', 'Courier New', monospace",
                    cursor: "pointer",
                    fontSize: "0.95em",
                    letterSpacing: "0.1em",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#1a3050";
                    e.target.style.boxShadow = "0 0 15px rgba(51, 153, 255, 0.5)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#0a1a2a";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  +{count} BOTS
                </button>
              ))}
              <button
                className="admin-btn"
                onClick={handleRemoveBots}
                disabled={botCount === 0}
                style={{
                  backgroundColor: botCount > 0 ? "#2a0a0a" : "#1a0a0a",
                  color: botCount > 0 ? "#ff6666" : "#4a2a2a",
                  border: `1px solid ${botCount > 0 ? "#ff6666" : "#4a2a2a"}`,
                  padding: "10px 18px",
                  fontFamily: "'PrintChar21', 'Courier New', monospace",
                  cursor: botCount > 0 ? "pointer" : "not-allowed",
                  fontSize: "0.95em",
                  letterSpacing: "0.1em",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (botCount > 0) {
                    e.target.style.backgroundColor = "#3a0a0a";
                    e.target.style.boxShadow = "0 0 15px rgba(255, 102, 102, 0.5)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = botCount > 0 ? "#2a0a0a" : "#1a0a0a";
                  e.target.style.boxShadow = "none";
                }}
              >
                REMOVE ALL
              </button>
            </div>
            <p style={{ opacity: 0.8, fontSize: "0.9em", color: "#3399ff" }}>
              {botCount > 0 
                ? `🤖 ${botCount} bot(s) in lobby. They'll auto-submit actions each turn.`
                : "Add bots to test with a full lobby or play alongside AI nations."
              }
            </p>
          </div>
        )}
        
        {/* START GAME Button (only shown when game hasn't started) */}
        {!gameStarted && !gameCompleted && (
          <div style={{ 
            marginBottom: "25px", 
            padding: "20px",
            backgroundColor: "rgba(255, 51, 51, 0.1)",
            border: "2px solid rgba(255, 51, 51, 0.5)",
          }}>
            <label style={{ display: "block", marginBottom: "15px", letterSpacing: "0.1em", color: "#ff3333" }}>
              GAME STATUS: LOBBY (waiting to start)
            </label>
            <button
              className="admin-btn"
              onClick={handleStartGame}
              disabled={players.length === 0}
              style={{
                backgroundColor: players.length > 0 ? "#330000" : "#1a0a0a",
                color: players.length > 0 ? "#ff3333" : "#4a2a2a",
                border: players.length > 0 ? "2px solid #ff3333" : "1px solid #4a2a2a",
                padding: "20px 50px",
                fontFamily: "'PrintChar21', 'Courier New', monospace",
                cursor: players.length > 0 ? "pointer" : "not-allowed",
                fontSize: "1.3em",
                letterSpacing: "0.2em",
                boxShadow: players.length > 0 ? "0 0 20px rgba(255, 51, 51, 0.5)" : "none",
                textShadow: players.length > 0 ? "0 0 10px #ff3333" : "none",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                if (players.length > 0) {
                  e.target.style.backgroundColor = "#550000";
                  e.target.style.boxShadow = "0 0 30px rgba(255, 51, 51, 0.8)";
                }
              }}
              onMouseLeave={(e) => {
                if (players.length > 0) {
                  e.target.style.backgroundColor = "#330000";
                  e.target.style.boxShadow = "0 0 20px rgba(255, 51, 51, 0.5)";
                }
              }}
            >
              [ START GAME ]
            </button>
            <p style={{ marginTop: "15px", opacity: 0.8, fontSize: "0.9em", color: "#ff3333" }}>
              {players.length === 0 
                ? "Waiting for players to join..."
                : `${players.length} player(s) in lobby. Click to start the game.`
              }
            </p>
          </div>
        )}
        
        {/* Turn Limit Control */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "10px", letterSpacing: "0.1em" }}>
            TURN LIMIT (game ends after this turn):
          </label>
          <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
            <input
              type="number"
              min="1"
              className="admin-input"
              value={turnLimitInput}
              onChange={(e) => setTurnLimitInput(e.target.value)}
              style={{
                backgroundColor: "#1a0a0a",
                color: "#ff3333",
                border: "1px solid #ff3333",
                padding: "10px 15px",
                fontFamily: "'PrintChar21', 'Courier New', monospace",
                fontSize: "1em",
                width: "100px",
                textAlign: "center",
              }}
            />
            <button
              className="admin-btn"
              onClick={handleSetTurnLimit}
              style={{
                ...buttonStyle,
                backgroundColor: parseInt(turnLimitInput) !== turnLimit ? "#660000" : "#330000",
                boxShadow: parseInt(turnLimitInput) !== turnLimit ? "0 0 15px #ff3333" : "none",
              }}
            >
              [ SET LIMIT ]
            </button>
            <span style={{ opacity: 0.6, fontSize: "0.9em" }}>
              Current: {turnLimit} | Progress: {turnNumber}/{turnLimit}
            </span>
          </div>
          <p style={{ marginTop: "10px", opacity: 0.5, fontSize: "0.8em" }}>
            Can be changed during gameplay to extend or shorten the game.
          </p>
        </div>

        {/* Turn Duration Control */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "10px", letterSpacing: "0.1em" }}>
            TURN DURATION (seconds per turn):
          </label>
          <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
            <input
              type="number"
              min="30"
              max="600"
              className="admin-input"
              value={turnDurationInput}
              onChange={(e) => setTurnDurationInput(e.target.value)}
              style={{
                backgroundColor: "#1a0a0a",
                color: "#ff3333",
                border: "1px solid #ff3333",
                padding: "10px 15px",
                fontFamily: "'PrintChar21', 'Courier New', monospace",
                fontSize: "1em",
                width: "100px",
                textAlign: "center",
              }}
            />
            <button
              className="admin-btn"
              onClick={handleSetTurnDuration}
              style={{
                ...buttonStyle,
                backgroundColor: parseInt(turnDurationInput) !== turnDuration ? "#660000" : "#330000",
                boxShadow: parseInt(turnDurationInput) !== turnDuration ? "0 0 15px #ff3333" : "none",
              }}
            >
              [ SET DURATION ]
            </button>
            <span style={{ opacity: 0.6, fontSize: "0.9em" }}>
              Current: {turnDuration}s ({Math.floor(turnDuration / 60)}:{String(turnDuration % 60).padStart(2, '0')})
            </span>
          </div>
          <p style={{ marginTop: "10px", opacity: 0.5, fontSize: "0.8em" }}>
            Time players have to submit actions each turn (30-600 seconds). Takes effect next turn.
          </p>
        </div>
        
        {/* Reset Game Button */}
        <div style={{ marginTop: "25px", paddingTop: "20px", borderTop: "1px solid rgba(255, 51, 51, 0.2)" }}>
          <label style={{ display: "block", marginBottom: "10px", letterSpacing: "0.1em" }}>
            RESET GAME:
          </label>
          <button
            className="admin-btn"
            onClick={handleResetGame}
            style={{
              ...buttonStyle,
              backgroundColor: gameCompleted ? "#660000" : "#330000",
              boxShadow: gameCompleted ? "0 0 20px #ff3333" : "none",
              textShadow: gameCompleted ? "0 0 10px #ff3333" : "none",
              padding: "15px 30px",
              letterSpacing: "0.15em",
            }}
          >
            {gameCompleted ? "[ START NEW GAME ]" : "[ RESET GAME ]"}
          </button>
          <p style={{ marginTop: "10px", opacity: 0.5, fontSize: "0.8em" }}>
            {gameCompleted 
              ? "Game has ended. Click to start a new game with the same players."
              : "Clears all history and restarts from turn 1. Players stay connected."
            }
          </p>
        </div>
      </div>

      {/* Player List */}
      <div style={boxStyle}>
        <h3 style={{ 
          marginBottom: "20px", 
          letterSpacing: "0.1em",
          borderBottom: "1px solid rgba(255, 51, 51, 0.3)",
          paddingBottom: "10px"
        }}>
          [ ACTIVE PLAYERS ]
        </h3>
        
        {players.length === 0 ? (
          <p style={{ opacity: 0.6, textAlign: "center", padding: "20px" }}>
            NO PLAYERS CONNECTED
          </p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255, 51, 51, 0.3)" }}>
                <th style={{ textAlign: "left", padding: "10px", letterSpacing: "0.1em" }}>NATION</th>
                <th style={{ textAlign: "left", padding: "10px", letterSpacing: "0.1em" }}>INFLUENCE</th>
                <th style={{ textAlign: "left", padding: "10px", letterSpacing: "0.1em" }}>STATUS</th>
                <th style={{ textAlign: "left", padding: "10px", letterSpacing: "0.1em" }}>INACTIVITY</th>
                <th style={{ textAlign: "center", padding: "10px", letterSpacing: "0.1em" }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {players.map((player) => (
                <tr 
                  key={player.socketId} 
                  style={{ 
                    borderBottom: "1px solid rgba(255, 51, 51, 0.1)",
                    transition: "background-color 0.2s"
                  }}
                >
                  <td style={{ padding: "12px 10px", fontWeight: "bold" }}>
                    {player.socketId.startsWith('bot_') && <span style={{ marginRight: "6px" }}>🤖</span>}
                    {player.name}
                  </td>
                  <td style={{ padding: "12px 10px" }}>
                    {player.influence}
                  </td>
                  <td style={{ padding: "12px 10px" }}>
                    {player.hasSubmittedAction ? (
                      <span style={{ color: "#ff2222", textShadow: "0 0 8px #ff0000" }}>SUBMITTED</span>
                    ) : (
                      <span style={{ color: "#aa4444" }}>WAITING</span>
                    )}
                  </td>
                  <td style={{ padding: "12px 10px" }}>
                    {player.inactivityCount > 0 ? (
                      <span style={{ color: "#ff3333" }}>{player.inactivityCount} turn(s)</span>
                    ) : (
                      <span style={{ opacity: 0.6 }}>Active</span>
                    )}
                  </td>
                  <td style={{ padding: "12px 10px", textAlign: "center" }}>
                    <button
                      onClick={() => handleKickPlayer(player.socketId, player.name)}
                      style={buttonStyle}
                      className="admin-btn"
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#550000";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#330000";
                      }}
                    >
                      [ KICK ]
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Refresh Button */}
      <div style={{ textAlign: "center", marginTop: "30px" }}>
        <button
          onClick={() => {
            socket.emit("admin:getSettings");
            socket.emit("admin:getPlayers");
          }}
          style={{ ...buttonStyle, padding: "15px 40px", letterSpacing: "0.15em" }}
          className="admin-refresh-btn"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#550000";
            e.target.style.boxShadow = "0 0 25px #ff0000, 0 0 50px #ff3333, 0 0 75px #cc0000";
            e.target.style.textShadow = "0 0 15px #ff0000";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#330000";
            e.target.style.boxShadow = "none";
            e.target.style.textShadow = "none";
          }}
        >
          [ REFRESH DATA ]
        </button>
      </div>

      <div style={{ 
        marginTop: "40px", 
        textAlign: "center", 
        opacity: 0.5, 
        fontSize: "0.9em",
        letterSpacing: "0.1em"
      }}>
        TURNS ADMIN CONSOLE v1.0
      </div>
    </div>
  );
}

// Component to add random per-line flickering with randomized intervals
function FlickerText({ children, enabled = true }) {
  const containerRef = useRef(null);
  const originalTextRef = useRef('');

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    // ALWAYS update original text when children change
    originalTextRef.current = containerRef.current.textContent;

    const applyFlicker = () => {
      const element = containerRef.current;
      if (!element) return;

      const text = originalTextRef.current || element.textContent;
      
      // Split into words and randomly select DIFFERENT words each time
      const words = text.split(' ');
      const wrapped = words.map((word) => {
        // Random 15% chance for each word to flicker
        if (Math.random() < 0.15) {
          // Random duration between 1-10 seconds
          const randomDuration = 1 + Math.random() * 9;
          const randomDelay = Math.random() * randomDuration;
          return `<span class="flicker-line" style="animation-delay: ${randomDelay}s; animation-duration: ${randomDuration}s;">${word}</span>`;
        }
        return word;
      });
      
      element.innerHTML = wrapped.join(' ');
    };

    // Apply immediately
    applyFlicker();

    // Re-randomize which words flicker every 3-8 seconds
    const scheduleNext = () => {
      const nextInterval = 3000 + Math.random() * 5000; // 3-8 seconds
      return setTimeout(() => {
        applyFlicker();
        intervalRef.current = scheduleNext();
      }, nextInterval);
    };

    const intervalRef = { current: scheduleNext() };

    return () => clearTimeout(intervalRef.current);
  }, [children, enabled]);

  return <span ref={containerRef}>{children}</span>;
}

// Story Completed Screen Component - Shows when game ends
// Side-by-side layout: scrollable text on left, fixed video on right
function StoryCompletedScreen({ summary, revealedSecrets, videoHistory, turnHistory, finalInfluences, finalTurn, isMuted, setIsMuted, hasUserToggledMute, setHasUserToggledMute, finaleWaitingForTts, setFinaleWaitingForTts, gameId }) {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false); // Actual playing state (synced via onPlay/onPause)
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDurations, setVideoDurations] = useState({}); // { index: duration }
  const [isDragging, setIsDragging] = useState(false);
  const [localVideoHistory, setLocalVideoHistory] = useState(videoHistory || {});
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  // Combined video status from server - 'idle' | 'processing' | 'ready' | 'error'
  const [combinedVideoStatus, setCombinedVideoStatus] = useState('idle');
  const [combinedVideoError, setCombinedVideoError] = useState(null);
  const [showFullRankings, setShowFullRankings] = useState(false);
  // GIF conversion state - per-video
  const [gifStatus, setGifStatus] = useState({}); // { "turn_index": 'idle'|'processing'|'ready'|'error' }
  const [gifsReadyCount, setGifsReadyCount] = useState(0);
  const [gifsTotalCount, setGifsTotalCount] = useState(0);
  const [gifsProcessingCount, setGifsProcessingCount] = useState(0);
  const [allGifsReady, setAllGifsReady] = useState(false); // True only when all GIFs including finale are ready
  const [isDownloadingGif, setIsDownloadingGif] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false); // Track if video can play
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Only show loading on first video
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const playPromiseRef = useRef(null); // Track pending play() promise
  const intendedPlayingRef = useRef(true); // Track user's intended play state
  
  // Safe play function that handles the Promise properly
  const safePlay = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    
    intendedPlayingRef.current = true;
    
    // If there's a pending play promise, wait for it to resolve/reject first
    if (playPromiseRef.current) {
      try {
        await playPromiseRef.current;
      } catch (e) {
        // Ignore - we'll try to play again
      }
    }
    
    // Check if we still want to play (user might have paused while waiting)
    if (!intendedPlayingRef.current) return;
    
    try {
      playPromiseRef.current = video.play();
      await playPromiseRef.current;
    } catch (error) {
      // AbortError means play() was interrupted - this is expected and not an error
      if (error.name !== 'AbortError') {
        console.log('[Video] Play error:', error.message);
      }
    } finally {
      playPromiseRef.current = null;
    }
  }, []);
  
  // Safe pause function that handles pending play() gracefully
  const safePause = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    
    intendedPlayingRef.current = false;
    
    // If there's a pending play promise, let it complete/fail before pausing
    if (playPromiseRef.current) {
      try {
        await playPromiseRef.current;
      } catch (e) {
        // Play was interrupted, that's fine
      }
      playPromiseRef.current = null;
    }
    
    // Now safe to pause
    video.pause();
  }, []);
  
  // TTS Audio State
  const [ttsAudioUrl, setTtsAudioUrl] = useState(null);
  const [ttsStatus, setTtsStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'
  const [ttsError, setTtsError] = useState(null);
  const [ttsIsPlaying, setTtsIsPlaying] = useState(false);
  const [ttsCurrentTime, setTtsCurrentTime] = useState(0);
  const [ttsDuration, setTtsDuration] = useState(0);
  const [ttsIsDragging, setTtsIsDragging] = useState(false);
  const ttsAudioRef = useRef(null);
  const ttsProgressRef = useRef(null);
  
  // Update local video history when prop changes (for when final video arrives)
  useEffect(() => {
    setLocalVideoHistory(videoHistory || {});
  }, [videoHistory]);
  
  // Track if TTS should autoplay when loaded (only on initial load)
  const shouldAutoplayTtsRef = useRef(finaleWaitingForTts);
  
  // Fetch pre-generated TTS audio from server cache (generated ONCE per game)
  // Server generates TTS when game ends - all clients share the same cached audio
  useEffect(() => {
    if (!summary || ttsStatus !== 'idle') return;
    
    const fetchCachedTts = async () => {
      setTtsStatus('loading');
      setTtsError(null);
      
      const maxRetries = 30; // More retries since we wait for TTS before showing finale
      const retryDelay = 2000; // 2 seconds between retries
      
      // Include gameId in URL to prevent browser from serving cached audio from previous games
      const ttsUrl = gameId ? `/api/finale-tts?gameId=${gameId}` : '/api/finale-tts';
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          console.log(`[TTS] Fetching cached finale audio (attempt ${attempt + 1}, gameId: ${gameId})...`);
          const response = await fetch(ttsUrl);
          
          if (response.status === 202) {
            // TTS is still generating - wait and retry
            console.log('[TTS] Server still generating audio, waiting...');
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            continue;
          }
          
          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.error || `TTS failed: ${response.status}`);
          }
          
          const audioBlob = await response.blob();
          const audioUrl = URL.createObjectURL(audioBlob);
          setTtsAudioUrl(audioUrl);
          setTtsStatus('ready');
          console.log('[TTS] Cached audio ready - revealing finale and autoplaying narration');
          
          // Reveal the finale page now that TTS is ready
          if (setFinaleWaitingForTts) {
            setFinaleWaitingForTts(false);
          }
          
          return; // Success - exit retry loop
          
        } catch (error) {
          console.error('[TTS] Error:', error);
          if (attempt === maxRetries - 1) {
            setTtsStatus('error');
            setTtsError(error.message);
            // Still reveal finale even if TTS failed
            if (setFinaleWaitingForTts) {
              setFinaleWaitingForTts(false);
            }
          }
        }
      }
    };
    
    fetchCachedTts();
    
    // Cleanup URL on unmount
    return () => {
      if (ttsAudioUrl) {
        URL.revokeObjectURL(ttsAudioUrl);
      }
    };
  }, [summary, gameId]);
  
  // Autoplay TTS when audio URL becomes available and we should autoplay
  useEffect(() => {
    if (ttsAudioUrl && ttsStatus === 'ready' && shouldAutoplayTtsRef.current && ttsAudioRef.current) {
      // Small delay to ensure audio element is fully loaded
      const timer = setTimeout(() => {
        console.log('[TTS] Autoplaying narration...');
        ttsAudioRef.current?.play().catch(e => console.log('[TTS] Autoplay blocked:', e.message));
        shouldAutoplayTtsRef.current = false; // Only autoplay once
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [ttsAudioUrl, ttsStatus]);
  
  // TTS Audio event handlers
  const handleTtsLoadedMetadata = () => {
    if (ttsAudioRef.current) {
      setTtsDuration(ttsAudioRef.current.duration);
    }
  };
  
  const handleTtsTimeUpdate = () => {
    if (ttsAudioRef.current && !ttsIsDragging) {
      setTtsCurrentTime(ttsAudioRef.current.currentTime);
    }
  };
  
  const handleTtsEnded = () => {
    setTtsIsPlaying(false);
    // Don't reset to beginning - just stop at the end
    
    // Auto-unmute video when TTS narration ends (if user hasn't manually toggled mute)
    if (!hasUserToggledMute && setIsMuted) {
      console.log('[TTS] Narration ended - auto-unmuting video');
      setIsMuted(false);
    }
  };
  
  const handleTtsPlay = () => setTtsIsPlaying(true);
  const handleTtsPause = () => setTtsIsPlaying(false);
  
  const toggleTtsPlayPause = () => {
    if (ttsAudioRef.current) {
      if (ttsIsPlaying) {
        ttsAudioRef.current.pause();
      } else {
        ttsAudioRef.current.play().catch(e => console.log('[TTS] Play error:', e));
      }
    }
  };
  
  // TTS Scrubbing
  const handleTtsScrub = (e) => {
    if (!ttsProgressRef.current || ttsDuration === 0) return;
    
    const rect = ttsProgressRef.current.getBoundingClientRect();
    const clickX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = clickX / rect.width;
    const targetTime = percentage * ttsDuration;
    
    setTtsCurrentTime(targetTime);
    if (ttsAudioRef.current) {
      ttsAudioRef.current.currentTime = targetTime;
    }
  };
  
  const handleTtsMouseDown = (e) => {
    setTtsIsDragging(true);
    handleTtsScrub(e);
  };
  
  const handleTtsMouseMove = (e) => {
    if (ttsIsDragging) {
      handleTtsScrub(e);
    }
  };
  
  const handleTtsMouseUp = () => {
    setTtsIsDragging(false);
  };
  
  // Global mouse listeners for TTS scrubbing
  useEffect(() => {
    if (ttsIsDragging) {
      window.addEventListener('mouseup', handleTtsMouseUp);
      window.addEventListener('mousemove', handleTtsMouseMove);
      return () => {
        window.removeEventListener('mouseup', handleTtsMouseUp);
        window.removeEventListener('mousemove', handleTtsMouseMove);
      };
    }
  }, [ttsIsDragging]);
  
  // Listen for new videos arriving (final video)
  useEffect(() => {
    const handleTurnVideo = (data) => {
      console.log("StoryCompletedScreen received turnVideo:", data);
      const videoIndex = data.videoIndex !== undefined ? data.videoIndex : 0;
      
      setLocalVideoHistory(prev => {
        const turnVideos = prev[data.turnNumber] || [null, null];
        // Handle both array and single video format
        const newTurnVideos = Array.isArray(turnVideos) ? [...turnVideos] : [turnVideos, null];
        newTurnVideos[videoIndex] = data.videoUrl;
        return {
          ...prev,
          [data.turnNumber]: newTurnVideos
        };
      });
    };
    
    socket.on("turnVideo", handleTurnVideo);
    return () => socket.off("turnVideo", handleTurnVideo);
  }, []);
  
  // Listen for combined video status updates from server
  useEffect(() => {
    const handleCombinedVideoStatus = (data) => {
      console.log("[CombinedVideo] Status update:", data);
      setCombinedVideoStatus(data.status);
      setCombinedVideoError(data.error || null);
    };
    
    socket.on("combinedVideoStatus", handleCombinedVideoStatus);
    return () => socket.off("combinedVideoStatus", handleCombinedVideoStatus);
  }, []);
  
  // Listen for GIF status updates from server
  useEffect(() => {
    const handleGifStatus = (data) => {
      console.log("[GIF] Status update:", data);
      const gifKey = `${data.turnNumber}_${data.videoIndex}`;
      setGifStatus(prev => ({ ...prev, [gifKey]: data.status }));
    };
    
    const handleAllGifStatus = (data) => {
      console.log("[GIF] All status received:", data);
      // Convert server's gifStatus format to our simpler format
      const simpleStatus = {};
      for (const [key, info] of Object.entries(data.gifStatus || {})) {
        simpleStatus[key] = info.status;
      }
      setGifStatus(simpleStatus);
    };
    
    socket.on("gifStatus", handleGifStatus);
    socket.on("allGifStatus", handleAllGifStatus);
    
    return () => {
      socket.off("gifStatus", handleGifStatus);
      socket.off("allGifStatus", handleAllGifStatus);
    };
  }, []);
  
  // Poll for GIF status to update counts
  useEffect(() => {
    const pollGifsStatus = async () => {
      try {
        const response = await fetch('/api/gifs-status');
        if (response.ok) {
          const data = await response.json();
          setGifsReadyCount(data.ready);
          setGifsTotalCount(data.total);
          setGifsProcessingCount(data.processing);
          setAllGifsReady(data.allReady); // Server checks game completed + all GIFs ready
        }
      } catch (e) {
        // Ignore polling errors
      }
    };
    
    // Poll every 3 seconds
    pollGifsStatus();
    const interval = setInterval(pollGifsStatus, 3000);
    return () => clearInterval(interval);
  }, []);
  
  // Get sorted array of video URLs - flatten arrays (2 per turn) and skip missing ones
  // Handle both new format (arrays) and old format (single urls)
  const videoUrls = Object.entries(localVideoHistory || {})
    .sort(([a], [b]) => parseInt(a) - parseInt(b))
    .flatMap(([turnNum, urls]) => {
      // Handle both array format [url1, url2] and legacy single url format
      const urlArray = Array.isArray(urls) ? urls : [urls];
      return urlArray
        .filter(url => url) // Skip null/undefined
        .map((url, idx) => ({ 
          turnNumber: parseInt(turnNum), 
          videoIndex: idx,
          url 
        }));
    });
  
  const currentVideoData = videoUrls[currentVideoIndex];
  const totalVideos = videoUrls.length;
  
  // Check if we're waiting for the final video
  const hasFinalVideo = localVideoHistory && localVideoHistory[finalTurn];
  const isWaitingForFinalVideo = !hasFinalVideo && finalTurn > 0;
  const isOnLastAvailableVideo = currentVideoIndex === totalVideos - 1;
  
  // Calculate total duration and cumulative offsets
  const totalDuration = Object.values(videoDurations).reduce((sum, d) => sum + d, 0);
  const cumulativeOffsets = videoUrls.map((_, idx) => {
    let offset = 0;
    for (let i = 0; i < idx; i++) {
      offset += videoDurations[i] || 5; // Default 5s if not loaded
    }
    return offset;
  });
  
  // Calculate global time position
  const globalTime = (cumulativeOffsets[currentVideoIndex] || 0) + currentTime;
  const overallProgress = totalDuration > 0 ? (globalTime / totalDuration) * 100 : 0;
  
  // Handle video metadata loaded - store duration
  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setVideoDurations(prev => ({
        ...prev,
        [currentVideoIndex]: videoRef.current.duration
      }));
    }
  }, [currentVideoIndex]);
  
  // Auto-play when video becomes ready and user intends to play
  // We use a ref to track if we've already attempted to play for this video load
  const hasAttemptedPlayRef = useRef(false);
  
  useEffect(() => {
    // When video becomes ready, attempt to play if user wants to
    if (isVideoReady && intendedPlayingRef.current && !hasAttemptedPlayRef.current) {
      hasAttemptedPlayRef.current = true;
      setIsInitialLoad(false); // First video loaded, no more "LOADING" on transitions
      safePlay();
    }
  }, [isVideoReady, safePlay]);
  
  // Reset video ready state and play attempt when video index changes
  useEffect(() => {
    setIsVideoReady(false);
    hasAttemptedPlayRef.current = false;
  }, [currentVideoIndex]);
  
  // Handle video end - move to next video or loop (or wait for final)
  const handleVideoEnded = useCallback(() => {
    if (currentVideoIndex < totalVideos - 1) {
      // More videos to play - the next video will auto-play via isVideoReady effect
      intendedPlayingRef.current = true;
      setCurrentVideoIndex(prev => prev + 1);
      setCurrentTime(0);
    } else if (isWaitingForFinalVideo) {
      // At the end but waiting for final video - pause here
      intendedPlayingRef.current = false;
      setIsPlaying(false);
    } else {
      // All videos available, loop back to beginning
      intendedPlayingRef.current = true;
      setCurrentVideoIndex(0);
      setCurrentTime(0);
    }
  }, [currentVideoIndex, totalVideos, isWaitingForFinalVideo]);
  
  // Track previous video count to detect when final video arrives
  const prevVideoCountRef = useRef(videoUrls.length);
  
  // When final video arrives, jump to it and play
  useEffect(() => {
    const prevCount = prevVideoCountRef.current;
    const currentCount = videoUrls.length;
    prevVideoCountRef.current = currentCount;
    
    // If video count increased (new video arrived) and we have the final video
    if (currentCount > prevCount && hasFinalVideo) {
      console.log("Final video arrived, jumping to it");
      const finalIndex = currentCount - 1;
      setCurrentVideoIndex(finalIndex);
      intendedPlayingRef.current = true;
      // Auto-play effect will handle playing once video is ready
    }
  }, [videoUrls.length, hasFinalVideo]);
  
  // Handle video time update
  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current && !isDragging) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, [isDragging]);
  
  // Handle scrubbing - map global position to video + time
  const handleScrub = useCallback((e) => {
    if (!progressRef.current || totalDuration === 0) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = clickX / rect.width;
    const targetGlobalTime = percentage * totalDuration;
    
    // Find which video this time falls into
    let targetVideoIndex = 0;
    let accumulatedTime = 0;
    
    for (let i = 0; i < videoUrls.length; i++) {
      const duration = videoDurations[i] || 5;
      if (accumulatedTime + duration > targetGlobalTime) {
        targetVideoIndex = i;
        break;
      }
      accumulatedTime += duration;
      if (i === videoUrls.length - 1) {
        targetVideoIndex = i;
      }
    }
    
    const timeWithinVideo = targetGlobalTime - accumulatedTime;
    
    if (targetVideoIndex !== currentVideoIndex) {
      setCurrentVideoIndex(targetVideoIndex);
      // Wait for video to load then seek
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = Math.max(0, timeWithinVideo);
          if (intendedPlayingRef.current) {
            safePlay();
          }
        }
      }, 100);
    } else if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, timeWithinVideo);
    }
    
    setCurrentTime(Math.max(0, timeWithinVideo));
  }, [totalDuration, videoUrls.length, videoDurations, currentVideoIndex, safePlay]);
  
  // Mouse event handlers for dragging
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    handleScrub(e);
  }, [handleScrub]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      handleScrub(e);
    }
  }, [isDragging, handleScrub]);
  
  // Global mouse up listener for drag release
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleMouseMove);
      return () => {
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isDragging, handleMouseUp, handleMouseMove]);
  
  // Handle play/pause by clicking on video
  const togglePlayPause = useCallback(() => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      safePause();
    } else {
      safePlay();
    }
    // State will be updated by onPlay/onPause event handlers
  }, [isPlaying, safePlay, safePause]);
  
  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Handle download - downloads pre-processed combined video from server
  const handleDownload = async () => {
    if (combinedVideoStatus !== 'ready') {
      setDownloadError("Video not ready yet");
      return;
    }
    
    setIsDownloading(true);
    setDownloadError(null);
    
    try {
      console.log(`[Download] Downloading pre-processed combined video...`);
      
      const response = await fetch('/api/combined-video-download');
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
      
      // Get the file blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `turns_story_${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log(`[Download] Video downloaded successfully`);
    } catch (error) {
      console.error(`[Download] Error:`, error);
      setDownloadError(error.message || "Failed to download video");
    } finally {
      setIsDownloading(false);
    }
  };
  
  // Handle download all GIFs as a zip - waits for all GIFs including finale to be ready
  const handleDownloadAllGifs = async () => {
    // Check if all GIFs are ready (server validates game completed + all GIFs converted)
    if (!allGifsReady) {
      if (gifsProcessingCount > 0) {
        setDownloadError(`Still converting ${gifsProcessingCount} GIF(s)...`);
      } else if (gifsTotalCount === 0) {
        setDownloadError("No GIFs available yet");
      } else {
        setDownloadError("Waiting for finale GIF...");
      }
      return;
    }
    
    setIsDownloadingGif(true);
    setDownloadError(null);
    
    try {
      console.log(`[GIF] Downloading all ${gifsReadyCount} GIFs as zip...`);
      
      const response = await fetch('/api/download-all-gifs');
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `turns_gifs_${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log(`[GIF] All GIFs downloaded successfully`);
    } catch (error) {
      console.error(`[GIF] Download error:`, error);
      setDownloadError(error.message || "Failed to download GIFs");
    } finally {
      setIsDownloadingGif(false);
    }
  };
  
  // Handle full game log download - fetches comprehensive log from server
  const handleDownloadFullLog = async () => {
    try {
      console.log(`[Log] Downloading full game log...`);
      
      const response = await fetch('/api/full-game-log');
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `turns_full_log_${Date.now()}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log(`[Log] Full game log downloaded successfully`);
    } catch (error) {
      console.error(`[Log] Download error:`, error);
      setDownloadError(error.message || "Failed to download full log");
    }
  };
  
  // Handle game results download - generates a formatted markdown file
  const handleDownloadResults = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    
    // Build the markdown content
    let content = `# TURNS: Mutually Assured\n`;
    content += `## Game Results - ${timestamp}\n\n`;
    content += `═══════════════════════════════════════════════════════════════\n\n`;
    
    // Final Report (Summary)
    content += `## THE FINAL REPORT\n\n`;
    content += `${summary || "No summary available."}\n\n`;
    content += `───────────────────────────────────────────────────────────────\n\n`;
    
    // Final Influence Scores
    if (sortedInfluences.length > 0) {
      content += `## FINAL INFLUENCE SCORES\n\n`;
      sortedInfluences.forEach(([nation, score], index) => {
        const prefix = index === 0 ? "★ " : "  ";
        const suffix = index === 0 ? " (WINNER)" : "";
        content += `${prefix}${index + 1}. ${nation.toUpperCase()}: ${score}${suffix}\n`;
      });
      content += `\n───────────────────────────────────────────────────────────────\n\n`;
    }
    
    // Declassified Secrets
    if (revealedSecrets && revealedSecrets.length > 0) {
      content += `## DECLASSIFIED SECRETS\n\n`;
      revealedSecrets.forEach((secret, idx) => {
        content += `► ${secret}\n\n`;
      });
      content += `───────────────────────────────────────────────────────────────\n\n`;
    }
    
    // Complete History
    if (turnHistory && turnHistory.length > 0) {
      content += `## COMPLETE HISTORY\n\n`;
      [...turnHistory].reverse().forEach((turn) => {
        content += `### TURN ${turn.turnNumber}\n\n`;
        
        // Global Outcome
        if (turn.globalOutcome || turn.outcome) {
          content += `${turn.globalOutcome || turn.outcome}\n\n`;
        }
        
        content += `---\n\n`;
      });
    }
    
    // Footer
    content += `═══════════════════════════════════════════════════════════════\n`;
    content += `END OF TRANSMISSION\n`;
    content += `Generated: ${new Date().toLocaleString()}\n`;
    
    // Create and trigger download
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `turns_results_${Date.now()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    console.log(`[Download] Game results downloaded successfully`);
  };
  
  // Sort influences by score for display
  const sortedInfluences = Object.entries(finalInfluences || {})
    .sort(([, a], [, b]) => b - a);

  // Show loading screen while waiting for TTS to load before revealing finale
  if (finaleWaitingForTts) {
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "#000a00",
        color: "#39FF14",
        fontFamily: "'PrintChar21', 'Courier New', monospace",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
      }}>
        {/* CRT Scanlines */}
        <div className="scanlines" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 9999 }} />
        
        {/* Loading content */}
        <div style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "rgba(57, 255, 20, 0.05)",
          border: "2px solid rgba(57, 255, 20, 0.4)",
          maxWidth: "500px",
        }}>
          <div className="pulse-animation" style={{
            fontSize: "2em",
            fontWeight: "bold",
            color: "#39FF14",
            textShadow: "0 0 20px #39FF14, 0 0 40px #39FF14",
            letterSpacing: "0.15em",
            marginBottom: "30px",
          }}>
            ══ GAME COMPLETE ══
          </div>
          
          <div style={{
            fontSize: "1.2em",
            marginBottom: "30px",
            letterSpacing: "0.1em",
            opacity: 0.9,
          }}>
            PREPARING THE FINALE...
          </div>
          
          {/* Loading animation */}
          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            marginBottom: "20px",
          }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  width: "12px",
                  height: "12px",
                  backgroundColor: "#39FF14",
                  boxShadow: "0 0 10px #39FF14",
                  animation: `pulse-dot 1.4s ease-in-out ${i * 0.15}s infinite`,
                }}
              />
            ))}
          </div>
          
          <div style={{
            fontSize: "0.85em",
            opacity: 0.6,
            letterSpacing: "0.05em",
          }}>
            {ttsStatus === 'loading' ? 'Generating audio narration...' : 
             ttsStatus === 'error' ? 'Audio unavailable - loading finale...' :
             'Loading finale assets...'}
          </div>
        </div>
        
        {/* CSS for loading animation */}
        <style>{`
          @keyframes pulse-dot {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "#000a00",
      color: "#39FF14",
      fontFamily: "'PrintChar21', 'Courier New', monospace",
      display: "flex",
      zIndex: 2000,
    }}>
      {/* CRT Scanlines */}
      <div className="scanlines" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 9999 }} />
      
      {/* LEFT SIDE - Scrollable Text Content */}
      <div style={{
        width: "50%",
        height: "100vh",
        overflowY: "auto",
        padding: "30px",
        boxSizing: "border-box",
      }}>
        {/* Header */}
        <div style={{
          textAlign: "center",
          marginBottom: "30px",
          padding: "20px",
          backgroundColor: "rgba(57, 255, 20, 0.05)",
          border: "1px solid rgba(57, 255, 20, 0.3)",
        }}>
          <div className="pulse-animation" style={{
            fontSize: "1.8em",
            fontWeight: "bold",
            color: "#39FF14",
            textShadow: "0 0 20px #39FF14, 0 0 40px #39FF14",
            letterSpacing: "0.15em",
            marginBottom: "8px",
          }}>
            ══ STORY COMPLETED ══
          </div>
          <div style={{
            fontSize: "0.9em",
            opacity: 0.7,
            letterSpacing: "0.1em",
          }}>
            TURNS: Mutually Assured
          </div>
        </div>

        {/* Story Summary */}
        <div style={{
          backgroundColor: "rgba(57, 255, 20, 0.05)",
          border: "1px solid rgba(57, 255, 20, 0.3)",
          padding: "25px",
          marginBottom: "25px",
        }}>
          <h2 style={{
            fontSize: "1.3em",
            marginBottom: "20px",
            letterSpacing: "0.1em",
            borderBottom: "1px solid rgba(57, 255, 20, 0.3)",
            paddingBottom: "15px",
            textShadow: "0 0 10px #39FF14",
          }}>
            [ THE FINAL REPORT ]
          </h2>
          
          {/* TTS Audio Player */}
          <div style={{
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            border: "1px solid rgba(57, 255, 20, 0.4)",
            padding: "15px",
            marginBottom: "20px",
            borderRadius: "2px",
          }}>
            {/* Hidden audio element */}
            {ttsAudioUrl && (
              <audio
                ref={ttsAudioRef}
                src={ttsAudioUrl}
                onLoadedMetadata={handleTtsLoadedMetadata}
                onTimeUpdate={handleTtsTimeUpdate}
                onEnded={handleTtsEnded}
                onPlay={handleTtsPlay}
                onPause={handleTtsPause}
                preload="auto"
              />
            )}
            
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
            }}>
              {/* Play/Pause Button */}
              <button
                onClick={toggleTtsPlayPause}
                disabled={ttsStatus !== 'ready'}
                style={{
                  width: "44px",
                  height: "44px",
                  backgroundColor: ttsStatus === 'ready' ? "rgba(57, 255, 20, 0.15)" : "rgba(57, 255, 20, 0.05)",
                  border: `2px solid ${ttsStatus === 'ready' ? '#39FF14' : 'rgba(57, 255, 20, 0.3)'}`,
                  borderRadius: "2px",
                  color: ttsStatus === 'ready' ? "#39FF14" : "rgba(57, 255, 20, 0.4)",
                  cursor: ttsStatus === 'ready' ? "pointer" : "default",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "'PrintChar21', monospace",
                  fontSize: "1.2em",
                  fontWeight: "bold",
                  transition: "all 0.2s ease",
                  textShadow: ttsStatus === 'ready' ? "0 0 8px #39FF14" : "none",
                  boxShadow: ttsStatus === 'ready' ? "0 0 10px rgba(57, 255, 20, 0.3), inset 0 0 10px rgba(57, 255, 20, 0.1)" : "none",
                }}
              >
                {ttsStatus === 'loading' ? (
                  <span style={{ animation: "spin 1s linear infinite" }}>◌</span>
                ) : ttsIsPlaying ? (
                  "▮▮"
                ) : (
                  "▶"
                )}
              </button>
              
              {/* Scrubber / Progress Bar */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                {/* Label */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.75em",
                  opacity: 0.7,
                  letterSpacing: "0.1em",
                }}>
                  <span>
                    {ttsStatus === 'loading' ? 'GENERATING NARRATION...' : 
                     ttsStatus === 'error' ? `ERROR: ${ttsError}` :
                     ttsStatus === 'ready' ? 'AUDIO NARRATION' : 'LOADING...'}
                  </span>
                  <span>
                    {formatTime(ttsCurrentTime)} / {formatTime(ttsDuration)}
                  </span>
                </div>
                
                {/* Progress Bar Track */}
                <div
                  ref={ttsProgressRef}
                  onMouseDown={handleTtsMouseDown}
                  style={{
                    height: "12px",
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    border: "1px solid rgba(57, 255, 20, 0.3)",
                    borderRadius: "1px",
                    cursor: ttsStatus === 'ready' ? "pointer" : "default",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Progress Fill */}
                  <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: ttsDuration > 0 ? `${(ttsCurrentTime / ttsDuration) * 100}%` : "0%",
                    backgroundColor: "#39FF14",
                    boxShadow: "0 0 10px #39FF14, 0 0 20px rgba(57, 255, 20, 0.5)",
                    transition: ttsIsDragging ? "none" : "width 0.1s linear",
                  }} />
                  
                  {/* Playhead */}
                  {ttsStatus === 'ready' && (
                    <div style={{
                      position: "absolute",
                      top: "-2px",
                      left: ttsDuration > 0 ? `calc(${(ttsCurrentTime / ttsDuration) * 100}% - 4px)` : "0",
                      width: "8px",
                      height: "16px",
                      backgroundColor: "#39FF14",
                      border: "1px solid #001100",
                      boxShadow: "0 0 8px #39FF14",
                      transition: ttsIsDragging ? "none" : "left 0.1s linear",
                    }} />
                  )}
                  
                  {/* Loading shimmer effect */}
                  {ttsStatus === 'loading' && (
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: "100%",
                      width: "30%",
                      background: "linear-gradient(90deg, transparent, rgba(57, 255, 20, 0.3), transparent)",
                      animation: "shimmer 1.5s infinite",
                    }} />
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div style={{
            fontSize: "1em",
            lineHeight: "1.8em",
            fontWeight: "bold",
            whiteSpace: "pre-wrap",
          }}>
            {summary || "No summary available."}
          </div>
        </div>

        {/* Revealed Secrets */}
        {revealedSecrets && revealedSecrets.length > 0 && (
          <div style={{
            backgroundColor: "rgba(57, 255, 20, 0.08)",
            border: "1px solid rgba(57, 255, 20, 0.4)",
            padding: "25px",
            marginBottom: "25px",
          }}>
            <h2 style={{
              fontSize: "1.3em",
              marginBottom: "20px",
              letterSpacing: "0.1em",
              borderBottom: "1px solid rgba(57, 255, 20, 0.3)",
              paddingBottom: "15px",
              textShadow: "0 0 10px #39FF14",
            }}>
              [ DECLASSIFIED SECRETS ]
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
              {revealedSecrets.map((secret, idx) => (
                <div key={idx} style={{
                  padding: "15px",
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  border: "1px solid rgba(57, 255, 20, 0.2)",
                  fontSize: "0.95em",
                  lineHeight: "1.6em",
                  fontWeight: "bold",
                }}>
                  <span style={{ opacity: 0.6, marginRight: "10px" }}>►</span>
                  {secret}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Turn History Summary */}
        {turnHistory && turnHistory.length > 0 && (
          <div style={{
            backgroundColor: "rgba(57, 255, 20, 0.03)",
            border: "1px solid rgba(57, 255, 20, 0.2)",
            padding: "25px",
            marginBottom: "25px",
          }}>
            <h2 style={{
              fontSize: "1.3em",
              marginBottom: "20px",
              letterSpacing: "0.1em",
              borderBottom: "1px solid rgba(57, 255, 20, 0.3)",
              paddingBottom: "15px",
              textShadow: "0 0 10px #39FF14",
            }}>
              [ COMPLETE HISTORY ]
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {[...turnHistory].reverse().map((turn, idx) => (
                <div key={turn.turnNumber || idx} style={{
                  padding: "15px",
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  border: "1px solid rgba(57, 255, 20, 0.15)",
                }}>
                  <div style={{
                    fontSize: "0.85em",
                    opacity: 0.7,
                    marginBottom: "10px",
                    letterSpacing: "0.1em",
                  }}>
                    TURN {turn.turnNumber}
                  </div>
                  {/* Turn Outcome */}
                  {(turn.globalOutcome || turn.outcome) && (
                    <div style={{
                      fontSize: "0.9em",
                      lineHeight: "1.6em",
                    }}>
                      {turn.globalOutcome || turn.outcome}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          textAlign: "center",
          padding: "30px",
          opacity: 0.5,
          fontSize: "0.85em",
          letterSpacing: "0.1em",
        }}>
          END OF TRANSMISSION
        </div>
      </div>
      
      {/* RIGHT SIDE - Fixed Video Player */}
      <div style={{
        width: "50%",
        height: "100vh",
        backgroundColor: "#000",
        display: "flex",
        flexDirection: "column",
        borderLeft: "2px solid rgba(57, 255, 20, 0.3)",
      }}>
        {/* Final Influence Scores - Clickable */}
        {sortedInfluences.length > 0 && (
          <div 
            style={{
              padding: "15px 20px",
              backgroundColor: "rgba(57, 255, 20, 0.08)",
              borderBottom: "1px solid rgba(57, 255, 20, 0.3)",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onClick={() => setShowFullRankings(true)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(57, 255, 20, 0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(57, 255, 20, 0.08)";
            }}
            title="Click for full rankings"
          >
            <div style={{
              fontSize: "1em",
              letterSpacing: "0.1em",
              marginBottom: "15px",
              textShadow: "0 0 10px #39FF14",
            }}>
              [ FINAL INFLUENCE SCORES ]
            </div>
            <InfluenceRankingsPanel
              influences={sortedInfluences}
              showWinner={true}
              onShowAll={() => setShowFullRankings(true)}
            />
          </div>
        )}
        
        {/* Video Container - Takes remaining space */}
        <div 
          style={{
            flex: 1,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#000",
            cursor: currentVideoData ? "pointer" : "default",
          }}
          onClick={currentVideoData ? togglePlayPause : undefined}
        >
          {currentVideoData ? (
            <>
              <video
                ref={videoRef}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  filter: "contrast(1.1) saturate(0.9)",
                }}
                src={currentVideoData.url}
                muted={isMuted}
                playsInline
                preload="auto"
                onEnded={handleVideoEnded}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onCanPlay={() => setIsVideoReady(true)}
                onLoadStart={() => setIsVideoReady(false)}
              />
              {/* CRT overlay */}
              <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "linear-gradient(transparent 50%, rgba(0,0,0,0.08) 50%)",
                backgroundSize: "100% 3px",
                pointerEvents: "none",
              }} />
              {/* Loading indicator - only show on initial load */}
              {isInitialLoad && !isVideoReady && (
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: "1.5em",
                  opacity: 0.8,
                  textShadow: "0 0 15px #39FF14",
                  pointerEvents: "none",
                  animation: "pulse 1.5s infinite",
                  letterSpacing: "0.2em",
                }}>
                  LOADING...
                </div>
              )}
              {/* Play indicator - show when paused (except during initial load or waiting for final) */}
              {!isPlaying && !isInitialLoad && !(isWaitingForFinalVideo && isOnLastAvailableVideo) && (
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: "3em",
                  opacity: 0.7,
                  textShadow: "0 0 20px #39FF14",
                  pointerEvents: "none",
                }}>
                  ▶
                </div>
              )}
              {/* Waiting for finale - prominent full overlay */}
              {!isInitialLoad && isWaitingForFinalVideo && isOnLastAvailableVideo && (
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 8, 0, 0.5)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 20,
                  color: "#39FF14",
                  fontFamily: "'PrintChar21', 'Courier New', monospace",
                }}>
                  {/* ASCII-style loading spinner */}
                  <div style={{
                    width: "50px",
                    height: "50px",
                    border: "2px solid rgba(57, 255, 20, 0.2)",
                    borderTop: "2px solid #39FF14",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginBottom: "20px",
                    boxShadow: "0 0 15px rgba(57, 255, 20, 0.4)",
                  }} />
                  <div style={{
                    fontSize: "1.3em",
                    textShadow: "0 0 15px #39FF14",
                    letterSpacing: "0.15em",
                    marginBottom: "8px",
                    animation: "pulse 2s infinite",
                  }}>
                    [ GENERATING FINALE ]
                  </div>
                  <div style={{
                    fontSize: "0.85em",
                    opacity: 0.6,
                    letterSpacing: "0.1em",
                  }}>
                    PLEASE WAIT...
                  </div>
                  {!isPlaying && (
                    <div style={{
                      marginTop: "20px",
                      fontSize: "0.75em",
                      opacity: 0.5,
                    }}>
                      [ CLICK TO REPLAY ]
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{
              fontSize: "1.2em",
              opacity: 0.4,
              letterSpacing: "0.1em",
              textAlign: "center",
            }}>
              {isWaitingForFinalVideo ? (
                <div style={{ animation: "pulse 2s infinite" }}>
                  AWAITING FINAL FOOTAGE...
                </div>
              ) : (
                "NO FOOTAGE AVAILABLE"
              )}
            </div>
          )}
        </div>
        
        {/* Video Controls - Simplified */}
        <div style={{
          padding: "15px 20px",
          backgroundColor: "rgba(0, 17, 0, 0.8)",
          borderTop: "1px solid rgba(57, 255, 20, 0.3)",
        }}>
          {/* Progress Bar - Seamless scrubbing */}
          <div 
            ref={progressRef}
            style={{
              width: "100%",
              height: "12px",
              backgroundColor: "rgba(57, 255, 20, 0.15)",
              cursor: "pointer",
              position: "relative",
              borderRadius: "2px",
            }}
            onMouseDown={handleMouseDown}
          >
            {/* Progress fill */}
            <div style={{
              width: `${overallProgress}%`,
              height: "100%",
              backgroundColor: "#39FF14",
              boxShadow: "0 0 10px #39FF14",
              borderRadius: "2px",
              transition: isDragging ? "none" : "width 0.1s linear",
            }} />
            {/* Scrubber handle */}
            <div style={{
              position: "absolute",
              left: `${overallProgress}%`,
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: "16px",
              height: "16px",
              backgroundColor: "#39FF14",
              borderRadius: "50%",
              boxShadow: "0 0 15px #39FF14",
              border: "2px solid #001100",
            }} />
            {/* Segment markers (subtle) */}
            {videoUrls.map((_, idx) => {
              const markerPos = totalDuration > 0 
                ? ((cumulativeOffsets[idx] || 0) / totalDuration) * 100 
                : (idx / totalVideos) * 100;
              return idx > 0 ? (
                <div key={idx} style={{
                  position: "absolute",
                  left: `${markerPos}%`,
                  top: 0,
                  bottom: 0,
                  width: "1px",
                  backgroundColor: "rgba(57, 255, 20, 0.3)",
                }} />
              ) : null;
            })}
          </div>
          
          {/* Controls row - play hint, mute, time */}
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "10px",
            fontSize: "0.85em",
            letterSpacing: "0.05em",
          }}>
            <span style={{ opacity: 0.7 }}>CLICK VIDEO TO {isPlaying ? "PAUSE" : "PLAY"}</span>
            
            {/* Mute/Unmute Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMuted(!isMuted);
                // Track that user has manually toggled mute (prevents auto-unmute after TTS)
                if (setHasUserToggledMute) {
                  setHasUserToggledMute(true);
                }
              }}
              style={{
                background: "none",
                border: "1px solid rgba(57, 255, 20, 0.5)",
                color: isMuted ? "#ff4444" : "#39FF14",
                padding: "4px 12px",
                fontFamily: "'PrintChar21', 'Courier New', monospace",
                fontSize: "0.9em",
                letterSpacing: "0.1em",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
                textShadow: isMuted ? "0 0 8px #ff4444" : "0 0 8px #39FF14",
                borderColor: isMuted ? "rgba(255, 68, 68, 0.5)" : "rgba(57, 255, 20, 0.5)",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = isMuted ? "rgba(255, 68, 68, 0.15)" : "rgba(57, 255, 20, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
              }}
            >
              {isMuted ? "[X] MUTED" : "[*] AUDIO"}
            </button>
            
            <span style={{ opacity: 0.7 }}>{formatTime(globalTime)} / {formatTime(totalDuration || totalVideos * 5)}</span>
          </div>
          
          {/* Download Buttons */}
          <div style={{ marginTop: "15px" }}>
            {(() => {
              const isNotReady = combinedVideoStatus !== 'ready';
              const isProcessing = combinedVideoStatus === 'processing';
              const hasError = combinedVideoStatus === 'error';
              const isDisabled = isDownloading || isNotReady;
              
              // Determine button text based on status
              let buttonText = "[ DOWNLOAD VIDEO ]";
              if (isDownloading) {
                buttonText = "[ DOWNLOADING... ]";
              } else if (isProcessing) {
                buttonText = "[ PROCESSING... ]";
              } else if (hasError) {
                buttonText = "[ FAILED ]";
              } else if (combinedVideoStatus === 'idle') {
                buttonText = "[ AWAITING... ]";
              }
              
              // GIF button state - all individual GIFs including finale must be ready
              const gifsProcessing = gifsProcessingCount > 0;
              const gifDisabled = isDownloadingGif || !allGifsReady;
              
              // GIF button text
              let gifButtonText = "[ DOWNLOAD GIFS ]";
              if (isDownloadingGif) {
                gifButtonText = "[ DOWNLOADING... ]";
              } else if (gifsProcessing) {
                gifButtonText = `[ CONVERTING ${gifsReadyCount}/${gifsTotalCount} ]`;
              } else if (gifsTotalCount === 0) {
                gifButtonText = "[ AWAITING GIFS ]";
              } else if (!allGifsReady) {
                gifButtonText = `[ ${gifsReadyCount}/${gifsTotalCount} READY ]`;
              } else {
                gifButtonText = `[ DOWNLOAD ${gifsTotalCount} GIFS ]`;
              }
              
              return (
                <>
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    gap: "15px",
                    flexWrap: "wrap",
                  }}>
                    <button
                      onClick={handleDownloadResults}
                      style={{
                        padding: "12px 24px",
                        fontSize: "0.95em",
                        fontFamily: "'PrintChar21', 'Courier New', monospace",
                        letterSpacing: "0.12em",
                        backgroundColor: "#001a00",
                        color: "#39FF14",
                        border: "2px solid #39FF14",
                        cursor: "pointer",
                        textShadow: "0 0 10px #39FF14",
                        boxShadow: "0 0 20px rgba(57, 255, 20, 0.3)",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#003300";
                        e.target.style.boxShadow = "0 0 30px rgba(57, 255, 20, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#001a00";
                        e.target.style.boxShadow = "0 0 20px rgba(57, 255, 20, 0.3)";
                      }}
                    >
                      [ DOWNLOAD RESULTS ]
                    </button>
                    
                    <button
                      onClick={handleDownloadFullLog}
                      style={{
                        padding: "12px 24px",
                        fontSize: "0.95em",
                        fontFamily: "'PrintChar21', 'Courier New', monospace",
                        letterSpacing: "0.12em",
                        backgroundColor: "#001a00",
                        color: "#39FF14",
                        border: "2px solid #39FF14",
                        cursor: "pointer",
                        textShadow: "0 0 10px #39FF14",
                        boxShadow: "0 0 20px rgba(57, 255, 20, 0.3)",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#003300";
                        e.target.style.boxShadow = "0 0 30px rgba(57, 255, 20, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#001a00";
                        e.target.style.boxShadow = "0 0 20px rgba(57, 255, 20, 0.3)";
                      }}
                    >
                      [ DOWNLOAD FULL LOG ]
                    </button>
                    
                    <button
                      onClick={handleDownload}
                      disabled={isDisabled}
                      style={{
                        padding: "12px 24px",
                        fontSize: "0.95em",
                        fontFamily: "'PrintChar21', 'Courier New', monospace",
                        letterSpacing: "0.12em",
                        backgroundColor: isDisabled ? "#222" : "#001a00",
                        color: isDisabled ? (hasError ? "#ff4444" : "#666") : "#39FF14",
                        border: "2px solid",
                        borderColor: isDisabled ? (hasError ? "#ff4444" : "#333") : "#39FF14",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        textShadow: isDisabled ? "none" : "0 0 10px #39FF14",
                        boxShadow: isDisabled ? "none" : "0 0 20px rgba(57, 255, 20, 0.3)",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isDisabled) {
                          e.target.style.backgroundColor = "#003300";
                          e.target.style.boxShadow = "0 0 30px rgba(57, 255, 20, 0.5)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isDisabled) {
                          e.target.style.backgroundColor = "#001a00";
                          e.target.style.boxShadow = "0 0 20px rgba(57, 255, 20, 0.3)";
                        }
                      }}
                    >
                      {buttonText}
                    </button>
                    
                    <button
                      onClick={handleDownloadAllGifs}
                      disabled={gifDisabled}
                      style={{
                        padding: "12px 24px",
                        fontSize: "0.95em",
                        fontFamily: "'PrintChar21', 'Courier New', monospace",
                        letterSpacing: "0.12em",
                        backgroundColor: gifDisabled ? "#222" : "#001a00",
                        color: gifDisabled ? "#666" : "#39FF14",
                        border: "2px solid",
                        borderColor: gifDisabled ? "#333" : "#39FF14",
                        cursor: gifDisabled ? "not-allowed" : "pointer",
                        textShadow: gifDisabled ? "none" : "0 0 10px #39FF14",
                        boxShadow: gifDisabled ? "none" : "0 0 20px rgba(57, 255, 20, 0.3)",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!gifDisabled) {
                          e.target.style.backgroundColor = "#003300";
                          e.target.style.boxShadow = "0 0 30px rgba(57, 255, 20, 0.5)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!gifDisabled) {
                          e.target.style.backgroundColor = "#001a00";
                          e.target.style.boxShadow = "0 0 20px rgba(57, 255, 20, 0.3)";
                        }
                      }}
                    >
                      {gifButtonText}
                    </button>
                  </div>
                  
                  {(downloadError || combinedVideoError) && (
                    <div style={{
                      marginTop: "10px",
                      color: "#ff4444",
                      fontSize: "0.85em",
                      textShadow: "0 0 5px #ff4444",
                      textAlign: "center",
                    }}>
                      ERROR: {downloadError || combinedVideoError}
                    </div>
                  )}
                  {isProcessing && (
                    <div style={{
                      marginTop: "8px",
                      fontSize: "0.75em",
                      opacity: 0.7,
                      color: "#ffaa00",
                      textAlign: "center",
                    }}>
                      Server is combining {videoUrls.length} clips...
                    </div>
                  )}
                  {gifsProcessing && (
                    <div style={{
                      marginTop: "8px",
                      fontSize: "0.75em",
                      opacity: 0.7,
                      color: "#ffaa00",
                      textAlign: "center",
                    }}>
                      Converting GIFs: {gifsReadyCount}/{gifsTotalCount} complete...
                    </div>
                  )}
                  {combinedVideoStatus === 'ready' && !isDownloading && !gifsProcessing && (
                    <div style={{
                      marginTop: "8px",
                      fontSize: "0.75em",
                      opacity: 0.5,
                      textAlign: "center",
                    }}>
                      {videoUrls.length} clips combined and ready
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
      
      {/* Full Rankings Overlay */}
      {showFullRankings && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.95)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
          onClick={(e) => e.target === e.currentTarget && setShowFullRankings(false)}
        >
          <div className="scanlines" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 9999 }} />
          
          <div style={{
            backgroundColor: "rgba(0, 17, 0, 0.98)",
            border: "2px solid #39FF14",
            padding: "25px 30px",
            maxWidth: "500px",
            width: "90%",
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 0 40px rgba(57, 255, 20, 0.3), inset 0 0 30px rgba(57, 255, 20, 0.1)",
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              borderBottom: "1px solid #39FF14",
              paddingBottom: "15px",
            }}>
              <div style={{
                fontSize: "1.2em",
                color: "#FFD700",
                textShadow: "0 0 15px #FFD700",
                letterSpacing: "0.15em",
              }}>
                [ FINAL STANDINGS ]
              </div>
              <button
                onClick={() => setShowFullRankings(false)}
                style={{
                  background: "none",
                  border: "1px solid rgba(57, 255, 20, 0.5)",
                  color: "#39FF14",
                  padding: "6px 12px",
                  fontFamily: "'PrintChar21', 'Courier New', monospace",
                  fontSize: "0.9em",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "rgba(57, 255, 20, 0.2)";
                  e.target.style.boxShadow = "0 0 10px rgba(57, 255, 20, 0.5)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.boxShadow = "none";
                }}
              >
                ESC
              </button>
            </div>
            
            <div style={{
              flex: 1,
              overflowY: "auto",
              paddingRight: "10px",
            }}>
              {sortedInfluences.map(([nation, score], index) => {
                const isWinner = index === 0;
                return (
                  <div
                    key={nation}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 15px",
                      marginBottom: "6px",
                      backgroundColor: isWinner ? "rgba(255, 215, 0, 0.15)" : "rgba(57, 255, 20, 0.05)",
                      border: isWinner ? "1px solid rgba(255, 215, 0, 0.5)" : "1px solid rgba(57, 255, 20, 0.2)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      {isWinner && <span style={{ color: "#FFD700", fontSize: "1.2em" }}>★</span>}
                      <span style={{
                        fontWeight: "bold",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: isWinner ? "#FFD700" : "#39FF14",
                        textShadow: isWinner ? "0 0 10px #FFD700" : "none",
                      }}>
                        {index + 1}. {nation}
                      </span>
                    </div>
                    <span style={{
                      fontWeight: "bold",
                      color: isWinner ? "#FFD700" : "#39FF14",
                      textShadow: isWinner ? "0 0 8px #FFD700" : "0 0 5px #39FF14",
                    }}>
                      {score}
                    </span>
                  </div>
                );
              })}
            </div>
            
            <div style={{
              marginTop: "15px",
              paddingTop: "15px",
              borderTop: "1px solid rgba(57, 255, 20, 0.3)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "0.8em",
              opacity: 0.6,
            }}>
              <span>{sortedInfluences.length} NATIONS FINAL</span>
              <span>PRESS ESC TO CLOSE</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const [name, setName] = useState("");
  const [isNameSet, setIsNameSet] = useState(false);
  const [publicAction, setPublicAction] = useState("");
  const [privateAction, setPrivateAction] = useState("");
  const [playerCount, setPlayerCount] = useState(0);
  const [playerList, setPlayerList] = useState([]);
  const [turnNumber, setTurnNumber] = useState(0);
  const [tick, setTick] = useState(0); // Force re-renders for timer display
  const [turnOutcome, setTurnOutcome] = useState("");
  const [globalOutcome, setGlobalOutcome] = useState("");
  const [latestRegionalBriefing, setLatestRegionalBriefing] = useState(""); // Personalized regional briefing for this player
  const [regionalBriefingHistory, setRegionalBriefingHistory] = useState({}); // { turnNumber: briefing }
  const [actionSubmitted, setActionSubmitted] = useState(false);
  const [turnHistory, setTurnHistory] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [playerSnapshot, setPlayerSnapshot] = useState({});
  const [latestSecretEvent, setLatestSecretEvent] = useState("");
  const [secretHistory, setSecretHistory] = useState({});
  const [showPlayerData, setShowPlayerData] = useState(true);
  const [playerInfluences, setPlayerInfluences] = useState([]);
  const [loadingDots, setLoadingDots] = useState("");
  const [currentVideos, setCurrentVideos] = useState([null, null]); // [video1, video2] for dual streams
  const [currentVideoTurns, setCurrentVideoTurns] = useState([0, 0]); // Track which turn each video is from
  const [currentVideoCaptions, setCurrentVideoCaptions] = useState(["", ""]); // [caption1, caption2]
  const [videoHistory, setVideoHistory] = useState({}); // { turnNumber: [url1, url2] }
  const [videoCaptionHistory, setVideoCaptionHistory] = useState({}); // { turnNumber: [caption1, caption2] }
  const [videoLoading, setVideoLoading] = useState([false, false]); // [loading1, loading2]
  const [gifStatusMap, setGifStatusMap] = useState({}); // { "turn_index": 'idle'|'processing'|'ready'|'error' }
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoStartTime, setVideoStartTime] = useState(null);
  const [isTurnProcessing, setIsTurnProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(null); // { stage, progress, message }
  const [sessionToken, setSessionToken] = useState(null);
  const [waitingForPlayers, setWaitingForPlayers] = useState([]);
  const [connected, setConnected] = useState(socket.connected);
  const [activeNations, setActiveNations] = useState([]);
  const [nameError, setNameError] = useState("");
  const [isModerationPending, setIsModerationPending] = useState(false); // AI content moderation in progress
  const [tvStations] = useState(["GLOBAL HEADLINE 1", "GLOBAL HEADLINE 2"]); // Global TV station names for world events
  const [serverTimeOffset, setServerTimeOffset] = useState(0); // Offset between server and client time
  const [turnEndTime, setTurnEndTime] = useState(0); // Absolute server timestamp when turn ends
  const [turnLimit, setTurnLimit] = useState(12); // Default turn limit
  const [gameCompleted, setGameCompleted] = useState(false); // True when story has concluded
  const [storySummary, setStorySummary] = useState(null); // Final story summary data
  const [completedVideoHistory, setCompletedVideoHistory] = useState({}); // Videos for completed game
  const [finalInfluences, setFinalInfluences] = useState({}); // Final influence scores for completed game
  const [currentGameId, setCurrentGameId] = useState(null); // Unique game ID for TTS cache-busting
  const [interactionTarget, setInteractionTarget] = useState(null); // Player being targeted for interaction
  const [interactionDrafts, setInteractionDrafts] = useState({}); // Draft messages per player { playerName: "draft text" }
  const [isMuted, setIsMuted] = useState(true); // Audio mute state for videos (starts muted, auto-unmutes after TTS)
  const [hasUserToggledMute, setHasUserToggledMute] = useState(false); // Track if user has manually toggled mute
  const [finaleWaitingForTts, setFinaleWaitingForTts] = useState(false); // True while waiting for TTS to load before showing finale
  const [lobbyInfo, setLobbyInfo] = useState(null); // Lobby state: { gameStarted, fantasyLevel, fantasyLevelDescription, playerCount, players }
  const [inLobby, setInLobby] = useState(false); // True when player has joined but game hasn't started
  const [showPlayerDirectory, setShowPlayerDirectory] = useState(false); // Player directory overlay for 100-player scale
  const [showFullRankings, setShowFullRankings] = useState(false); // Full influence rankings overlay
  const [showNationsInLobby, setShowNationsInLobby] = useState(true); // Collapsible nations list in pre-lobby
  const [showLobbyPlayers, setShowLobbyPlayers] = useState(true); // Collapsible players list in lobby waiting screen
  const [showAllLobbyPlayers, setShowAllLobbyPlayers] = useState(false); // Show all players or condensed view (top 3 + bottom 3)
  const [isReady, setIsReady] = useState(false); // Player's ready state in lobby
  
  // Refs to access latest values in timeout callbacks
  const publicActionRef = useRef(publicAction);
  const privateActionRef = useRef(privateAction);
  const actionSubmittedRef = useRef(actionSubmitted);
  const interactionDraftsRef = useRef(interactionDrafts);
  const turnNumberRef = useRef(turnNumber);
  
  // Track last synced draft to avoid redundant network traffic at scale
  const lastSyncedDraftRef = useRef({ pub: "", priv: "", interactions: "{}" });
  
  // Refs for auto-focus on input fields
  const nameInputRef = useRef(null);
  const publicActionInputRef = useRef(null);
  const interactionInputRef = useRef(null);
  
  // Keep refs in sync with state
  useEffect(() => { publicActionRef.current = publicAction; }, [publicAction]);
  useEffect(() => { privateActionRef.current = privateAction; }, [privateAction]);
  useEffect(() => { actionSubmittedRef.current = actionSubmitted; }, [actionSubmitted]);
  useEffect(() => { interactionDraftsRef.current = interactionDrafts; }, [interactionDrafts]);
  useEffect(() => { turnNumberRef.current = turnNumber; }, [turnNumber]);
  
  // Auto-focus nation name input on join page
  useEffect(() => {
    if (!isNameSet && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isNameSet]);
  
  // Auto-focus public action input when turn is ready for input
  // Triggers when: new turn starts, or processing ends, or action is reset
  const prevProcessingRef = useRef(isTurnProcessing);
  useEffect(() => {
    const wasProcessing = prevProcessingRef.current;
    prevProcessingRef.current = isTurnProcessing;
    
    // Focus when conditions are right
    if (isNameSet && turnNumber > 0 && !actionSubmitted && !isTurnProcessing && !gameCompleted) {
      // Extra delay if we just finished processing (overlay needs to fade)
      const delay = wasProcessing ? 400 : 100;
      setTimeout(() => {
        if (publicActionInputRef.current && !document.activeElement?.closest('textarea, input')) {
          publicActionInputRef.current.focus();
        }
      }, delay);
    }
  }, [turnNumber, isNameSet, actionSubmitted, isTurnProcessing, gameCompleted]);
  
  // Auto-focus interaction textarea when modal opens
  useEffect(() => {
    if (interactionTarget && interactionInputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        if (interactionInputRef.current) {
          interactionInputRef.current.focus();
        }
      }, 50);
    }
  }, [interactionTarget]);
  
  // Keyboard handler for closing overlays with Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showPlayerDirectory) setShowPlayerDirectory(false);
        else if (showFullRankings) setShowFullRankings(false);
      }
    };
    if (showPlayerDirectory || showFullRankings) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [showPlayerDirectory, showFullRankings]);

  // Track socket connection status at the top level
  useEffect(() => {
    const attemptRejoin = () => {
      const savedToken = sessionStorage.getItem('turns_session_token');
      const savedName = sessionStorage.getItem('turns_player_name');
      if (savedToken && savedName) {
        console.log(`Attempting to rejoin as ${savedName}`);
        socket.emit("rejoinGame", { sessionToken: savedToken, name: savedName });
      }
    };
    
    const handleConnect = () => {
      setConnected(true);
      // CRITICAL: Attempt rejoin on every socket connect/reconnect
      attemptRejoin();
    };
    const handleDisconnect = () => setConnected(false);
    
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    
    // Set initial state and attempt rejoin if already connected
    setConnected(socket.connected);
    if (socket.connected) {
      attemptRejoin();
    }
    
    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, []);

  // GIF status listener - ALWAYS active (not conditional on isNameSet)
  // This ensures we receive GIF status updates at any point in the game
  useEffect(() => {
    const handleGifStatus = (data) => {
      console.log("[GIF] Status update received:", data);
      const gifKey = `${data.turnNumber}_${data.videoIndex}`;
      setGifStatusMap(prev => {
        const updated = { ...prev, [gifKey]: data.status };
        console.log("[GIF] Updated gifStatusMap:", updated);
        return updated;
      });
    };
    
    const handleAllGifStatus = (data) => {
      console.log("[GIF] All status received:", data);
      const simpleStatus = {};
      for (const [key, info] of Object.entries(data.gifStatus || {})) {
        simpleStatus[key] = info.status;
      }
      console.log("[GIF] Setting gifStatusMap to:", simpleStatus);
      setGifStatusMap(simpleStatus);
    };
    
    socket.on("gifStatus", handleGifStatus);
    socket.on("allGifStatus", handleAllGifStatus);
    
    return () => {
      socket.off("gifStatus", handleGifStatus);
      socket.off("allGifStatus", handleAllGifStatus);
    };
  }, []);

  // Listen for active nations (for join screen display and validation)
  useEffect(() => {
    socket.on("activeNations", (nations) => {
      console.log("Received activeNations:", nations);
      // Convert object { "USA": 10, "USSR": 5 } to array [{ name: "USA", influence_score: 10 }, ...]
      if (nations && typeof nations === 'object' && !Array.isArray(nations)) {
        const nationsArray = Object.entries(nations).map(([name, influence_score]) => ({
          name,
          influence_score
        }));
        setActiveNations(nationsArray);
      } else {
        setActiveNations(nations || []);
      }
    });
    
    socket.on("nameRejected", (data) => {
      console.log("Name rejected:", data);
      setNameError(data.message);
      setIsNameSet(false); // Reset to show join screen
      setIsModerationPending(false); // Clear moderation pending state
    });
    
    socket.on("nameModerationPending", (data) => {
      console.log("Name moderation pending:", data);
      setIsModerationPending(true);
    });
    
    // Lobby info (sent when player joins before game starts)
    socket.on("lobbyInfo", (info) => {
      console.log("Lobby info received:", info);
      setLobbyInfo(info);
      if (!info.gameStarted) {
        setInLobby(true);
      }
    });
    
    // Lobby updates (broadcast when lobby state changes)
    socket.on("lobbyUpdate", (info) => {
      console.log("Lobby update received:", info);
      setLobbyInfo(info);
      if (info.gameStarted) {
        setInLobby(false);
      }
    });
    
    // Game started (by admin or all-ready auto-launch)
    socket.on("gameStarted", (data) => {
      console.log("Game started:", data);
      setInLobby(false);
      setIsReady(false);
      setLobbyInfo(prev => prev ? { ...prev, gameStarted: true, ...data } : data);
    });
    
    // Request active nations and lobby info on mount
    socket.emit("getActiveNations");
    socket.emit("getLobbyInfo");
    
    return () => {
      socket.off("activeNations");
      socket.off("nameRejected");
      socket.off("nameModerationPending");
      socket.off("lobbyInfo");
      socket.off("lobbyUpdate");
      socket.off("gameStarted");
    };
  }, []);

  // Handle rejoin success/failure (rejoinGame is emitted in handleConnect above on every socket connect)
  useEffect(() => {
    // Handle successful rejoin
    socket.on("rejoinSuccess", (data) => {
      console.log("Rejoin successful:", data);
      setName(data.name);
      setSessionToken(data.sessionToken);
      setIsNameSet(true);
      if (data.snapshot) {
        setPlayerSnapshot(data.snapshot);
      }
      if (data.secretEvents && data.secretEvents.length > 0) {
        setLatestSecretEvent(data.secretEvents[data.secretEvents.length - 1]);
      }
      if (data.regionalBriefings && data.regionalBriefings.length > 0) {
        setLatestRegionalBriefing(data.regionalBriefings[data.regionalBriefings.length - 1]);
      }
      // Handle lobby state
      if (data.lobbyInfo) {
        setLobbyInfo(data.lobbyInfo);
      }
      if (!data.gameStarted) {
        setInLobby(true);
        setIsReady(false); // Server clears ready state on disconnect; reset client to match
        console.log("Rejoined to lobby - waiting for game to start");
      } else {
        setInLobby(false);
      }
      // Handle rejoining a completed game
      if (data.gameCompleted && data.storySummary) {
        // Don't show finale immediately - wait for TTS to load first
        setFinaleWaitingForTts(true);
        setGameCompleted(true);
        setInLobby(false);
        setIsMuted(true); // Start muted - auto-unmutes after TTS concludes
        setHasUserToggledMute(false); // Reset user toggle tracking
        setStorySummary(data.storySummary);
        if (data.videoHistory) {
          setCompletedVideoHistory(data.videoHistory);
          setVideoHistory(data.videoHistory);
        }
        if (data.turnHistory) {
          setTurnHistory(data.turnHistory);
        }
      }
    });
    
    // Handle failed rejoin - need to set name again
    socket.on("rejoinFailed", (data) => {
      console.log("Rejoin failed:", data.reason);
      // Clear old session data
      sessionStorage.removeItem('turns_session_token');
      sessionStorage.removeItem('turns_player_name');
    });
    
    // Handle new session creation (name approved)
    socket.on("sessionCreated", (data) => {
      console.log("Session created:", data);
      setSessionToken(data.sessionToken);
      setIsModerationPending(false); // Clear moderation pending state
      sessionStorage.setItem('turns_session_token', data.sessionToken);
      sessionStorage.setItem('turns_player_name', data.name);
    });
    
    return () => {
      socket.off("rejoinSuccess");
      socket.off("rejoinFailed");
      socket.off("sessionCreated");
    };
  }, []);

  useEffect(() => {
    if (isNameSet) {
      socket.on("playerCount", (count) => {
        console.log("Received playerCount:", count);
        setPlayerCount(count);
      });

      socket.on("playerList", (list) => {
        console.log("Received playerList:", list);
        setPlayerList(list);
      });

      socket.on("newTurn", (data) => {
        console.log("Received newTurn:", data);
        
        // Check if this is a genuinely new turn (full duration) vs a rejoin (partial duration)
        // If full duration or turn number advanced, clear the draft cache
        const isFullTurn = data.turnDuration && data.turnEndTime && 
          (data.turnEndTime - Date.now()) > (data.turnDuration * 0.9); // >90% time remaining = fresh turn
        
        // Handle sessionStorage cache for browser crash recovery
        let cacheRestored = false;
        try {
          const cached = sessionStorage.getItem('turns_draft_cache');
          if (cached) {
            const cachedData = JSON.parse(cached);
            // Clear if: different turn number OR this is a full fresh turn
            if (cachedData.turnNumber !== data.turnNumber || isFullTurn) {
              console.log(`[Cache] Clearing stale cache (cached turn ${cachedData.turnNumber}, new turn ${data.turnNumber}, isFullTurn: ${isFullTurn})`);
              sessionStorage.removeItem('turns_draft_cache');
            } else if (!isFullTurn && cachedData.turnNumber === data.turnNumber) {
              // Rejoining same turn - restore from cache (server may not have latest draft)
              // Only restore if cache is recent (within last 10 minutes)
              const cacheAge = Date.now() - (cachedData.timestamp || 0);
              if (cacheAge < 10 * 60 * 1000) {
                console.log(`[Cache] Restoring draft from local cache (age: ${Math.round(cacheAge / 1000)}s)`);
                if (cachedData.publicAction) setPublicAction(cachedData.publicAction);
                if (cachedData.privateAction) setPrivateAction(cachedData.privateAction);
                if (cachedData.interactions && Object.keys(cachedData.interactions).length > 0) {
                  setInteractionDrafts(cachedData.interactions);
                }
                cacheRestored = true;
                // Update sync tracker to match restored content
                lastSyncedDraftRef.current = {
                  pub: cachedData.publicAction || "",
                  priv: cachedData.privateAction || "",
                  interactions: JSON.stringify(cachedData.interactions || {})
                };
              } else {
                console.log(`[Cache] Cache too old (${Math.round(cacheAge / 1000)}s), not restoring`);
                sessionStorage.removeItem('turns_draft_cache');
              }
            }
          }
        } catch (e) {
          console.warn('[Cache] Error reading draft cache:', e);
        }
        
        setTurnNumber(data.turnNumber);
        
        // Update turn limit if provided
        if (data.turnLimit) {
          setTurnLimit(data.turnLimit);
        }
        
        // Calculate server time offset for sync (positive = server ahead, negative = server behind)
        const offset = data.serverTime ? (data.serverTime - Date.now()) : 0;
        if (data.serverTime) {
          setServerTimeOffset(offset);
          console.log(`Server time offset: ${offset}ms`);
        }
        
        // Store turnEndTime - client will derive remaining time from this
        if (data.turnEndTime) {
          setTurnEndTime(data.turnEndTime);
          console.log(`Turn ${data.turnNumber} started. Ends at server time ${data.turnEndTime}`);
        }
        
        setActionSubmitted(false);
        
        // Only clear drafts if this is a full new turn (not restored from cache)
        if (!cacheRestored) {
          setPublicAction("");
          setPrivateAction("");
          setInteractionDrafts({}); // Clear interaction drafts for new turn
          // Reset draft sync tracker for new turn
          lastSyncedDraftRef.current = { pub: "", priv: "", interactions: "{}" };
        }
        setInteractionTarget(null); // Close any open interaction modal
      });
      
      // Handle submission restoration on rejoin (sent after newTurn)
      socket.on("submissionReceived", (data) => {
        console.log("Submission restored on rejoin:", data);
        setActionSubmitted(true);
        if (data.publicAction !== undefined) {
          setPublicAction(data.publicAction);
        }
        if (data.privateAction !== undefined) {
          setPrivateAction(data.privateAction);
        }
      });
      
      // Handle draft restoration on rejoin (player had draft but hadn't submitted)
      socket.on("draftRestored", (data) => {
        console.log("Draft restored on rejoin:", data);
        if (data.publicAction !== undefined) {
          setPublicAction(data.publicAction);
        }
        if (data.privateAction !== undefined) {
          setPrivateAction(data.privateAction);
        }
        // Restore player interaction drafts if they exist
        if (data.playerInteractions && Array.isArray(data.playerInteractions)) {
          const restored = {};
          data.playerInteractions.forEach(({ target, action }) => {
            if (target && action) {
              restored[target] = action;
            }
          });
          if (Object.keys(restored).length > 0) {
            setInteractionDrafts(restored);
            console.log(`Restored ${Object.keys(restored).length} interaction drafts`);
          }
        }
      });
      
      socket.on("gameCompleted", (data) => {
        console.log("*** GAME COMPLETED ***", data);
        // Don't show finale immediately - wait for TTS to load first
        setFinaleWaitingForTts(true);
        setGameCompleted(true);
        setIsMuted(true); // Start muted - auto-unmutes after TTS concludes
        setHasUserToggledMute(false); // Reset user toggle tracking
        setCurrentGameId(data.gameId || null); // Store game ID for TTS cache-busting
        setStorySummary({
          summary: data.summary,
          revealedSecrets: data.revealedSecrets || [],
          videoCaption: data.videoCaption,
        });
        setCompletedVideoHistory(data.videoHistory || {});
        setFinalInfluences(data.finalInfluences || {});
        if (data.turnHistory) {
          setTurnHistory(data.turnHistory);
        }
      });
      
      socket.on("gameReset", () => {
        console.log("*** GAME RESET ***");
        setGameCompleted(false);
        setFinaleWaitingForTts(false);
        setHasUserToggledMute(false);
        setIsMuted(true); // Reset to default muted state
        setCurrentGameId(null); // Clear game ID
        setStorySummary(null);
        setCompletedVideoHistory({});
        setTurnHistory([]);
        setVideoHistory({});
        setTurnOutcome("");
        setGlobalOutcome("");
        setLatestRegionalBriefing("");
        setRegionalBriefingHistory({});
        setLatestSecretEvent("");
        setPlayerSnapshot({});
        setSecretHistory({});
        setCurrentVideos([null, null]);
        setCurrentVideoTurns([0, 0]);
        setCurrentVideoCaptions(["", ""]);
        setGifStatusMap({}); // Clear GIF status on reset
        setTurnNumber(0);
        // Also clear submission/draft state
        setActionSubmitted(false);
        setPublicAction("");
        setPrivateAction("");
        setInteractionDrafts({});
        setInteractionTarget(null);
        setIsReady(false);
      });
      
      // Handle periodic time sync from server - always sync the offset
      socket.on("turnTimeSync", (data) => {
        // Only sync if this is for the current turn
        setTurnNumber(currentTurn => {
          if (data.turnNumber !== currentTurn) return currentTurn;
          
          // Always update turnEndTime and server offset for accurate sync
          if (data.turnEndTime) {
            setTurnEndTime(data.turnEndTime);
          }
          if (data.serverTime) {
            const newOffset = data.serverTime - Date.now();
            setServerTimeOffset(newOffset);
          }
          
          return currentTurn; // Don't actually change turnNumber
        });
      });

      socket.on("turnOutcome", (data) => {
        console.log("*** RECEIVED turnOutcome EVENT ***:", data);
        console.log("*** Setting globalOutcome to:", data.globalOutcome);
        
        // Update current display with this turn's outcome
        setGlobalOutcome(data.globalOutcome || "");
        setTurnOutcome(data.globalOutcome || "");
        
        // Handle video captions - support both new array format and old single format
        const captions = data.videoCaptions || (data.videoCaption ? [data.videoCaption, data.videoCaption] : ["", ""]);
        setCurrentVideoCaptions(captions);
        setVideoCaptionHistory(prev => ({
          ...prev,
          [data.turnNumber]: captions
        }));
        
        // Reset current videos for new turn (will be populated by turnVideo events)
        setCurrentVideos([null, null]);
        setVideoLoading([true, true]); // Start loading both videos
        setVideoProgress(0);
        setVideoStartTime(Date.now());
        
        // Add to history for archives (filter out duplicates by turnNumber)
        setTurnHistory((prevHistory) => {
          // Check if this turn is already in history
          const exists = prevHistory.some(h => h.turnNumber === data.turnNumber);
          if (exists) return prevHistory;
          return [
            { 
              turnNumber: data.turnNumber, 
              globalOutcome: data.globalOutcome || "",
              videoCaptions: captions 
            },
            ...prevHistory,
          ];
        });
      });

      socket.on("turnHistory", (history) => {
        setTurnHistory(history.reverse());
      });

      socket.on("videoHistory", (videos) => {
        console.log("Received videoHistory:", videos);
        setVideoHistory(prev => ({ ...prev, ...videos }));
      });

      socket.on("actionReceived", (data) => {
        console.log(`Action received for turn ${data.turnNumber}`);
      });

      socket.on("playerSnapshot", (data) => {
        console.log("Received playerSnapshot:", data);
        setPlayerSnapshot((prevSnapshot) => ({
          ...prevSnapshot,
          ...data.snapshot,
        }));
      });

      socket.on("playerSecretEvent", (data) => {
        console.log("*** RECEIVED playerSecretEvent EVENT ***:", data);
        console.log("*** Setting latestSecretEvent to:", data.secretEvent);
        // Store latest secret event for display
        setLatestSecretEvent(data.secretEvent);
        // Also store in history immediately (needed for archives display)
        if (data.turnNumber) {
          setSecretHistory(prev => ({
            ...prev,
            [data.turnNumber]: data.secretEvent
          }));
        }
      });

      socket.on("playerRegionalBriefing", (data) => {
        console.log("*** RECEIVED playerRegionalBriefing EVENT ***:", data);
        console.log("*** Setting latestRegionalBriefing to:", data.regionalBriefing);
        // Store latest regional briefing for display (personalized for this player)
        setLatestRegionalBriefing(data.regionalBriefing);
        // Also store in history immediately (needed for archives display)
        if (data.turnNumber) {
          setRegionalBriefingHistory(prev => ({
            ...prev,
            [data.turnNumber]: data.regionalBriefing
          }));
        }
      });

      socket.on("playerInfluences", (influences) => {
        console.log("Received playerInfluences:", influences);
        setPlayerInfluences(influences);
      });

      socket.on("turnVideo", (data) => {
        console.log("Received turnVideo:", data);
        const videoIndex = data.videoIndex !== undefined ? data.videoIndex : 0;
        
        // Update current videos array
        setCurrentVideos(prev => {
          const newVideos = [...prev];
          newVideos[videoIndex] = data.videoUrl;
          return newVideos;
        });
        
        // Track which turn this video is from (for GIF buttons)
        setCurrentVideoTurns(prev => {
          const newTurns = [...prev];
          newTurns[videoIndex] = data.turnNumber;
          return newTurns;
        });
        
        // Update loading state for this video slot
        setVideoLoading(prev => {
          const newLoading = [...prev];
          newLoading[videoIndex] = false;
          return newLoading;
        });
        
        // Update video history - store as array per turn
        setVideoHistory(prev => {
          const turnVideos = prev[data.turnNumber] || [null, null];
          const newTurnVideos = [...turnVideos];
          newTurnVideos[videoIndex] = data.videoUrl;
          return {
            ...prev,
            [data.turnNumber]: newTurnVideos
          };
        });
      });

      socket.on("turnVideoError", (data) => {
        console.error("Video generation error:", data);
        const videoIndex = data.videoIndex !== undefined ? data.videoIndex : 0;
        setVideoLoading(prev => {
          const newLoading = [...prev];
          newLoading[videoIndex] = false;
          return newLoading;
        });
      });

      // Note: gifStatus listeners are in a separate always-active useEffect above

      socket.on("turnProcessing", (data) => {
        console.log("Turn processing:", data);
        
        // Final draft flush - stagger with random delay to avoid thundering herd
        // when 100 clients all receive turnProcessing simultaneously
        if (data.processing && !actionSubmittedRef.current) {
          const delay = Math.random() * 100; // 0-100ms random spread
          setTimeout(() => {
            const pub = (publicActionRef.current || "").trim();
            const priv = (privateActionRef.current || "").trim();
            const interactions = interactionDraftsRef.current || {};
            const hasContent = pub || priv || Object.keys(interactions).length > 0;
            
            if (hasContent) {
              socket.emit("updateDraft", { 
                publicAction: pub, 
                privateAction: priv,
                playerInteractions: interactions
              });
            }
          }, delay);
        }
        
        setIsTurnProcessing(data.processing);
        // Reset progress when processing ends
        if (!data.processing) {
          setProcessingProgress(null);
        }
      });

      socket.on("turnProcessingProgress", (data) => {
        console.log("Turn processing progress:", data);
        setProcessingProgress(data);
      });

      socket.on("turnSubmissionStatus", (data) => {
        console.log("Turn submission status:", data);
        setWaitingForPlayers(data.waitingForPlayers || []);
      });
      
      socket.on("debug", (data) => {
        console.log("Debug event received from server:", data);
      });

      return () => {
        socket.off("playerCount");
        socket.off("playerList");
        socket.off("newTurn");
        socket.off("submissionReceived");
        socket.off("draftRestored");
        socket.off("turnTimeSync");
        socket.off("turnOutcome");
        socket.off("turnHistory");
        socket.off("videoHistory");
        socket.off("actionReceived");
        socket.off("playerSnapshot");
        socket.off("playerSecretEvent");
        socket.off("playerRegionalBriefing");
        socket.off("playerInfluences");
        socket.off("turnVideo");
        socket.off("turnVideoError");
        // Note: gifStatus listeners are cleaned up in their own useEffect
        socket.off("turnProcessing");
        socket.off("turnProcessingProgress");
        socket.off("turnSubmissionStatus");
        socket.off("debug");
        socket.off("gameCompleted");
        socket.off("gameReset");
      };
    }
  }, [isNameSet]);

  // Timer tick - triggers re-render every second for time display
  // The actual time remaining is calculated from server's turnEndTime
  useEffect(() => {
    const timer = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate time remaining from server's turnEndTime (single source of truth)
  // All clients derive this from the same server timestamp, ensuring perfect sync
  const timeLeft = turnEndTime > 0 
    ? Math.max(0, Math.ceil((turnEndTime - (Date.now() + serverTimeOffset)) / 1000))
    : 0;
  
  // Continuous draft sync - send current text to server periodically
  // Server will use this if player doesn't explicitly submit
  // Only syncs when content actually changed (see lastSyncedDraftRef)
  useEffect(() => {
    if (!isNameSet || actionSubmitted) return;
    
    const syncDraft = () => {
      const pub = (publicActionRef.current || "").trim();
      const priv = (privateActionRef.current || "").trim();
      const interactions = interactionDraftsRef.current || {};
      const interactionsJson = JSON.stringify(interactions);
      const currentTurn = turnNumberRef.current;
      
      // Only sync if content actually changed since last sync
      const lastSync = lastSyncedDraftRef.current;
      const hasChanged = pub !== lastSync.pub || priv !== lastSync.priv || interactionsJson !== lastSync.interactions;
      
      if (!hasChanged) return; // Skip - nothing changed
      
      // Only sync if there's content
      const hasContent = pub || priv || Object.keys(interactions).length > 0;
      if (hasContent) {
        socket.emit("updateDraft", { 
          publicAction: pub, 
          privateAction: priv,
          playerInteractions: interactions
        });
        // Update last synced state
        lastSyncedDraftRef.current = { pub, priv, interactions: interactionsJson };
        
        // Also save to sessionStorage as backup (survives browser crashes)
        try {
          sessionStorage.setItem('turns_draft_cache', JSON.stringify({
            turnNumber: currentTurn,
            publicAction: pub,
            privateAction: priv,
            interactions,
            timestamp: Date.now()
          }));
        } catch (e) {
          // Ignore storage errors (quota exceeded, private mode, etc.)
        }
      }
    };
    
    // Sync every 5 seconds (only if changed) - increased from 3s for scale
    const interval = setInterval(syncDraft, 5000);
    
    // Also sync immediately on mount (in case player already has content)
    syncDraft();
    
    // Sync immediately when user switches away from tab (prevents data loss)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        syncDraft();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Sync on page unload (best effort - may not always fire)
    const handleBeforeUnload = () => {
      syncDraft();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isNameSet, actionSubmitted]); // Don't include publicAction/privateAction - we read from refs, and including them resets the interval on every keystroke!

  // Video progress estimation (~90 seconds typical generation time)
  useEffect(() => {
    let interval;
    if (videoLoading && videoStartTime) {
      interval = setInterval(() => {
        const elapsed = (Date.now() - videoStartTime) / 1000;
        const estimatedTotal = 90; // ~31 attempts × 3 seconds
        const progress = Math.min(95, (elapsed / estimatedTotal) * 100);
        setVideoProgress(progress);
      }, 500);
    }
    return () => clearInterval(interval);
  }, [videoLoading, videoStartTime]);

  // Loading animation effect
  useEffect(() => {
    let interval;
    if (actionSubmitted) {
      // Animate dots while waiting for turn to complete
      interval = setInterval(() => {
        setLoadingDots((prev) => {
          if (prev.length >= 6) return ".";
          return prev + ".";
        });
      }, 300);
    } else {
      setLoadingDots("");
    }
    return () => clearInterval(interval);
  }, [actionSubmitted]);

  // NOTE: Removed global Enter key handler - it was causing accidental submissions
  // Players submit via: (1) clicking Submit button, or (2) pressing Enter in input fields

  // Apply randomized flicker CSS variables to ALL text elements
  useEffect(() => {
    const applyRandomFlicker = () => {
      const elements = document.querySelectorAll('.crt-screen *');
      elements.forEach(el => {
        // Random speed between 3-12 seconds
        const speed = 3 + Math.random() * 9;
        // Random delay so they don't all flicker together
        const delay = Math.random() * speed;
        // Random opacity for flicker intensity (0.5 to 0.85 - reduced by 30%)
        const opacity = 0.5 + Math.random() * 0.35;
        // Random strength for RGB shift (0.3 to 1.5)
        const strength = 0.3 + Math.random() * 1.2;
        
        el.style.setProperty('--flicker-speed', `${speed}s`);
        el.style.setProperty('--flicker-delay', `${delay}s`);
        el.style.setProperty('--flicker-opacity', opacity);
        el.style.setProperty('--flicker-strength', strength);
      });
    };

    // Apply on mount
    applyRandomFlicker();
    
    // Re-randomize every 5-15 seconds
    const scheduleNext = () => {
      const nextInterval = 5000 + Math.random() * 10000;
      return setTimeout(() => {
        applyRandomFlicker();
        intervalRef.current = scheduleNext();
      }, nextInterval);
    };
    
    const intervalRef = { current: scheduleNext() };
    
    return () => clearTimeout(intervalRef.current);
  }, []);

  const sendCommand = () => {
    if (!actionSubmitted) {
      // Submit whatever is currently in the fields (can be empty)
      socket.emit("command", { 
        publicAction: publicAction.trim(), 
        privateAction: privateAction.trim() 
      });
      setActionSubmitted(true);
      // Clear local draft cache since we've explicitly submitted
      try {
        sessionStorage.removeItem('turns_draft_cache');
      } catch (e) {}
      // Don't clear inputs immediately - let them see what was submitted
    }
  };

  const savePlayerInteraction = () => {
    // Just close the modal - the draft is already saved in interactionDrafts
    // and will be synced to server via updateDraft
    // All interactions get processed together at turn end
    setInteractionTarget(null);
    setShowPlayerDirectory(true); // Go back to player list
  };
  
  const clearPlayerInteraction = () => {
    // Clear this player's draft and close modal
    if (interactionTarget) {
      setInteractionDrafts(prev => {
        const updated = { ...prev };
        delete updated[interactionTarget];
        return updated;
      });
    }
    setInteractionTarget(null);
    setShowPlayerDirectory(true); // Go back to player list
  };
  
  // Handle individual video GIF download (conversion now auto-starts when video arrives)
  // videoIndex can be 0, 1, or 'finale' (string)
  const handleVideoGifDownload = async (turnNum, videoIndex) => {
    const gifKey = `${turnNum}_${videoIndex}`;
    const currentStatus = gifStatusMap[gifKey] || 'idle';
    
    // If processing, do nothing
    if (currentStatus === 'processing') {
      return;
    }
    
    // If not ready yet (idle or error), nothing to download
    if (currentStatus !== 'ready') {
      console.log(`[GIF] GIF not ready yet for turn ${turnNum}, video ${videoIndex} (status: ${currentStatus})`);
      return;
    }
    
    // Download the ready GIF
    try {
      console.log(`[GIF] Downloading GIF for turn ${turnNum}, video ${videoIndex}...`);
      const response = await fetch(`/api/download-gif/${turnNum}/${videoIndex}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `turns_t${turnNum}_v${videoIndex}_${Date.now()}.gif`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log(`[GIF] Downloaded successfully`);
    } catch (error) {
      console.error(`[GIF] Download error:`, error);
    }
  };

  const fetchMoreData = () => {
    setHasMore(false);
  };

  // Style objects for the retro hacker vibe
  const appStyles = {
    backgroundColor: "#001100",
    color: "#0f0",
    minHeight: "100vh",
    fontFamily: "'PrintChar21', 'Courier New', monospace",
    padding: "20px",
    position: "relative",
  };

  const headingStyles = {
    color: "#39FF14",
    textShadow: "0 0 10px #39FF14, 0 0 20px #39FF14",
    letterSpacing: "0.1em",
  };

  const inputStyles = {
    backgroundColor: "#002200",
    color: "#39FF14",
    border: "none",
    padding: "12px",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "'PrintChar21', 'Courier New', monospace",
    fontSize: "1em",
    fontWeight: "bold",
    outline: "none",
  };

  const buttonStyles = {
    backgroundColor: "#002200",
    color: "#39FF14",
    border: "none",
    padding: "12px 20px",
    fontFamily: "'PrintChar21', 'Courier New', monospace",
    cursor: "pointer",
    fontSize: "1em",
    transition: "all 0.2s",
  };

  const submitButtonStyles = {
    ...buttonStyles,
    backgroundColor: "#003300",
    textShadow: "0 0 5px #39FF14",
    boxShadow: "0 0 10px #39FF14", // Pre-glow for submit button
  };

  const boxStyles = {
    border: "none",
    padding: "15px",
    marginBottom: "20px",
    backgroundColor: "rgba(0, 17, 0, 0.3)",
  };

  // Show Story Completed screen if game is finished
  if (gameCompleted && storySummary && isNameSet) {
    return (
      <StoryCompletedScreen
        summary={storySummary.summary}
        revealedSecrets={storySummary.revealedSecrets}
        videoHistory={completedVideoHistory}
        turnHistory={turnHistory}
        finalInfluences={finalInfluences}
        finalTurn={turnNumber}
        isMuted={isMuted}
        setIsMuted={setIsMuted}
        hasUserToggledMute={hasUserToggledMute}
        setHasUserToggledMute={setHasUserToggledMute}
        finaleWaitingForTts={finaleWaitingForTts}
        setFinaleWaitingForTts={setFinaleWaitingForTts}
        gameId={currentGameId}
      />
    );
  }

  return (
    <div className="crt-screen" style={appStyles}>
      {/* CRT Scanlines over entire app */}
      <div className="scanlines" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 9999 }} />
      
      {/* Terminal prompt indicator - anchored in top right (scrolls with page) */}
      <div style={{
        position: "absolute",
        top: "10px",
        right: "20px",
        fontSize: "0.9em",
        letterSpacing: "0.1em",
        fontWeight: "bold",
        zIndex: 100,
        pointerEvents: "none",
      }}>
        {connected ? (
          <span style={{ color: "#39FF14", textShadow: "0 0 10px #39FF14, 0 0 20px #00ff00" }}>SYS_ONLINE</span>
        ) : (
          <span style={{ color: "#2a4a2a", textShadow: "0 0 5px #1a3a1a" }}>SYS_OFFLINE</span>
        )}
      </div>
      
      {!isNameSet ? (
        <div>
          <div style={{
            ...headingStyles,
            fontSize: "2.5em",
            marginBottom: "30px",
            textAlign: "center",
            padding: "30px",
            fontWeight: "bold",
            backgroundColor: "rgba(0, 17, 0, 0.3)",
          }}>
            ====================================<br/>
            TURNS: Mutually Assured<br/>
            ====================================
          </div>
          <div style={boxStyles}>
            <h3 style={{
              ...headingStyles, 
              fontSize: '1.3em',
              fontWeight: 'bold',
              letterSpacing: '0.1em',
              marginBottom: '15px',
              borderBottom: '1px solid #39FF14',
              paddingBottom: '10px'
            }}>
              [ GAME PREMISE ]
            </h3>
            <p style={{ lineHeight: "1.8em", marginBottom: "15px", fontWeight: "bold"  }}>
              &gt; TURNS: Mutually Assured is a multiplayer simulation set in an alternate 1980s Cold War.
            </p>
            <p style={{ lineHeight: "1.8em", marginBottom: "15px", fontWeight: "bold"  }}>
              &gt; You control a nation. Each turn, submit orders to conduct diplomacy, espionage, and military operations. An AI processes all players' actions simultaneously and advances the world state.
            </p>
            <p style={{ lineHeight: "1.8em", fontWeight: "bold" }}>
              &gt; Navigate global politics. Your decisions shape the outcome.
            </p>
          </div>

          {/* Game Settings Display (Fantasy Level) */}
          {lobbyInfo && (
            <div style={{
              ...boxStyles,
              marginTop: "20px",
              backgroundColor: "rgba(57, 255, 20, 0.1)",
              border: "2px solid rgba(57, 255, 20, 0.5)",
            }}>
              <h3 style={{
                ...headingStyles, 
                fontSize: '1.3em',
                fontWeight: 'bold',
                letterSpacing: '0.1em',
                marginBottom: '15px',
                borderBottom: '1px solid #39FF14',
                paddingBottom: '10px',
                color: '#39FF14',
              }}>
                [ GAME SETTINGS ]
              </h3>
              <div style={{
                padding: "15px",
                backgroundColor: "rgba(57, 255, 20, 0.1)",
                border: "1px solid rgba(57, 255, 20, 0.3)",
                marginBottom: "15px",
              }}>
                <div style={{
                  fontSize: "1.2em",
                  fontWeight: "bold",
                  marginBottom: "10px",
                  color: "#39FF14",
                  textShadow: "0 0 10px rgba(57, 255, 20, 0.5)",
                }}>
                  FANTASY LEVEL: {lobbyInfo.fantasyLevel}/3
                </div>
                <p style={{
                  lineHeight: "1.6em",
                  color: "#39FF14",
                  fontWeight: "bold",
                }}>
                  {lobbyInfo.fantasyLevelDescription || FANTASY_LEVEL_DESCRIPTIONS[lobbyInfo.fantasyLevel]}
                </p>
              </div>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
              }}>
                <span style={{ 
                  color: "#39FF14",
                  fontWeight: "bold",
                  textShadow: "0 0 10px #39FF14",
                }}>
                  {lobbyInfo.gameStarted ? "● GAME IN PROGRESS" : "○ WAITING FOR HOST TO START"}
                </span>
              </div>
            </div>
          )}

          {/* Name Entry Section */}
          <div style={{
            ...boxStyles,
            marginTop: "20px",
            backgroundColor: "rgba(0, 17, 0, 0.5)",
            border: "1px solid rgba(57, 255, 20, 0.3)",
          }}>
            <h3 style={{
              ...headingStyles, 
              fontSize: '1.3em',
              fontWeight: 'bold',
              letterSpacing: '0.1em',
              marginBottom: '15px',
              borderBottom: '1px solid #39FF14',
              paddingBottom: '10px'
            }}>
              [ ENTER YOUR NATION NAME ]
            </h3>
            
            {/* Moderation Pending Indicator */}
            {isModerationPending && (
              <div style={{
                backgroundColor: "rgba(255, 200, 0, 0.15)",
                border: "1px solid #FFC800",
                padding: "15px",
                marginBottom: "15px",
                color: "#FFC800",
                fontWeight: "bold",
                textAlign: "center",
                textShadow: "0 0 10px #FFC800",
                boxShadow: "0 0 15px rgba(255, 200, 0, 0.3)",
                animation: "pulse 1.5s ease-in-out infinite",
              }}>
                <span style={{ display: "inline-block", animation: "spin 1s linear infinite", marginRight: "10px" }}>◐</span>
                SCANNING NATION NAME FOR COMPLIANCE...
                <style>{`
                  @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.6; }
                  }
                  @keyframes spin {
                    0% { content: "◐"; }
                    25% { transform: rotate(90deg); }
                    50% { transform: rotate(180deg); }
                    75% { transform: rotate(270deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            )}
            
            {/* Error Message */}
            {nameError && !isModerationPending && (
              <div style={{
                backgroundColor: nameError.includes("rejected") ? "rgba(255, 50, 50, 0.15)" : "rgba(57, 255, 20, 0.15)",
                border: nameError.includes("rejected") ? "1px solid #FF3232" : "1px solid #39FF14",
                padding: "15px",
                marginBottom: "15px",
                color: nameError.includes("rejected") ? "#FF3232" : "#39FF14",
                fontWeight: "bold",
                textAlign: "center",
                textShadow: nameError.includes("rejected") ? "0 0 10px #FF3232" : "0 0 10px #39FF14",
                boxShadow: nameError.includes("rejected") ? "0 0 15px rgba(255, 50, 50, 0.3)" : "0 0 15px rgba(57, 255, 20, 0.3)",
              }}>
                {(() => {
                  // Extract type from hidden delimiter and display clean message
                  const parts = nameError.split('\x00');
                  const displayMsg = parts[0];
                  const rejectionType = parts[1] || '';
                  const prefix = rejectionType === 'fantasy_level' 
                    ? "NAME REJECTED: " 
                    : rejectionType === 'duplicate'
                      ? "DUPLICATE: "
                      : rejectionType === 'content'
                        ? "CONTENT VIOLATION: "
                        : "ERROR: ";
                  return prefix + displayMsg;
                })()}
              </div>
            )}
            
            <input
              ref={nameInputRef}
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError(""); // Clear error when typing
              }}
              placeholder="e.g., USA, USSR, FRANCE"
              maxLength={50}
              disabled={isModerationPending}
              style={{
                ...inputStyles,
                maxWidth: "500px",
                fontSize: "1.2em",
                textTransform: "uppercase",
                opacity: isModerationPending ? 0.5 : 1,
                cursor: isModerationPending ? "not-allowed" : "text",
              }}
            onKeyDown={async (e) => {
              if (e.key === "Enter" && name.trim() && !isModerationPending) {
                // Check if name is already taken (case-insensitive literal match)
                const isTaken = activeNations.some(
                  n => n.name.toUpperCase() === name.trim().toUpperCase()
                );
                if (isTaken) {
                  setNameError(`${name.trim().toUpperCase()} is already an active nation. Choose another.`);
                  return;
                }
                setNameError("");
                
                // Client-side moderation (enables parallel moderation for 100+ players)
                // Also checks for semantic duplicates (America = USA, USSR = Russia, etc.)
                // Also validates thematic appropriateness based on fantasy level
                setIsModerationPending(true);
                const existingNationNames = activeNations.map(n => n.name);
                const currentFantasyLevel = lobbyInfo?.fantasyLevel || 3;
                const moderationResult = await moderateNationName(name.trim(), existingNationNames, currentFantasyLevel);
                
                if (!moderationResult.approved) {
                  // Format error message based on rejection type from AI
                  const reason = moderationResult.reason || "Name not allowed";
                  const rejectionType = moderationResult.type || 'content';
                  const levelNote = rejectionType === 'fantasy_level' ? ` (at Fantasy Level ${currentFantasyLevel})` : '';
                  // Type encoded at end with \x00 delimiter (invisible, for detection only)
                  setNameError(`"${name.trim().toUpperCase()}" - ${reason}${levelNote}\x00${rejectionType}`);
                  setIsModerationPending(false);
                  return;
                }
                
                // Moderation passed - emit to server
                socket.emit("setName", name.trim());
                setIsNameSet(true);
                // Note: isModerationPending cleared by sessionCreated or nameRejected socket events
              }
            }}
            />
          <button
            onClick={async () => {
              if (name.trim() && !isModerationPending) {
                // Check if name is already taken (case-insensitive literal match)
                const isTaken = activeNations.some(
                  n => n.name.toUpperCase() === name.trim().toUpperCase()
                );
                if (isTaken) {
                  setNameError(`${name.trim().toUpperCase()} is already an active nation. Choose another.`);
                  return;
                }
                setNameError("");
                
                // Client-side moderation (enables parallel moderation for 100+ players)
                // Also checks for semantic duplicates (America = USA, USSR = Russia, etc.)
                // Also validates thematic appropriateness based on fantasy level
                setIsModerationPending(true);
                const existingNationNames = activeNations.map(n => n.name);
                const currentFantasyLevel = lobbyInfo?.fantasyLevel || 3;
                const moderationResult = await moderateNationName(name.trim(), existingNationNames, currentFantasyLevel);
                
                if (!moderationResult.approved) {
                  // Format error message based on rejection type from AI
                  const reason = moderationResult.reason || "Name not allowed";
                  const rejectionType = moderationResult.type || 'content';
                  const levelNote = rejectionType === 'fantasy_level' ? ` (at Fantasy Level ${currentFantasyLevel})` : '';
                  // Type encoded at end with \x00 delimiter (invisible, for detection only)
                  setNameError(`"${name.trim().toUpperCase()}" - ${reason}${levelNote}\x00${rejectionType}`);
                  setIsModerationPending(false);
                  return;
                }
                
                // Moderation passed - emit to server
                socket.emit("setName", name.trim());
                setIsNameSet(true);
                // Note: isModerationPending cleared by sessionCreated or nameRejected socket events
              }
            }}
            disabled={isModerationPending}
              style={{ 
                ...buttonStyles, 
                marginLeft: "20px",
                marginTop: "20px",
                padding: "15px 40px",
                fontSize: "1.2em",
                letterSpacing: "0.15em",
                fontWeight: "bold",
                opacity: isModerationPending ? 0.5 : 1,
                cursor: isModerationPending ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!isModerationPending) {
                  e.target.style.backgroundColor = "#002200";
                  e.target.style.boxShadow = "0 0 20px #39FF14";
                  e.target.style.textShadow = "0 0 10px #39FF14";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#001100";
                e.target.style.boxShadow = "none";
                e.target.style.textShadow = "none";
              }}
            >
              {isModerationPending ? "[ SCANNING... ]" : "[ CONNECT TO NETWORK ]"}
            </button>
          </div>

          {/* Active Nations Display - Collapsible */}
          <div style={{
            ...boxStyles,
            marginTop: "20px",
            backgroundColor: "rgba(0, 17, 0, 0.5)",
            border: "1px solid rgba(57, 255, 20, 0.3)",
          }}>
            <div 
              onClick={() => setShowNationsInLobby(!showNationsInLobby)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                paddingBottom: showNationsInLobby ? "10px" : "0",
                borderBottom: showNationsInLobby ? "1px solid #39FF14" : "none",
                marginBottom: showNationsInLobby ? "15px" : "0",
              }}
            >
              <h3 style={{
                ...headingStyles, 
                fontSize: '1.3em',
                fontWeight: 'bold',
                letterSpacing: '0.1em',
                margin: 0,
              }}>
                [ ACTIVE NATIONS IN LOBBY: {activeNations.length} ]
              </h3>
              <span style={{
                color: "#39FF14",
                fontSize: "1.2em",
                fontWeight: "bold",
                textShadow: "0 0 5px #39FF14",
                transition: "transform 0.2s ease",
                transform: showNationsInLobby ? "rotate(0deg)" : "rotate(-90deg)",
              }}>
                ▼
              </span>
            </div>
            {showNationsInLobby && (
              <>
                {activeNations.length === 0 ? (
                  <p style={{ 
                    lineHeight: "1.8em", 
                    fontWeight: "bold",
                    color: "#2a6a2a",
                    fontStyle: "italic"
                  }}>
                    &gt; NO ACTIVE NATIONS - BE THE FIRST TO JOIN
                  </p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {activeNations.map((nation, index) => (
                      <div key={index} style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        backgroundColor: "rgba(57, 255, 20, 0.1)",
                        border: "1px solid rgba(57, 255, 20, 0.2)",
                      }}>
                        <span style={{ 
                          fontWeight: "bold", 
                          letterSpacing: "0.1em",
                          textTransform: "uppercase"
                        }}>
                          &gt; {nation.name}
                        </span>
                        <span style={{ 
                          color: "#39FF14",
                          fontWeight: "bold",
                          textShadow: "0 0 5px #39FF14"
                        }}>
                          INFLUENCE: {nation.influence_score || 0}
                        </span>
                      </div>
                    ))}
                    <p style={{ 
                      marginTop: "10px", 
                      fontSize: "0.9em", 
                      color: "#39FF14",
                      fontWeight: "bold",
                      textShadow: "0 0 5px #39FF14"
                    }}>
                      ⚠ THESE NATIONS ARE UNAVAILABLE - CHOOSE A DIFFERENT NAME
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : inLobby ? (
        /* LOBBY WAITING SCREEN - Player joined but game hasn't started */
        <div>
          <div style={{
            ...headingStyles,
            fontSize: "2.5em",
            marginBottom: "30px",
            textAlign: "center",
            padding: "30px",
            fontWeight: "bold",
            backgroundColor: "rgba(0, 17, 0, 0.3)",
          }}>
            ====================================<br/>
            LOBBY: AWAITING GAME START<br/>
            ====================================
          </div>
          
          {/* Player's Nation Confirmation */}
          <div style={{
            ...boxStyles,
            backgroundColor: "rgba(57, 255, 20, 0.1)",
            border: "2px solid rgba(57, 255, 20, 0.5)",
            marginBottom: "20px",
            textAlign: "center",
          }}>
            <h3 style={{
              ...headingStyles, 
              fontSize: '1.5em',
              fontWeight: 'bold',
              letterSpacing: '0.1em',
              marginBottom: '15px',
            }}>
              YOUR NATION
            </h3>
            <div style={{
              fontSize: "2em",
              fontWeight: "bold",
              color: "#39FF14",
              textShadow: "0 0 20px #39FF14",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
            }}>
              {name}
            </div>
          </div>
          
          {/* Game Rules Section */}
          <div style={boxStyles}>
            <h3 style={{
              ...headingStyles, 
              fontSize: '1.3em',
              fontWeight: 'bold',
              letterSpacing: '0.1em',
              marginBottom: '15px',
              borderBottom: '1px solid #39FF14',
              paddingBottom: '10px'
            }}>
              [ GAME PREMISE ]
            </h3>
            <p style={{ lineHeight: "1.8em", marginBottom: "15px", fontWeight: "bold" }}>
              &gt; TURNS: Mutually Assured is a multiplayer simulation set in an alternate 1980s Cold War.
            </p>
            <p style={{ lineHeight: "1.8em", marginBottom: "15px", fontWeight: "bold" }}>
              &gt; You control a nation. Each turn, submit orders to conduct diplomacy, espionage, and military operations. An AI processes all players' actions simultaneously and advances the world state.
            </p>
            <p style={{ lineHeight: "1.8em", fontWeight: "bold" }}>
              &gt; Navigate global politics. Your decisions shape the outcome.
            </p>
          </div>

          {/* Fantasy Level Display */}
          {lobbyInfo && (
            <div style={{
              ...boxStyles,
              marginTop: "20px",
              backgroundColor: "rgba(57, 255, 20, 0.1)",
              border: "2px solid rgba(57, 255, 20, 0.5)",
              marginBottom: "20px",
            }}>
              <h3 style={{
                ...headingStyles, 
                fontSize: '1.3em',
                fontWeight: 'bold',
                letterSpacing: '0.1em',
                marginBottom: '15px',
                borderBottom: '1px solid #39FF14',
                paddingBottom: '10px',
                color: '#39FF14',
              }}>
                [ GAME CONFIGURATION ]
              </h3>
              <div style={{
                padding: "15px",
                backgroundColor: "rgba(57, 255, 20, 0.1)",
                border: "1px solid rgba(57, 255, 20, 0.3)",
              }}>
                <div style={{
                  fontSize: "1.3em",
                  fontWeight: "bold",
                  marginBottom: "12px",
                  color: "#39FF14",
                  textShadow: "0 0 10px rgba(57, 255, 20, 0.5)",
                }}>
                  FANTASY LEVEL: {lobbyInfo.fantasyLevel}/3
                </div>
                <p style={{
                  lineHeight: "1.6em",
                  color: "#39FF14",
                  fontWeight: "bold",
                  fontSize: "1.1em",
                }}>
                  {lobbyInfo.fantasyLevelDescription || FANTASY_LEVEL_DESCRIPTIONS[lobbyInfo.fantasyLevel]}
                </p>
              </div>
            </div>
          )}
          
          {/* Waiting Message */}
          <div style={{
            ...boxStyles,
            backgroundColor: "rgba(255, 255, 0, 0.1)",
            border: "2px solid rgba(255, 255, 0, 0.3)",
            textAlign: "center",
            padding: "30px",
            marginBottom: "20px",
          }}>
            <div style={{
              fontSize: "1.2em",
              fontWeight: "bold",
              color: "#ffff00",
              textShadow: "0 0 15px rgba(255, 255, 0, 0.5)",
              letterSpacing: "0.1em",
              animation: "pulse 2s ease-in-out infinite",
            }}>
              <span style={{ display: "inline-block", animation: "spin 2s linear infinite", marginRight: "15px" }}>◐</span>
              MARK READY TO LAUNCH
              <span style={{ display: "inline-block", animation: "spin 2s linear infinite reverse", marginLeft: "15px" }}>◑</span>
            </div>
            <p style={{
              marginTop: "15px",
              color: "#cccc00",
              opacity: 0.8,
            }}>
              When all players mark READY, the game launches automatically. The host can also start manually.
            </p>
            <style>{`
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.6; }
              }
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>

          {/* Players in Lobby - Collapsible with Condensed View */}
          <div style={{
            ...boxStyles,
            backgroundColor: "rgba(0, 17, 0, 0.5)",
            border: "1px solid rgba(57, 255, 20, 0.3)",
            marginBottom: "20px",
          }}>
            <div 
              onClick={() => setShowLobbyPlayers(!showLobbyPlayers)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                paddingBottom: showLobbyPlayers ? "10px" : "0",
                borderBottom: showLobbyPlayers ? "1px solid #39FF14" : "none",
                marginBottom: showLobbyPlayers ? "15px" : "0",
              }}
            >
              <h3 style={{
                ...headingStyles, 
                fontSize: '1.3em',
                fontWeight: 'bold',
                letterSpacing: '0.1em',
                margin: 0,
              }}>
                [ NATIONS IN LOBBY: {lobbyInfo?.players?.length || playerList.length} ]
              </h3>
              <span style={{
                color: "#39FF14",
                fontSize: "1.2em",
                fontWeight: "bold",
                textShadow: "0 0 5px #39FF14",
                transition: "transform 0.2s ease",
                transform: showLobbyPlayers ? "rotate(0deg)" : "rotate(-90deg)",
              }}>
                ▼
              </span>
            </div>
            {showLobbyPlayers && (() => {
              const rawPlayers = lobbyInfo?.players || playerList;
              // Normalize: players may be objects { name, ready } or plain strings
              const allPlayers = rawPlayers.map(p => typeof p === 'string' ? { name: p, ready: false } : p);
              const showCondensed = allPlayers.length > 6 && !showAllLobbyPlayers;
              
              const handleToggleReady = () => {
                const newReady = !isReady;
                setIsReady(newReady);
                socket.emit("toggleReady", newReady);
              };
              
              const PlayerRow = ({ player, index }) => {
                const playerName = player.name;
                const isMe = playerName === name;
                return (
                  <div key={`${playerName}-${index}`} style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 15px",
                    backgroundColor: isMe ? "rgba(57, 255, 20, 0.2)" : "rgba(57, 255, 20, 0.05)",
                    border: isMe ? "2px solid #39FF14" : "1px solid rgba(57, 255, 20, 0.2)",
                  }}>
                    <span style={{ 
                      fontWeight: "bold", 
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: isMe ? "#39FF14" : "#7fef7f",
                      textShadow: isMe ? "0 0 10px #39FF14" : "none",
                    }}>
                      &gt; {playerName} {isMe && "(YOU)"}
                    </span>
                    {isMe ? (
                      <label 
                        onClick={handleToggleReady}
                        style={{ 
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          userSelect: "none",
                        }}
                      >
                        <span style={{
                          display: "inline-block",
                          width: "18px",
                          height: "18px",
                          border: `2px solid ${isReady ? "#39FF14" : "#7fef7f"}`,
                          backgroundColor: isReady ? "rgba(57, 255, 20, 0.3)" : "transparent",
                          textAlign: "center",
                          lineHeight: "18px",
                          fontSize: "14px",
                          color: "#39FF14",
                          textShadow: isReady ? "0 0 5px #39FF14" : "none",
                          boxShadow: isReady ? "0 0 8px rgba(57, 255, 20, 0.5)" : "none",
                          transition: "all 0.2s",
                        }}>
                          {isReady ? "■" : ""}
                        </span>
                        <span style={{ 
                          color: isReady ? "#39FF14" : "#7fef7f",
                          fontWeight: "bold",
                          opacity: isReady ? 1 : 0.7,
                          textShadow: isReady ? "0 0 5px #39FF14" : "none",
                          transition: "all 0.2s",
                        }}>
                          {isReady ? "READY" : "NOT READY"}
                        </span>
                      </label>
                    ) : (
                      <span style={{ 
                        color: player.ready ? "#39FF14" : "#ff6600",
                        fontWeight: "bold",
                        opacity: player.ready ? 1 : 0.5,
                        textShadow: player.ready ? "0 0 5px #39FF14" : "none",
                      }}>
                        {player.ready ? "READY" : "NOT READY"}
                      </span>
                    )}
                  </div>
                );
              };
              
              const readyCount = allPlayers.filter(p => p.ready).length;
              
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {showCondensed ? (
                    <>
                      {allPlayers.slice(0, 3).map((player, idx) => (
                        <PlayerRow key={`top-${player.name}`} player={player} index={idx} />
                      ))}
                      <div 
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAllLobbyPlayers(true);
                        }}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          padding: "8px 0",
                          color: "#39FF14",
                          opacity: 0.6,
                          fontSize: "0.9em",
                          letterSpacing: "0.3em",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = "1";
                          e.currentTarget.style.textShadow = "0 0 10px #39FF14";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = "0.6";
                          e.currentTarget.style.textShadow = "none";
                        }}
                        title="Click to view all nations"
                      >
                        ·<br/>·<br/>·
                      </div>
                      {allPlayers.slice(-3).map((player, idx) => (
                        <PlayerRow key={`bottom-${player.name}`} player={player} index={allPlayers.length - 3 + idx} />
                      ))}
                      <div style={{
                        textAlign: "center",
                        fontSize: "0.8em",
                        opacity: 0.6,
                        marginTop: "10px",
                        borderTop: "1px solid rgba(57, 255, 20, 0.2)",
                        paddingTop: "10px",
                      }}>
                        ▼ {allPlayers.length} NATIONS ({readyCount}/{allPlayers.length} READY)
                      </div>
                    </>
                  ) : (
                    <>
                      {allPlayers.map((player, idx) => (
                        <PlayerRow key={player.name} player={player} index={idx} />
                      ))}
                      {allPlayers.length > 6 && (
                        <div 
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllLobbyPlayers(false);
                          }}
                          style={{
                            textAlign: "center",
                            fontSize: "0.8em",
                            opacity: 0.6,
                            marginTop: "10px",
                            borderTop: "1px solid rgba(57, 255, 20, 0.2)",
                            paddingTop: "10px",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = "1";
                            e.currentTarget.style.textShadow = "0 0 5px #39FF14";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = "0.6";
                            e.currentTarget.style.textShadow = "none";
                          }}
                          title="Click to collapse list"
                        >
                          ▲ COLLAPSE LIST
                        </div>
                      )}
                    </>
                  )}
                  <div style={{
                    textAlign: "center",
                    fontSize: "0.9em",
                    fontWeight: "bold",
                    marginTop: "8px",
                    padding: "8px",
                    color: readyCount === allPlayers.length ? "#39FF14" : "#ffff00",
                    backgroundColor: readyCount === allPlayers.length
                      ? "rgba(57, 255, 20, 0.15)" 
                      : "rgba(255, 255, 0, 0.1)",
                    border: `1px solid ${readyCount === allPlayers.length ? "rgba(57, 255, 20, 0.4)" : "rgba(255, 255, 0, 0.2)"}`,
                  }}>
                    {readyCount}/{allPlayers.length} NATIONS READY
                    {readyCount === allPlayers.length && " — LAUNCHING..."}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      ) : (
        <div>
          <div style={{
            ...headingStyles,
            fontSize: "1.8em",
            marginBottom: "20px",
            textAlign: "center",
            padding: "15px",
          }}>
            TURNS: Mutually Assured
          </div>
          {/* Header row: Player info + Comms + Video Feed */}
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px", alignItems: "flex-start" }}>
            {/* Player Info - Left */}
            <div 
              className={
                !actionSubmitted && !isTurnProcessing && timeLeft > 0 && timeLeft <= 15 ? "time-critical-box" :
                !actionSubmitted && !isTurnProcessing && timeLeft > 15 && timeLeft <= 30 ? "time-warning-30-box" :
                !actionSubmitted && !isTurnProcessing && timeLeft > 30 && timeLeft <= 60 ? "time-warning-60-box" :
                ""
              }
              style={{
                ...boxStyles,
                fontSize: "1.1em",
                fontWeight: "bold",
                backgroundColor: 
                  !actionSubmitted && !isTurnProcessing && timeLeft > 0 && timeLeft <= 15 ? "rgba(50, 0, 0, 0.7)" :
                  !actionSubmitted && !isTurnProcessing && timeLeft > 15 && timeLeft <= 30 ? "rgba(40, 15, 0, 0.6)" :
                  !actionSubmitted && !isTurnProcessing && timeLeft > 30 && timeLeft <= 60 ? "rgba(30, 20, 0, 0.5)" :
                  "rgba(0, 17, 0, 0.5)",
                borderColor: 
                  !actionSubmitted && !isTurnProcessing && timeLeft > 0 && timeLeft <= 15 ? "#ff0000" :
                  !actionSubmitted && !isTurnProcessing && timeLeft > 15 && timeLeft <= 30 ? "#ff6600" :
                  !actionSubmitted && !isTurnProcessing && timeLeft > 30 && timeLeft <= 60 ? "#ffaa00" :
                  undefined,
                flexShrink: 0,
                transition: "all 0.3s ease",
              }}
            >
              <div style={{ marginBottom: "8px" }}>
                <span style={{ color: "#39FF14", textShadow: "0 0 10px #39FF14" }}>
                  &gt; {name.toUpperCase()}
                </span>
              </div>
              <div style={{ marginBottom: "4px" }}>
                &gt; TURN: {String(turnNumber).padStart(3, '0')}/{String(turnLimit).padStart(3, '0')}
              </div>
              {/* Enhanced Time Display */}
              <div 
                className={
                  !actionSubmitted && !isTurnProcessing && timeLeft > 0 && timeLeft <= 15 ? "time-critical" :
                  !actionSubmitted && !isTurnProcessing && timeLeft > 15 && timeLeft <= 30 ? "time-warning-30" :
                  !actionSubmitted && !isTurnProcessing && timeLeft > 30 && timeLeft <= 60 ? "time-warning-60" :
                  ""
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: timeLeft <= 60 && !actionSubmitted && !isTurnProcessing ? "8px 12px" : "0",
                  marginTop: timeLeft <= 60 && !actionSubmitted && !isTurnProcessing ? "8px" : "0",
                  backgroundColor: timeLeft <= 60 && !actionSubmitted && !isTurnProcessing ? "rgba(0, 0, 0, 0.4)" : "transparent",
                  borderRadius: "4px",
                  transition: "all 0.3s ease",
                }}
              >
                <span style={{ opacity: 0.8 }}>&gt; TIME:</span>
                <span 
                  style={{
                    fontWeight: "bold",
                    fontSize: 
                      !actionSubmitted && !isTurnProcessing && timeLeft <= 15 ? "1.8em" :
                      !actionSubmitted && !isTurnProcessing && timeLeft <= 30 ? "1.4em" :
                      !actionSubmitted && !isTurnProcessing && timeLeft <= 60 ? "1.2em" :
                      "inherit",
                    letterSpacing: "0.05em",
                  }}
                >
                  {String(timeLeft).padStart(3, '0')}s
                </span>
                {/* Warning indicator for critical time */}
                {!actionSubmitted && !isTurnProcessing && timeLeft > 0 && timeLeft <= 15 && (
                  <span style={{ marginLeft: "4px", fontSize: "1.2em" }}>⚠</span>
                )}
              </div>
              {turnNumber === turnLimit && (
                <div style={{ marginTop: "8px", color: "#ff9900", textShadow: "0 0 10px #ff9900" }}>
                  [ FINAL TURN ]
                </div>
              )}
            </div>

            {/* COMMS Button - Center */}
            {playerList.length > 1 && (
              <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <div
                  onClick={() => setShowPlayerDirectory(true)}
                  style={{
                    display: "inline-flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    padding: "8px 16px",
                    backgroundColor: "rgba(57, 255, 20, 0.1)",
                    border: "1px solid rgba(57, 255, 20, 0.4)",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "rgba(57, 255, 20, 0.25)";
                    e.currentTarget.style.boxShadow = "0 0 15px rgba(57, 255, 20, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "rgba(57, 255, 20, 0.1)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                  title="Open player directory - Direct communications"
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "1.1em", fontWeight: "bold" }}>
                    <span style={{ fontSize: "0.85em", opacity: 0.7 }}>+</span>
                    <span style={{ 
                      color: "#39FF14",
                      textShadow: "0 0 8px #39FF14",
                    }}>
                      {playerList.length - 1}
                    </span>
                    <span style={{ fontSize: "0.7em", opacity: 0.6, letterSpacing: "0.05em" }}>
                      {playerList.length === 2 ? "NATION" : "NATIONS"}
                    </span>
                    <span style={{ fontSize: "0.85em", opacity: 0.8 }}>◈</span>
                  </div>
                  <div style={{ 
                    fontSize: "0.75em", 
                    opacity: 0.7, 
                    letterSpacing: "0.08em",
                    marginTop: "3px",
                    textAlign: "center",
                  }}>
                    [ COMMS ]
                  </div>
                </div>
              </div>
            )}

            {/* Dual Video Feeds - Right */}
            <div style={{
              display: "flex",
              gap: "10px",
              flexShrink: 0,
              marginRight: "25px",
            }}>
              {/* Video Feed α */}
              <div style={{
                width: "265px",
                backgroundColor: "rgba(0, 17, 0, 0.8)",
                border: "1px solid rgba(57, 255, 20, 0.3)",
                padding: "10px",
              }}>
                <div style={{
                  fontSize: "0.65em",
                  color: "#39FF14",
                  textAlign: "center",
                  marginBottom: "6px",
                  letterSpacing: "0.08em",
                  opacity: 0.7,
                }}>
                  [ {tvStations[0]} ]
                </div>
                <div style={{ 
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%",
                  backgroundColor: "#000",
                  border: "1px solid rgba(57, 255, 20, 0.2)",
                }}>
                  {(videoLoading[0] || videoLoading[1]) && !currentVideos[0] ? (
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.55em",
                      color: "#39FF14",
                      opacity: 0.6,
                      letterSpacing: "0.08em"
                    }}>
                      GENERATING...
                    </div>
                  ) : currentVideos[0] ? (
                    <>
                      <video
                        key={currentVideos[0]}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          filter: "contrast(1.2) saturate(0.8)",
                        }}
                        src={currentVideos[0]}
                        autoPlay
                        loop
                        muted
                      />
                      {/* GIF Download Button - only show when ready */}
                      {(() => {
                        const videoTurn = currentVideoTurns[0] || turnNumber;
                        const gifKey = `${videoTurn}_0`;
                        const status = gifStatusMap[gifKey] || 'idle';
                        const isReady = status === 'ready';
                        // Only render button when GIF is ready
                        if (!isReady) return null;
                        return (
                          <button
                            onClick={() => handleVideoGifDownload(videoTurn, 0)}
                            title="Download GIF"
                            style={{
                              position: "absolute",
                              bottom: "4px",
                              right: "4px",
                              width: "22px",
                              height: "22px",
                              padding: 0,
                              backgroundColor: "rgba(80, 80, 80, 0.3)",
                              color: "rgba(180, 180, 180, 0.7)",
                              border: "1px solid rgba(120, 120, 120, 0.3)",
                              borderRadius: "2px",
                              cursor: "pointer",
                              fontSize: "0.5em",
                              fontFamily: "'PrintChar21', 'Courier New', monospace",
                              letterSpacing: "0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 10,
                              textShadow: "none",
                              boxShadow: "none",
                              transition: "all 0.3s ease",
                              opacity: 0.7,
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "rgba(57, 255, 20, 0.2)";
                              e.target.style.color = "#39FF14";
                              e.target.style.borderColor = "rgba(57, 255, 20, 0.5)";
                              e.target.style.boxShadow = "0 0 8px rgba(57, 255, 20, 0.6)";
                              e.target.style.opacity = "1";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "rgba(80, 80, 80, 0.3)";
                              e.target.style.color = "rgba(180, 180, 180, 0.7)";
                              e.target.style.borderColor = "rgba(120, 120, 120, 0.3)";
                              e.target.style.boxShadow = "none";
                              e.target.style.opacity = "0.7";
                            }}
                          >
                            GIF
                          </button>
                        );
                      })()}
                    </>
                  ) : (
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.5em",
                      color: "#39FF14",
                      opacity: 0.4,
                      letterSpacing: "0.08em"
                    }}>
                      NO SIGNAL
                    </div>
                  )}
                </div>
                {currentVideoCaptions[0] && (
                  <div style={{
                    marginTop: "5px",
                    fontSize: "0.5em",
                    color: "#39FF14",
                    textAlign: "center",
                    opacity: 0.75,
                    fontStyle: "italic",
                    lineHeight: "1.2em",
                    minHeight: "2.4em",
                  }}>
                    {currentVideoCaptions[0]}
                  </div>
                )}
              </div>

              {/* Video Feed β */}
              <div style={{
                width: "265px",
                backgroundColor: "rgba(0, 17, 0, 0.8)",
                border: "1px solid rgba(57, 255, 20, 0.3)",
                padding: "10px",
              }}>
                <div style={{
                  fontSize: "0.65em",
                  color: "#39FF14",
                  textAlign: "center",
                  marginBottom: "6px",
                  letterSpacing: "0.08em",
                  opacity: 0.7,
                }}>
                  [ {tvStations[1]} ]
                </div>
                <div style={{ 
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%",
                  backgroundColor: "#000",
                  border: "1px solid rgba(57, 255, 20, 0.2)",
                }}>
                  {(videoLoading[0] || videoLoading[1]) && !currentVideos[1] ? (
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.55em",
                      color: "#39FF14",
                      opacity: 0.6,
                      letterSpacing: "0.08em"
                    }}>
                      GENERATING...
                    </div>
                  ) : currentVideos[1] ? (
                    <>
                      <video
                        key={currentVideos[1]}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          filter: "contrast(1.2) saturate(0.8)",
                        }}
                        src={currentVideos[1]}
                        autoPlay
                        loop
                        muted
                      />
                      {/* GIF Download Button - only show when ready */}
                      {(() => {
                        const videoTurn = currentVideoTurns[1] || turnNumber;
                        const gifKey = `${videoTurn}_1`;
                        const status = gifStatusMap[gifKey] || 'idle';
                        const isReady = status === 'ready';
                        // Only render button when GIF is ready
                        if (!isReady) return null;
                        return (
                          <button
                            onClick={() => handleVideoGifDownload(videoTurn, 1)}
                            title="Download GIF"
                            style={{
                              position: "absolute",
                              bottom: "4px",
                              right: "4px",
                              width: "22px",
                              height: "22px",
                              padding: 0,
                              backgroundColor: "rgba(80, 80, 80, 0.3)",
                              color: "rgba(180, 180, 180, 0.7)",
                              border: "1px solid rgba(120, 120, 120, 0.3)",
                              borderRadius: "2px",
                              cursor: "pointer",
                              fontSize: "0.5em",
                              fontFamily: "'PrintChar21', 'Courier New', monospace",
                              letterSpacing: "0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 10,
                              textShadow: "none",
                              boxShadow: "none",
                              transition: "all 0.3s ease",
                              opacity: 0.7,
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "rgba(57, 255, 20, 0.2)";
                              e.target.style.color = "#39FF14";
                              e.target.style.borderColor = "rgba(57, 255, 20, 0.5)";
                              e.target.style.boxShadow = "0 0 8px rgba(57, 255, 20, 0.6)";
                              e.target.style.opacity = "1";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "rgba(80, 80, 80, 0.3)";
                              e.target.style.color = "rgba(180, 180, 180, 0.7)";
                              e.target.style.borderColor = "rgba(120, 120, 120, 0.3)";
                              e.target.style.boxShadow = "none";
                              e.target.style.opacity = "0.7";
                            }}
                          >
                            GIF
                          </button>
                        );
                      })()}
                    </>
                  ) : (
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.5em",
                      color: "#39FF14",
                      opacity: 0.4,
                      letterSpacing: "0.08em"
                    }}>
                      NO SIGNAL
                    </div>
                  )}
                </div>
                {currentVideoCaptions[1] && (
                  <div style={{
                    marginTop: "5px",
                    fontSize: "0.5em",
                    color: "#39FF14",
                    textAlign: "center",
                    opacity: 0.75,
                    fontStyle: "italic",
                    lineHeight: "1.2em",
                    minHeight: "2.4em",
                  }}>
                    {currentVideoCaptions[1]}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dim entire game board when loading */}
          <div style={{ 
            filter: (actionSubmitted || isTurnProcessing) ? "brightness(0.3) blur(2px)" : "none",
            transition: "filter 0.3s ease-in-out"
          }}>
            {/* GLOBAL, REGIONAL, and SECRET INTEL - 3 columns */}
            <div style={{ display: "flex", gap: "15px", marginBottom: "20px" }}>
              {/* GLOBAL NEWS */}
              <div style={{ ...boxStyles, flex: 1 }}>
                <h3 className="flicker-text" style={{ 
                  paddingBottom: "10px",
                  marginBottom: "15px",
                  letterSpacing: "0.1em",
                  fontSize: "0.9em"
                }}>
                  [ GLOBAL HEADLINES ]
                </h3>
                <p style={{ lineHeight: "1.6em", fontWeight: "bold", fontSize: "0.9em" }}>
                  {globalOutcome || "AWAITING WORLD HEADLINES..."}
                </p>
              </div>
              {/* REGIONAL BRIEFING - Personalized for this player's nation */}
              <div style={{ ...boxStyles, flex: 1 }}>
                <h3 className="flicker-text" style={{ 
                  paddingBottom: "10px",
                  marginBottom: "15px",
                  letterSpacing: "0.1em",
                  fontSize: "0.9em"
                }}>
                  [ REGIONAL NEWS ]
                </h3>
                <p style={{ lineHeight: "1.6em", fontWeight: "bold", fontSize: "0.9em" }}>
                  {latestRegionalBriefing || "NO REGIONAL DEVELOPMENTS..."}
                </p>
              </div>
              {/* SECRET INTEL */}
              <div style={{ ...boxStyles, flex: 1 }}>
                <h3 className="flicker-text" style={{ 
                  paddingBottom: "10px",
                  marginBottom: "15px",
                  letterSpacing: "0.1em",
                  fontSize: "0.9em"
                }}>
                  [ SECRET INTEL ]
                </h3>
                <p style={{ lineHeight: "1.6em", fontWeight: "bold", fontSize: "0.9em" }}>
                  {latestSecretEvent || "NO COVERT OPERATIONS THIS TURN..."}
                </p>
              </div>
            </div>

          {/* Two input fields: Public and Private */}
          <div style={boxStyles}>
            <div style={{ marginBottom: "20px" }}>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                color: "#39FF14",
                letterSpacing: "0.05em",
                fontSize: "1.1em"
              }}>
                &gt; PUBLIC ACTION [VISIBLE TO ALL]:
              </label>
              <input
                ref={publicActionInputRef}
                type="text"
                value={publicAction}
                onChange={(e) => setPublicAction(e.target.value)}
                placeholder={
                  (actionSubmitted || isTurnProcessing)
                    ? "Action submitted. Wait for next turn."
                    : "e.g., 'Announce peace treaty with France' (optional)"
                }
                maxLength={1000}
                style={{ ...inputStyles, marginBottom: "10px" }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") sendCommand();
                }}
                disabled={actionSubmitted || isTurnProcessing}
              />
            </div>
            <div style={{ marginBottom: "20px" }}>
              <label style={{ 
                display: "block", 
                marginBottom: "8px",
                letterSpacing: "0.05em",
                fontSize: "1.1em"
              }}>
                &gt; PRIVATE ACTION [CLASSIFIED]:
              </label>
              <input
                type="text"
                value={privateAction}
                onChange={(e) => setPrivateAction(e.target.value)}
                placeholder={
                  (actionSubmitted || isTurnProcessing)
                    ? "Action submitted. Wait for next turn."
                    : "e.g., 'Deploy spies to infiltrate Germany' (optional)"
                }
                maxLength={1000}
                style={inputStyles}
                onKeyDown={(e) => {
                  if (e.key === "Enter") sendCommand();
                }}
                disabled={actionSubmitted || isTurnProcessing}
              />
            </div>
            <button
              onClick={sendCommand}
              style={{ 
                ...submitButtonStyles,
                letterSpacing: "0.1em"
              }}
              disabled={actionSubmitted || isTurnProcessing}
              onMouseEnter={(e) => {
                if (!actionSubmitted && !isTurnProcessing) {
                  e.target.style.backgroundColor = "#002200";
                  e.target.style.boxShadow = "0 0 20px #39FF14";
                  e.target.style.textShadow = "0 0 10px #39FF14";
                }
              }}
              onMouseLeave={(e) => {
                if (!actionSubmitted && !isTurnProcessing) {
                  e.target.style.backgroundColor = "#001100";
                  e.target.style.boxShadow = "0 0 10px #39FF14";
                  e.target.style.textShadow = "0 0 5px #39FF14";
                }
              }}
            >
              [ SUBMIT ACTIONS ]
            </button>

          </div>

          </div>
          
            {/* Retro Loading Overlay - Fixed position over entire app */}
            {/* Show overlay when: user submitted OR server says turn is processing (time ran out) */}
            {(actionSubmitted || isTurnProcessing) && (
              <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.92)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                overflow: "hidden",
              }}>
              {/* CRT Scanlines effect */}
              <div className="scanlines" />
              
              {/* Scrolling binary background */}
              <div className="binary-scroll" style={{
                position: "absolute",
                top: "-30%",
                left: 0,
                right: 0,
                height: "160%",
                opacity: 0.1,
                fontSize: "1.2em",
                lineHeight: "1.2em",
                color: "#39FF14",
                fontFamily: "'PrintChar21', 'Courier New', monospace",
                whiteSpace: "pre",
                overflow: "hidden",
                pointerEvents: "none",
              }}>
                {Array(100).fill(null).map((_, i) => (
                  <div key={i}>
                    {Array(120).fill(null).map((_, j) => Math.random() > 0.5 ? '1' : '0').join(' ')}
                  </div>
                ))}
              </div>

              {/* Main loading box - centered via flex on parent */}
              <div style={{
                position: "relative",
                zIndex: 2,
                display: "flex",
                gap: "40px",
                alignItems: "flex-start",
                maxWidth: "95vw",
              }}>
                {/* Dual Video Panels - Stacked Vertically */}
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  flexShrink: 0,
                }}>
                  {/* Video Panel 1 */}
                  <div style={{
                    width: "416px",
                    backgroundColor: "rgba(0, 17, 0, 0.9)",
                    border: "1px solid rgba(57, 255, 20, 0.4)",
                    padding: "12px",
                  }}>
                    <div style={{
                      fontSize: "0.75em",
                      color: "#39FF14",
                      textAlign: "center",
                      marginBottom: "8px",
                      letterSpacing: "0.12em",
                      textShadow: "0 0 10px #39FF14",
                    }}>
                      [ {tvStations[0]} ]
                    </div>
                    <div style={{ 
                      position: "relative",
                      width: "100%",
                      paddingTop: "56.25%",
                      backgroundColor: "#000",
                      border: "1px solid rgba(57, 255, 20, 0.3)",
                    }}>
                      {currentVideos[0] ? (
                        <>
                          <video
                            key={currentVideos[0]}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              filter: "contrast(1.2) saturate(0.8)",
                            }}
                            src={currentVideos[0]}
                            autoPlay
                            loop
                            muted
                          />
                          {/* CRT overlay on video */}
                          <div style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "linear-gradient(transparent 50%, rgba(0,0,0,0.15) 50%)",
                            backgroundSize: "100% 3px",
                            pointerEvents: "none",
                          }} />
                          {/* GIF Download Button - only show when ready */}
                          {(() => {
                            const videoTurn = currentVideoTurns[0] || turnNumber;
                            const gifKey = `${videoTurn}_0`;
                            const status = gifStatusMap[gifKey] || 'idle';
                            const isReady = status === 'ready';
                            // Only render button when GIF is ready
                            if (!isReady) return null;
                            return (
                              <button
                                onClick={() => handleVideoGifDownload(videoTurn, 0)}
                                title="Download GIF"
                                style={{
                                  position: "absolute",
                                  bottom: "6px",
                                  right: "6px",
                                  width: "26px",
                                  height: "26px",
                                  padding: 0,
                                  backgroundColor: "rgba(80, 80, 80, 0.3)",
                                  color: "rgba(180, 180, 180, 0.7)",
                                  border: "1px solid rgba(120, 120, 120, 0.3)",
                                  borderRadius: "3px",
                                  cursor: "pointer",
                                  fontSize: "0.65em",
                                  fontFamily: "'PrintChar21', 'Courier New', monospace",
                                  letterSpacing: "0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  zIndex: 10,
                                  textShadow: "none",
                                  boxShadow: "none",
                                  transition: "all 0.3s ease",
                                  opacity: 0.7,
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = "rgba(57, 255, 20, 0.2)";
                                  e.target.style.color = "#39FF14";
                                  e.target.style.borderColor = "rgba(57, 255, 20, 0.5)";
                                  e.target.style.boxShadow = "0 0 10px rgba(57, 255, 20, 0.6)";
                                  e.target.style.opacity = "1";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = "rgba(80, 80, 80, 0.3)";
                                  e.target.style.color = "rgba(180, 180, 180, 0.7)";
                                  e.target.style.borderColor = "rgba(120, 120, 120, 0.3)";
                                  e.target.style.boxShadow = "none";
                                  e.target.style.opacity = "0.7";
                                }}
                              >
                                GIF
                              </button>
                            );
                          })()}
                        </>
                      ) : (
                        <div style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.75em",
                          color: "#39FF14",
                          opacity: 0.4,
                          letterSpacing: "0.1em"
                        }}>
                          {videoLoading[0] ? "GENERATING..." : "AWAITING FEED"}
                        </div>
                      )}
                    </div>
                    {/* Video Caption 1 */}
                    {currentVideoCaptions[0] && (
                      <div style={{
                        marginTop: "8px",
                        fontSize: "0.84em",
                        color: "#39FF14",
                        textAlign: "center",
                        letterSpacing: "0.05em",
                        opacity: 0.85,
                        fontStyle: "italic",
                        textShadow: "0 0 5px rgba(57, 255, 20, 0.5)",
                        lineHeight: "1.3em",
                        minHeight: "2.6em",
                      }}>
                        {currentVideoCaptions[0]}
                      </div>
                    )}
                  </div>

                  {/* Video Panel 2 */}
                  <div style={{
                    width: "416px",
                    backgroundColor: "rgba(0, 17, 0, 0.9)",
                    border: "1px solid rgba(57, 255, 20, 0.4)",
                    padding: "12px",
                  }}>
                    <div style={{
                      fontSize: "0.75em",
                      color: "#39FF14",
                      textAlign: "center",
                      marginBottom: "8px",
                      letterSpacing: "0.12em",
                      textShadow: "0 0 10px #39FF14",
                    }}>
                      [ {tvStations[1]} ]
                    </div>
                    <div style={{ 
                      position: "relative",
                      width: "100%",
                      paddingTop: "56.25%",
                      backgroundColor: "#000",
                      border: "1px solid rgba(57, 255, 20, 0.3)",
                    }}>
                      {currentVideos[1] ? (
                        <>
                          <video
                            key={currentVideos[1]}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              filter: "contrast(1.2) saturate(0.8)",
                            }}
                            src={currentVideos[1]}
                            autoPlay
                            loop
                            muted
                          />
                          {/* CRT overlay on video */}
                          <div style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "linear-gradient(transparent 50%, rgba(0,0,0,0.15) 50%)",
                            backgroundSize: "100% 3px",
                            pointerEvents: "none",
                          }} />
                          {/* GIF Download Button - only show when ready */}
                          {(() => {
                            const videoTurn = currentVideoTurns[1] || turnNumber;
                            const gifKey = `${videoTurn}_1`;
                            const status = gifStatusMap[gifKey] || 'idle';
                            const isReady = status === 'ready';
                            // Only render button when GIF is ready
                            if (!isReady) return null;
                            return (
                              <button
                                onClick={() => handleVideoGifDownload(videoTurn, 1)}
                                title="Download GIF"
                                style={{
                                  position: "absolute",
                                  bottom: "6px",
                                  right: "6px",
                                  width: "26px",
                                  height: "26px",
                                  padding: 0,
                                  backgroundColor: "rgba(80, 80, 80, 0.3)",
                                  color: "rgba(180, 180, 180, 0.7)",
                                  border: "1px solid rgba(120, 120, 120, 0.3)",
                                  borderRadius: "3px",
                                  cursor: "pointer",
                                  fontSize: "0.65em",
                                  fontFamily: "'PrintChar21', 'Courier New', monospace",
                                  letterSpacing: "0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  zIndex: 10,
                                  textShadow: "none",
                                  boxShadow: "none",
                                  transition: "all 0.3s ease",
                                  opacity: 0.7,
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = "rgba(57, 255, 20, 0.2)";
                                  e.target.style.color = "#39FF14";
                                  e.target.style.borderColor = "rgba(57, 255, 20, 0.5)";
                                  e.target.style.boxShadow = "0 0 10px rgba(57, 255, 20, 0.6)";
                                  e.target.style.opacity = "1";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = "rgba(80, 80, 80, 0.3)";
                                  e.target.style.color = "rgba(180, 180, 180, 0.7)";
                                  e.target.style.borderColor = "rgba(120, 120, 120, 0.3)";
                                  e.target.style.boxShadow = "none";
                                  e.target.style.opacity = "0.7";
                                }}
                              >
                                GIF
                              </button>
                            );
                          })()}
                        </>
                      ) : (
                        <div style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.75em",
                          color: "#39FF14",
                          opacity: 0.4,
                          letterSpacing: "0.1em"
                        }}>
                          {videoLoading[1] ? "GENERATING..." : "AWAITING FEED"}
                        </div>
                      )}
                    </div>
                    {/* Video Caption 2 */}
                    {currentVideoCaptions[1] && (
                      <div style={{
                        marginTop: "8px",
                        fontSize: "0.84em",
                        color: "#39FF14",
                        textAlign: "center",
                        letterSpacing: "0.05em",
                        opacity: 0.85,
                        fontStyle: "italic",
                        textShadow: "0 0 5px rgba(57, 255, 20, 0.5)",
                        lineHeight: "1.3em",
                        minHeight: "2.6em",
                      }}>
                        {currentVideoCaptions[1]}
                      </div>
                    )}
                  </div>
                </div>


                {/* Center - Processing Status */}
                <div style={{
                  padding: "25px 40px",
                  backgroundColor: "rgba(0, 0, 0, 0.9)",
                  textAlign: "center",
                  border: "1px solid rgba(57, 255, 20, 0.2)",
                  minWidth: "324px",
                }}>
                  <div style={{
                    fontSize: "0.9em",
                    color: "#39FF14",
                    textShadow: "0 0 12px #39FF14",
                    marginBottom: "10px",
                    letterSpacing: "0.15em",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}>
                    ════════════════════════════════
                  </div>
                  <div 
                    className="pulse-animation chromatic-text"
                    data-text={turnNumber >= turnLimit ? "[ FINAL TURN ]" : "[ PROCESSING TURN ]"}
                    style={{
                      fontSize: "1.1em",
                      color: turnNumber >= turnLimit ? "#ff9900" : "#39FF14",
                      textShadow: turnNumber >= turnLimit 
                        ? "0 0 15px #ff9900, 0 0 30px #ff6600" 
                        : "0 0 15px #39FF14, 0 0 30px #39FF14",
                      marginBottom: "12px",
                      letterSpacing: "0.1em",
                      whiteSpace: "nowrap",
                    }}>
                    {turnNumber >= turnLimit ? "[ FINAL TURN ]" : "[ PROCESSING TURN ]"}
                  </div>
                  <div style={{
                    fontSize: "2.4em",
                    color: turnNumber >= turnLimit ? "#ff9900" : "#39FF14",
                    letterSpacing: "0.3em",
                    fontWeight: "bold",
                    textShadow: turnNumber >= turnLimit ? "0 0 20px #ff9900" : "0 0 20px #39FF14",
                    marginBottom: "12px",
                    minWidth: "220px",
                  }}>
                    {loadingDots || "."}
                  </div>
                  <div style={{
                    fontSize: "0.75em",
                    color: turnNumber >= turnLimit ? "#ff9900" : "#39FF14",
                    textShadow: turnNumber >= turnLimit ? "0 0 10px #ff9900" : "0 0 10px #39FF14",
                    marginBottom: "10px",
                    letterSpacing: "0.08em",
                    lineHeight: "1.3em",
                  }}>
                    {turnNumber >= turnLimit ? "GENERATING STORY CONCLUSION" : "GAME MASTER DELIBERATING"}{loadingDots}
                  </div>
                  {/* Turn and time display - hide timer when turn is processing */}
                  <div style={{
                    fontSize: "0.7em",
                    color: "#39FF14",
                    opacity: 0.8,
                    marginBottom: "10px",
                    letterSpacing: "0.05em",
                  }}>
                    TURN {turnNumber}/{turnLimit}
                    {!isTurnProcessing && (
                      <span 
                        className={
                          timeLeft > 0 && timeLeft <= 15 ? "time-critical" :
                          timeLeft > 15 && timeLeft <= 30 ? "time-warning-30" :
                          timeLeft > 30 && timeLeft <= 60 ? "time-warning-60" :
                          ""
                        }
                        style={{
                          marginLeft: "8px",
                          fontWeight: timeLeft <= 30 ? "bold" : "normal",
                        }}
                      >
                        | TIME: {timeLeft > 0 ? `${timeLeft}s` : "0s"}
                      </span>
                    )}
                  </div>
                  {/* Only show "waiting for" if user submitted early and others haven't - not when time ran out */}
                  {actionSubmitted && !isTurnProcessing && waitingForPlayers.length > 0 ? (
                    <div style={{
                      fontSize: "0.75em",
                      color: "#39FF14",
                      marginTop: "10px",
                      letterSpacing: "0.05em",
                    }}>
                      <div style={{ opacity: 0.7, marginBottom: "6px" }}>
                        [ WAITING FOR ]
                      </div>
                      <div style={{ 
                        textShadow: "0 0 8px #39FF14",
                        lineHeight: "1.5em"
                      }}>
                        {waitingForPlayers.length > 10 ? (
                          <div>
                            &gt; {waitingForPlayers.length} PLAYERS
                          </div>
                        ) : (
                          waitingForPlayers.map((playerName, idx) => (
                            <div key={playerName}>
                              &gt; {playerName.toUpperCase()}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      fontSize: "0.75em",
                      color: "#39FF14",
                      marginTop: "10px",
                      letterSpacing: "0.05em",
                    }}>
                      {/* Show progress bar when processing with progress data */}
                      {processingProgress ? (
                        <div style={{ width: "240px", margin: "0 auto" }}>
                          {/* Fixed-height message container to prevent layout shift */}
                          <div style={{
                            textShadow: turnNumber >= turnLimit ? "0 0 12px #ff9900" : "0 0 12px #39FF14",
                            color: turnNumber >= turnLimit ? "#ff9900" : "#39FF14",
                            marginBottom: "8px",
                            opacity: 0.9,
                            height: "2.6em", // Fixed height for 2 lines max
                            lineHeight: "1.3em",
                            fontSize: "0.85em",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            overflow: "hidden",
                            padding: "0 4px",
                          }}>
                            [ {processingProgress.message || "PROCESSING..."} ]
                          </div>
                          {/* Progress bar container */}
                          <div style={{
                            width: "100%",
                            height: "14px",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            border: turnNumber >= turnLimit 
                              ? "1px solid rgba(255, 153, 0, 0.4)" 
                              : "1px solid rgba(57, 255, 20, 0.4)",
                            position: "relative",
                            overflow: "hidden",
                          }}>
                            {/* Progress bar fill */}
                            <div style={{
                              width: `${processingProgress.progress || 0}%`,
                              height: "100%",
                              backgroundColor: turnNumber >= turnLimit 
                                ? "rgba(255, 153, 0, 0.3)" 
                                : "rgba(57, 255, 20, 0.3)",
                              boxShadow: turnNumber >= turnLimit 
                                ? "0 0 12px rgba(255, 153, 0, 0.5)" 
                                : "0 0 12px rgba(57, 255, 20, 0.5)",
                              transition: "width 0.3s ease-out",
                            }} />
                            {/* Progress text overlay */}
                            <div style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.85em",
                              fontWeight: "bold",
                              color: turnNumber >= turnLimit ? "#ff9900" : "#39FF14",
                              textShadow: turnNumber >= turnLimit ? "0 0 6px #ff9900" : "0 0 6px #39FF14",
                            }}>
                              {processingProgress.progress || 0}%
                            </div>
                          </div>
                          {/* Stage indicator */}
                          <div style={{
                            marginTop: "6px",
                            fontSize: "0.7em",
                            opacity: 0.6,
                            letterSpacing: "0.08em",
                            color: turnNumber >= turnLimit ? "#ff9900" : "#39FF14",
                          }}>
                            STAGE: {(processingProgress.stage || "generating").toUpperCase()}
                          </div>
                        </div>
                      ) : (
                        <div style={{ 
                          textShadow: "0 0 12px #39FF14",
                          lineHeight: "1.4em",
                        }}>
                          [ ALL PLAYERS READY ]
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{
                    fontSize: "0.9em",
                    color: "#39FF14",
                    textShadow: "0 0 12px #39FF14",
                    marginTop: "10px",
                    letterSpacing: "0.15em",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}>
                    ═══════════════════════
                  </div>
                </div>

                {/* Leaderboard Panel - Right */}
                <div 
                  onClick={() => Object.entries(playerInfluences).length > 0 && setShowFullRankings(true)}
                  style={{
                    width: "200px",
                    flexShrink: 0,
                    backgroundColor: "rgba(0, 17, 0, 0.9)",
                    border: "1px solid rgba(57, 255, 20, 0.4)",
                    padding: "15px",
                    display: "flex",
                    flexDirection: "column",
                    cursor: Object.entries(playerInfluences).length > 0 ? "pointer" : "default",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (Object.entries(playerInfluences).length > 0) {
                      e.currentTarget.style.backgroundColor = "rgba(0, 34, 0, 0.95)";
                      e.currentTarget.style.boxShadow = "0 0 20px rgba(57, 255, 20, 0.3)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "rgba(0, 17, 0, 0.9)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                  title={Object.entries(playerInfluences).length > 0 ? "Click for full rankings" : ""}
                >
                  <div style={{
                    fontSize: "0.8em",
                    color: "#39FF14",
                    textAlign: "center",
                    marginBottom: "10px",
                    letterSpacing: "0.1em",
                    textShadow: "0 0 10px #39FF14",
                    borderBottom: "1px solid rgba(57, 255, 20, 0.3)",
                    paddingBottom: "8px",
                  }}>
                    [ INFLUENCE<br/>RANKINGS ]
                  </div>
                  <InfluenceRankingsPanel
                    influences={playerInfluences}
                    currentPlayer={name}
                    onShowAll={() => setShowFullRankings(true)}
                    compact={true}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Player Interaction Modal */}
          {interactionTarget && (
            <div style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}>
              <div className="scanlines" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 9999 }} />
              
              <div style={{
                backgroundColor: "rgba(0, 17, 0, 0.95)",
                border: "2px solid #39FF14",
                padding: "25px 30px",
                maxWidth: "500px",
                width: "90%",
                boxShadow: "0 0 30px rgba(57, 255, 20, 0.3), inset 0 0 20px rgba(57, 255, 20, 0.1)",
              }}>
                <div style={{
                  fontSize: "1.2em",
                  color: "#39FF14",
                  textShadow: "0 0 15px #39FF14",
                  marginBottom: "20px",
                  letterSpacing: "0.15em",
                  borderBottom: "1px solid #39FF14",
                  paddingBottom: "15px",
                }}>
                  [ INITIATE INTERACTION ]
                </div>
                
                <div style={{
                  fontSize: "1.1em",
                  color: "#39FF14",
                  marginBottom: "15px",
                }}>
                  TARGET: <span style={{ 
                    fontWeight: "bold", 
                    textShadow: "0 0 10px #39FF14",
                    letterSpacing: "0.1em"
                  }}>{interactionTarget.toUpperCase()}</span>
                </div>
                
                <div style={{
                  fontSize: "0.85em",
                  color: "#39FF14",
                  opacity: 0.7,
                  marginBottom: "15px",
                  lineHeight: "1.5em",
                }}>
                  Describe your interaction attempt. The Game Master will determine success based on context, your resources, and any countermeasures.
                </div>
                
                <textarea
                  ref={interactionInputRef}
                  value={interactionDrafts[interactionTarget] || ""}
                  onChange={(e) => setInteractionDrafts(prev => ({ ...prev, [interactionTarget]: e.target.value }))}
                  placeholder={`e.g., "Propose secret alliance", "Sabotage their trade deal", "Send diplomatic envoy"...`}
                  maxLength={500}
                  style={{
                    ...inputStyles,
                    width: "100%",
                    height: "100px",
                    resize: "none",
                    marginBottom: "20px",
                    boxSizing: "border-box",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation(); // Prevent global Enter handler from also firing
                      savePlayerInteraction();
                    }
                    if (e.key === "Escape") {
                      setInteractionTarget(null);
                      setShowPlayerDirectory(true); // Go back to player list
                    }
                  }}
                />
                
                <div style={{ display: "flex", gap: "15px", justifyContent: "center" }}>
                  <button
                    onClick={savePlayerInteraction}
                    style={{
                      ...submitButtonStyles,
                      padding: "12px 30px",
                      letterSpacing: "0.1em",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.boxShadow = "0 0 25px #39FF14";
                      e.target.style.textShadow = "0 0 15px #39FF14";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.boxShadow = "0 0 10px #39FF14";
                      e.target.style.textShadow = "0 0 5px #39FF14";
                    }}
                  >
                    [ SUBMIT ]
                  </button>
                  <button
                    onClick={clearPlayerInteraction}
                    disabled={!(interactionDrafts[interactionTarget] || "").trim()}
                    style={{
                      ...buttonStyles,
                      padding: "12px 30px",
                      letterSpacing: "0.1em",
                      opacity: (interactionDrafts[interactionTarget] || "").trim() ? 1 : 0.4,
                    }}
                    onMouseEnter={(e) => {
                      if ((interactionDrafts[interactionTarget] || "").trim()) {
                        e.target.style.backgroundColor = "#220000";
                        e.target.style.boxShadow = "0 0 15px #ff3333";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#001100";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    [ CLEAR ]
                  </button>
                </div>
                
                <div style={{
                  marginTop: "20px",
                  fontSize: "0.75em",
                  color: "#39FF14",
                  opacity: 0.5,
                  lineHeight: "1.5em",
                }}>
                  ⚠ Other players may have taken actions to interfere with or monitor your interactions. Proceed with caution.
                </div>
              </div>
            </div>
          )}

          {/* Player Directory Overlay - Extends DM functionality for 100 players */}
          {showPlayerDirectory && (
            <div 
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.95)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2000,
              }}
              onClick={(e) => e.target === e.currentTarget && setShowPlayerDirectory(false)}
            >
              <div className="scanlines" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 9999 }} />
              
              <div style={{
                backgroundColor: "rgba(0, 17, 0, 0.98)",
                border: "2px solid #39FF14",
                padding: "30px 40px",
                maxWidth: "700px",
                width: "95%",
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 0 40px rgba(57, 255, 20, 0.3), inset 0 0 30px rgba(57, 255, 20, 0.1)",
              }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  borderBottom: "1px solid #39FF14",
                  paddingBottom: "15px",
                }}>
                  <div style={{
                    fontSize: "1.2em",
                    color: "#39FF14",
                    textShadow: "0 0 15px #39FF14",
                    letterSpacing: "0.15em",
                  }}>
                    [ DIPLOMATIC CHANNELS ]
                  </div>
                  <button
                    onClick={() => setShowPlayerDirectory(false)}
                    style={{
                      background: "none",
                      border: "1px solid rgba(57, 255, 20, 0.5)",
                      color: "#39FF14",
                      padding: "6px 12px",
                      fontFamily: "'PrintChar21', 'Courier New', monospace",
                      fontSize: "0.9em",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "rgba(57, 255, 20, 0.2)";
                      e.target.style.boxShadow = "0 0 10px rgba(57, 255, 20, 0.5)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    ESC
                  </button>
                </div>
                
                <div style={{
                  fontSize: "0.85em",
                  color: "#39FF14",
                  opacity: 0.7,
                  marginBottom: "15px",
                  lineHeight: "1.5em",
                }}>
                  Select a nation to initiate direct communication. All diplomatic exchanges are monitored by intelligence services.
                </div>
                
                <div style={{
                  flex: 1,
                  overflowY: "auto",
                  paddingRight: "10px",
                }}>
                  {playerList
                    .filter(p => p !== name)
                    .sort((a, b) => a.localeCompare(b))
                    .map((playerName) => {
                      const influence = playerInfluences[playerName] ?? "—";
                      const hasDraft = (interactionDrafts[playerName] || "").trim();
                      return (
                        <div
                          key={playerName}
                          onClick={() => {
                            if (!actionSubmitted && !isTurnProcessing) {
                              setInteractionTarget(playerName);
                              setShowPlayerDirectory(false);
                            }
                          }}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 15px",
                            marginBottom: "8px",
                            backgroundColor: hasDraft ? "rgba(57, 255, 20, 0.15)" : "rgba(57, 255, 20, 0.05)",
                            border: hasDraft ? "1px solid rgba(57, 255, 20, 0.5)" : "1px solid rgba(57, 255, 20, 0.2)",
                            cursor: (actionSubmitted || isTurnProcessing) ? "default" : "pointer",
                            transition: "all 0.2s",
                            opacity: (actionSubmitted || isTurnProcessing) ? 0.5 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!actionSubmitted && !isTurnProcessing) {
                              e.currentTarget.style.backgroundColor = "rgba(57, 255, 20, 0.25)";
                              e.currentTarget.style.boxShadow = "0 0 15px rgba(57, 255, 20, 0.3)";
                              e.currentTarget.style.borderColor = "#39FF14";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = hasDraft ? "rgba(57, 255, 20, 0.15)" : "rgba(57, 255, 20, 0.05)";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.borderColor = hasDraft ? "rgba(57, 255, 20, 0.5)" : "rgba(57, 255, 20, 0.2)";
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <span style={{
                              fontWeight: "bold",
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                              color: "#39FF14",
                              textShadow: hasDraft ? "0 0 8px #39FF14" : "none",
                            }}>
                              {playerName}
                            </span>
                            {hasDraft && (
                              <span style={{
                                fontSize: "0.75em",
                                padding: "2px 6px",
                                backgroundColor: "rgba(57, 255, 20, 0.3)",
                                border: "1px solid rgba(57, 255, 20, 0.5)",
                                letterSpacing: "0.05em",
                              }}>
                                DRAFT
                              </span>
                            )}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                            <span style={{
                              fontSize: "0.85em",
                              opacity: 0.7,
                              letterSpacing: "0.05em",
                            }}>
                              INFLUENCE: <span style={{ fontWeight: "bold", opacity: 1 }}>{influence}</span>
                            </span>
                            <span style={{ fontSize: "1.1em", opacity: 0.6 }}>▶</span>
                          </div>
                        </div>
                      );
                    })}
                </div>
                
                <div style={{
                  marginTop: "15px",
                  paddingTop: "15px",
                  borderTop: "1px solid rgba(57, 255, 20, 0.3)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: "0.8em",
                  opacity: 0.6,
                }}>
                  <span>{playerList.length - 1} NATIONS AVAILABLE</span>
                  <span>PRESS ESC TO CLOSE</span>
                </div>
              </div>
            </div>
          )}

          {/* Full Influence Rankings Overlay */}
          {showFullRankings && (
            <div 
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.95)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2000,
              }}
              onClick={(e) => e.target === e.currentTarget && setShowFullRankings(false)}
              onKeyDown={(e) => e.key === "Escape" && setShowFullRankings(false)}
            >
              <div className="scanlines" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 9999 }} />
              
              <div style={{
                backgroundColor: "rgba(0, 17, 0, 0.98)",
                border: "2px solid #39FF14",
                padding: "25px 30px",
                maxWidth: "500px",
                width: "90%",
                maxHeight: "80vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 0 40px rgba(57, 255, 20, 0.3), inset 0 0 30px rgba(57, 255, 20, 0.1)",
              }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  borderBottom: "1px solid #39FF14",
                  paddingBottom: "15px",
                }}>
                  <div style={{
                    fontSize: "1.2em",
                    color: "#39FF14",
                    textShadow: "0 0 15px #39FF14",
                    letterSpacing: "0.15em",
                  }}>
                    [ GLOBAL INFLUENCE ]
                  </div>
                  <button
                    onClick={() => setShowFullRankings(false)}
                    style={{
                      background: "none",
                      border: "1px solid rgba(57, 255, 20, 0.5)",
                      color: "#39FF14",
                      padding: "6px 12px",
                      fontFamily: "'PrintChar21', 'Courier New', monospace",
                      fontSize: "0.9em",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "rgba(57, 255, 20, 0.2)";
                      e.target.style.boxShadow = "0 0 10px rgba(57, 255, 20, 0.5)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    ESC
                  </button>
                </div>
                
                <div style={{
                  flex: 1,
                  overflowY: "auto",
                  paddingRight: "10px",
                }}>
                  {Object.entries(playerInfluences)
                    .sort(([, a], [, b]) => b - a)
                    .map(([nation, influence], index) => {
                      const isYou = nation === name;
                      const isTop3 = index < 3;
                      return (
                        <div
                          key={nation}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "10px 15px",
                            marginBottom: "6px",
                            backgroundColor: isYou 
                              ? "rgba(57, 255, 20, 0.2)" 
                              : isTop3 
                                ? "rgba(255, 200, 0, 0.08)" 
                                : "rgba(57, 255, 20, 0.05)",
                            border: isYou 
                              ? "2px solid #39FF14" 
                              : isTop3 
                                ? "1px solid rgba(255, 200, 0, 0.3)" 
                                : "1px solid rgba(57, 255, 20, 0.15)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <span style={{
                              width: "30px",
                              fontSize: isTop3 ? "1.1em" : "0.95em",
                              fontWeight: isTop3 ? "bold" : "normal",
                              color: isTop3 ? "#ffc800" : "#39FF14",
                              textShadow: isTop3 ? "0 0 8px rgba(255, 200, 0, 0.5)" : "none",
                            }}>
                              {index === 0 ? "①" : index === 1 ? "②" : index === 2 ? "③" : `${index + 1}.`}
                            </span>
                            <span style={{
                              fontWeight: isYou ? "bold" : "normal",
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                              color: "#39FF14",
                              textShadow: isYou ? "0 0 10px #39FF14" : "none",
                            }}>
                              {nation}{isYou ? " ★" : ""}
                            </span>
                          </div>
                          <span style={{
                            fontSize: isTop3 ? "1.1em" : "1em",
                            fontWeight: "bold",
                            color: isTop3 ? "#ffc800" : "#39FF14",
                            textShadow: isTop3 ? "0 0 8px rgba(255, 200, 0, 0.5)" : "0 0 5px #39FF14",
                          }}>
                            {influence}
                          </span>
                        </div>
                      );
                    })}
                </div>
                
                <div style={{
                  marginTop: "15px",
                  paddingTop: "15px",
                  borderTop: "1px solid rgba(57, 255, 20, 0.3)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: "0.8em",
                  opacity: 0.6,
                }}>
                  <span>{Object.keys(playerInfluences).length} NATIONS TRACKED</span>
                  <span>TURN {turnNumber}/{turnLimit}</span>
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: "20px" }}>
            <button
              onClick={() => setShowPlayerData(!showPlayerData)}
              style={{ ...buttonStyles, marginBottom: "10px", letterSpacing: "0.1em" }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#002200";
                e.target.style.boxShadow = "0 0 20px #39FF14";
                e.target.style.textShadow = "0 0 10px #39FF14";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#001100";
                e.target.style.boxShadow = "none";
                e.target.style.textShadow = "none";
              }}
            >
              {showPlayerData ? "[ HIDE SNAPSHOT ]" : "[ SHOW SNAPSHOT ]"}
            </button>
            {showPlayerData && (
              <div style={{ display: "flex", gap: "20px" }}>
                {/* Your Nation Status - Left */}
                <div style={{ ...boxStyles, flex: 1 }}>
                  <h3 style={{ 
                    paddingBottom: "10px",
                    marginBottom: "15px",
                    letterSpacing: "0.1em",
                    fontWeight: "bold"
                  }}>
                    [ NATION STATUS ]
                  </h3>
                  {Object.entries(playerSnapshot).length > 0 ? (
                    // Sort entries: influence_score first, then alphabetically
                    Object.entries(playerSnapshot)
                      .sort(([a], [b]) => {
                        if (a === 'influence_score') return -1;
                        if (b === 'influence_score') return 1;
                        return a.localeCompare(b);
                      })
                      .map(([key, value]) => {
                        // Format value: handle objects, arrays, and primitives
                        let displayValue;
                        if (value === null || value === undefined) {
                          displayValue = 'N/A';
                        } else if (Array.isArray(value)) {
                          displayValue = value.join(', ');
                        } else if (typeof value === 'object') {
                          // Flatten object to key=value pairs
                          displayValue = Object.entries(value)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(', ');
                        } else {
                          displayValue = value;
                        }
                        return (
                          <p key={key} style={{ 
                            fontWeight: "bold",
                            lineHeight: "1.8em",
                            paddingLeft: "10px"
                          }}>
                            &gt; {key.toUpperCase().replace(/_/g, ' ')}: {displayValue}
                          </p>
                        );
                      })
                  ) : (
                    <p style={{ opacity: 0.6, paddingLeft: "10px" }}>
                      &gt; NO DATA AVAILABLE
                    </p>
                  )}
                </div>

                {/* Global Influence Rankings - Right */}
                <div 
                  style={{ ...boxStyles, flex: 1, cursor: Object.entries(playerInfluences).length > 0 ? "pointer" : "default" }}
                  onClick={() => Object.entries(playerInfluences).length > 0 && setShowFullRankings(true)}
                  title={Object.entries(playerInfluences).length > 0 ? "Click for full rankings" : ""}
                >
                  <h3 style={{ 
                    paddingBottom: "10px",
                    marginBottom: "15px",
                    letterSpacing: "0.1em",
                    fontWeight: "bold"
                  }}>
                    [ GLOBAL INFLUENCE RANKINGS ]
                  </h3>
                  <InfluenceRankingsPanel
                    influences={playerInfluences}
                    currentPlayer={name}
                    onShowAll={() => setShowFullRankings(true)}
                  />
                </div>
              </div>
            )}
            <button
              onClick={() => setShowHistory(!showHistory)}
              style={{ ...buttonStyles, marginBottom: "10px", letterSpacing: "0.1em" }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#003300";
                e.target.style.boxShadow = "0 0 20px #39FF14";
                e.target.style.textShadow = "0 0 10px #39FF14";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#002200";
                e.target.style.boxShadow = "none";
                e.target.style.textShadow = "none";
              }}
            >
              {showHistory ? "[ HIDE ARCHIVES ]" : "[ SHOW ARCHIVES ]"}
            </button>
            {showHistory && (
              <div style={{ ...boxStyles }}>
                <h3 className="flicker-text" style={{ 
                  paddingBottom: "10px",
                  marginBottom: "20px",
                  letterSpacing: "0.1em",
                  fontWeight: "bold",
                  textAlign: "center"
                }}>
                  [ ARCHIVES ]
                </h3>
                
                <div
                  id="archivesScrollDiv"
                  style={{
                    height: "500px",
                    overflow: "auto",
                  }}
                >
                  <InfiniteScroll
                    dataLength={turnHistory.length}
                    next={fetchMoreData}
                    hasMore={hasMore}
                    inverse={true}
                    loader={<h4>Loading...</h4>}
                    scrollableTarget="archivesScrollDiv"
                  >
                    {turnHistory.slice(1).map((turn) => (
                      <div key={turn.turnNumber} style={{ marginBottom: "25px" }}>
                        <div style={{ 
                          color: "#39FF14", 
                          fontWeight: "bold",
                          marginBottom: "15px",
                          letterSpacing: "0.1em",
                          fontSize: "1.1em"
                        }}>
                          &gt;&gt; TURN {turn.turnNumber}
                        </div>
                        
                        {/* 4-column layout: Global | Regional | Secret | Videos */}
                        <div style={{ display: "flex", gap: "15px", alignItems: "flex-start" }}>
                          {/* Global Outcome */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              opacity: 0.7, 
                              marginBottom: "8px",
                              fontSize: "0.75em",
                              letterSpacing: "0.05em"
                            }}>
                              GLOBAL:
                            </div>
                            <p style={{ 
                              fontWeight: "bold", 
                              lineHeight: "1.4em",
                              fontSize: "0.8em",
                              paddingLeft: "8px"
                            }}>
                              {turn.globalOutcome || turn.outcome || "(none)"}
                            </p>
                          </div>
                          
                          {/* Regional Briefing - Personalized for this player */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              opacity: 0.7, 
                              marginBottom: "8px",
                              fontSize: "0.75em",
                              letterSpacing: "0.05em"
                            }}>
                              REGIONAL:
                            </div>
                            <p style={{ 
                              fontWeight: "bold", 
                              lineHeight: "1.4em",
                              fontSize: "0.8em",
                              paddingLeft: "8px"
                            }}>
                              {regionalBriefingHistory[turn.turnNumber] || "(no regional developments)"}
                            </p>
                          </div>
                          
                          {/* Secret Event */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              opacity: 0.7, 
                              marginBottom: "8px",
                              fontSize: "0.75em",
                              letterSpacing: "0.05em"
                            }}>
                              CLASSIFIED:
                            </div>
                            <p style={{ 
                              fontWeight: "bold", 
                              lineHeight: "1.4em",
                              fontSize: "0.8em",
                              paddingLeft: "8px"
                            }}>
                              {secretHistory[turn.turnNumber] || "(no covert operations)"}
                            </p>
                          </div>
                          
                          {/* Dual Videos - Right */}
                          <div style={{ 
                            flex: "0 0 auto",
                            display: "flex",
                            gap: "8px",
                            alignItems: "flex-start"
                          }}>
                            {/* Video α */}
                            <div style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center"
                            }}>
                              <div style={{ 
                                opacity: 0.7, 
                                marginBottom: "6px",
                                fontSize: "0.65em",
                                letterSpacing: "0.05em"
                              }}>
                                FEED α
                              </div>
                              {(Array.isArray(videoHistory[turn.turnNumber]) ? videoHistory[turn.turnNumber][0] : videoHistory[turn.turnNumber]) ? (
                                <div style={{
                                  width: "143px",
                                  height: "80px",
                                  border: "1px solid rgba(57, 255, 20, 0.4)",
                                  backgroundColor: "#000",
                                  position: "relative",
                                  overflow: "hidden",
                                }}>
                                  <video
                                    src={Array.isArray(videoHistory[turn.turnNumber]) ? videoHistory[turn.turnNumber][0] : videoHistory[turn.turnNumber]}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      filter: "contrast(1.2) saturate(0.8)",
                                    }}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                  />
                                  <div style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: "linear-gradient(transparent 50%, rgba(0,0,0,0.2) 50%)",
                                    backgroundSize: "100% 2px",
                                    pointerEvents: "none",
                                  }} />
                                </div>
                              ) : (
                                <div style={{
                                  width: "143px",
                                  height: "80px",
                                  border: "1px dashed rgba(57, 255, 20, 0.2)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.5em",
                                  opacity: 0.4,
                                  color: "#39FF14",
                                }}>
                                  NO SIGNAL
                                </div>
                              )}
                            </div>
                            
                            {/* Video β */}
                            <div style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center"
                            }}>
                              <div style={{ 
                                opacity: 0.7, 
                                marginBottom: "6px",
                                fontSize: "0.65em",
                                letterSpacing: "0.05em"
                              }}>
                                FEED β
                              </div>
                              {(Array.isArray(videoHistory[turn.turnNumber]) && videoHistory[turn.turnNumber][1]) ? (
                                <div style={{
                                  width: "143px",
                                  height: "80px",
                                  border: "1px solid rgba(57, 255, 20, 0.4)",
                                  backgroundColor: "#000",
                                  position: "relative",
                                  overflow: "hidden",
                                }}>
                                  <video
                                    src={videoHistory[turn.turnNumber][1]}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      filter: "contrast(1.2) saturate(0.8)",
                                    }}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                  />
                                  <div style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: "linear-gradient(transparent 50%, rgba(0,0,0,0.2) 50%)",
                                    backgroundSize: "100% 2px",
                                    pointerEvents: "none",
                                  }} />
                                </div>
                              ) : (
                                <div style={{
                                  width: "143px",
                                  height: "80px",
                                  border: "1px dashed rgba(57, 255, 20, 0.2)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.5em",
                                  opacity: 0.4,
                                  color: "#39FF14",
                                }}>
                                  NO SIGNAL
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ 
                          margin: "15px 0",
                          color: "#39FF14",
                          opacity: 0.3,
                          letterSpacing: "0.2em"
                        }}>
                          ----------------------------------------
                        </div>
                      </div>
                    ))}
                  </InfiniteScroll>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// Wrap components with ErrorBoundary
function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

function AdminPanelWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <AdminPanel />
    </ErrorBoundary>
  );
}

// Export AdminPanel if in admin mode, otherwise export main App
export default isAdminMode ? AdminPanelWithErrorBoundary : AppWithErrorBoundary;
